import { NPC_IDLE_SEQ, NPC_WALK_SEQ } from "../../mapviewer/webgl/actor/ActorBakeTypes";
import { FUN_ENEMIES } from "./fun/enemies";
import { NIGHTMARE_ENEMIES } from "./nightmare";
import { PHOSANI_ENEMIES } from "./phosani";
import { getTheatreBosses } from "./theatre";
import { BossAttackDef, EnemyDef, PlayerEffectDef } from "./types";

// Animation ids come from scripts/cache/find-npc-seqs.ts and
// classify-npc-seqs.ts (attack = widest reach, death = ends collapsed).
// Humans share the player skeleton and use player animations
// (punch 422, death 836).
//
// Sound ids are the client's own sound effects, from the OSRS Wiki's list of
// sound ids (chicken_attack, goblin_death, ...).
const HUMAN = { idleSeq: 808, walkSeq: 819, attackSeq: 422, deathSeq: 836 };
const HUMAN_SOUNDS = { attack: 2564, hit: 513, death: 512 };

// Boss spot animations: the standard spellbook's (Fire Wave 155-157, Confuse
// 102-104, Weaken 105-107), the crossbow bolt (27) and the King Black Dragon's
// four breaths (393-396)

const TAINTED: PlayerEffectDef = {
    kind: "debuff",
    id: "tainted",
    name: "Tainted: agility drained",
    // Karil's crossbow
    iconObjId: 4734,
    stats: { moveSpeed: -0.3 },
    seconds: 2.5,
};

const WEAKENED: PlayerEffectDef = {
    kind: "debuff",
    id: "weakened",
    name: "Weakened: strength lowered",
    // Ahrim's staff
    iconObjId: 4710,
    stats: { might: -0.3 },
    seconds: 6,
};

const CONFUSED: PlayerEffectDef = {
    kind: "debuff",
    id: "confused",
    name: "Confused: attack lowered",
    // Ahrim's hood
    iconObjId: 4708,
    stats: { cooldown: -0.3 },
    seconds: 6,
};

const SHOCKED: PlayerEffectDef = {
    kind: "debuff",
    id: "shocked",
    name: "Shocked: stats drained",
    // Anti-dragon shield
    iconObjId: 1540,
    stats: { might: -0.2, moveSpeed: -0.15 },
    seconds: 5,
};

const CORRUPTED: PlayerEffectDef = {
    kind: "debuff",
    id: "corrupted",
    name: "Corrupted: run energy drained",
    // Torag's hammers
    iconObjId: 4747,
    stats: { moveSpeed: -0.25 },
    seconds: 3,
};

const DISARMED: PlayerEffectDef = {
    kind: "debuff",
    id: "disarmed",
    name: "Disarmed: weapons fumbled",
    // Bronze scimitar
    iconObjId: 1321,
    stats: { cooldown: -1 },
    seconds: 3,
};

// Slash Bash's: it builds up, and every dose starts the clock again
const DISEASED: PlayerEffectDef = {
    kind: "debuff",
    id: "diseased",
    name: "Diseased: stats drained",
    // Relicym's balm, what cures it in OSRS
    iconObjId: 4842,
    stats: { might: -0.08, moveSpeed: -0.08 },
    seconds: 8,
    stacks: 4,
};

const BILE_POOL: BossAttackDef["pool"] = {
    seconds: 7,
    radius: 1.5,
    damage: 1.5,
    interval: 0.8,
    effect: DISEASED,
    locId: 33423,
};

// Stand-ins: the Mimic's animations don't say what it sounds like
const MIMIC_SOUNDS = { attack: 2500, hit: 513, death: 512, candy: 163 };

function thirdAgeMinion(id: string, name: string, npcId: number): EnemyDef {
    return {
        id,
        name,
        npcId,
        idleSeq: 808,
        walkSeq: 819,
        attackSeq: 422,
        deathSeq: 836,
        hp: 90,
        damage: 3,
        speed: 2,
        radius: 0.45,
        xp: 0,
        sounds: HUMAN_SOUNDS,
    };
}

const SHADOWED: PlayerEffectDef = {
    kind: "debuff",
    id: "shadowed",
    name: "Shadowed: defence drained",
    // Shadow ancient sceptre
    iconObjId: 25492,
    stats: { armor: -1, might: -0.15 },
    seconds: 5,
};

// The melee bosses' gap closer: a red circle on the spot you're standing on,
// then the boss jumps onto it (the Barrows brothers and Count Draynor)
function leapAttack(damage: number, radius: number, cooldown: number): BossAttackDef {
    return {
        name: "Leap",
        kind: "blast",
        damage,
        windup: 1,
        cooldown,
        range: 10,
        minRange: 3.5,
        radius,
        weight: 2,
        leap: true,
        // Arms up, the god spell cast, for the jump
        seq: 811,
        sound: 2500,
    };
}

// Nex's claws, in each of her forms
const NEX_CLAWS: BossAttackDef = {
    name: "Claws",
    kind: "swing",
    damage: 5,
    windup: 0.6,
    cooldown: 0,
    range: 2.8,
    radius: 2.6,
    weight: 3,
    sound: 2564,
};

// The Inferno's JalTok-Jads: a stomp up close, and from further away the slow
// magic (TzTok-Jad's, 447-448) and the rocks that crash down where you stood
// (451), each its own animation like in OSRS
function jadAttacks(stomp: number, magic: number): BossAttackDef[] {
    return [
        {
            name: "Stomp",
            kind: "swing",
            damage: stomp,
            windup: 0.9,
            cooldown: 0,
            range: 3.6,
            radius: 3.4,
            weight: 4,
            sound: 2500,
        },
        {
            name: "Magic",
            kind: "projectile",
            damage: magic,
            windup: 1.2,
            cooldown: 0,
            range: 16,
            minRange: 3,
            weight: 3,
            speed: 9,
            homing: 0.6,
            castSpotAnim: 447,
            spotAnim: 448,
            impactSpotAnim: 157,
            seq: 7592,
            sound: 162,
            hitSound: 163,
        },
        {
            name: "Rockfall",
            kind: "blast",
            damage: magic,
            windup: 1.4,
            cooldown: 0,
            range: 16,
            minRange: 3,
            radius: 1.8,
            weight: 3,
            impactSpotAnim: 451,
            seq: 7593,
            sound: 2500,
        },
    ];
}

export const ENEMIES: EnemyDef[] = [
    {
        id: "chicken",
        name: "Chicken",
        npcId: 1173,
        idleSeq: 5386,
        walkSeq: 5385,
        attackSeq: 5387,
        deathSeq: 5389,
        hp: 3,
        damage: 1,
        speed: 1.7,
        radius: 0.3,
        xp: 4,
        sounds: { attack: 355, hit: 357, death: 356 },
    },
    {
        id: "rat",
        name: "Rat",
        npcId: 2492,
        idleSeq: 2704,
        walkSeq: 2703,
        attackSeq: 2705,
        deathSeq: 2894,
        hp: 4,
        damage: 1,
        speed: 2.0,
        radius: 0.3,
        xp: 4,
        sounds: { attack: 710, hit: 713, death: 711 },
        scale: 1.4,
    },
    {
        id: "goblin",
        name: "Goblin",
        npcId: 655,
        idleSeq: 6181,
        walkSeq: 6180,
        attackSeq: 6184,
        deathSeq: 6182,
        hp: 9,
        damage: 2,
        speed: 1.9,
        radius: 0.35,
        xp: 7,
        sounds: { attack: 469, hit: 472, death: 471 },
    },
    {
        id: "cow",
        name: "Cow",
        npcId: 2790,
        idleSeq: 5852,
        walkSeq: 5848,
        attackSeq: 5849,
        deathSeq: 5851,
        hp: 26,
        damage: 3,
        speed: 1.3,
        radius: 0.45,
        xp: 12,
        sounds: { attack: 369, hit: 371, death: 370 },
        knockbackResist: 0.6,
    },
    {
        id: "imp",
        name: "Imp",
        npcId: 5007,
        idleSeq: 171,
        walkSeq: 168,
        attackSeq: 169,
        deathSeq: 172,
        hp: 7,
        damage: 2,
        speed: 3.0,
        radius: 0.3,
        xp: 7,
        sounds: { attack: 534, hit: 536, death: 535 },
    },
    {
        id: "man",
        name: "Man",
        npcId: 3014,
        ...HUMAN,
        hp: 16,
        damage: 3,
        speed: 1.9,
        radius: 0.35,
        xp: 10,
        sounds: { attack: 2564, hit: 513, death: 512 },
    },
    {
        id: "giant_rat",
        name: "Giant rat",
        npcId: 2510,
        idleSeq: 4932,
        walkSeq: 4931,
        attackSeq: 4933,
        deathSeq: 4935,
        hp: 26,
        damage: 3,
        speed: 2.2,
        radius: 0.45,
        xp: 16,
        sounds: { attack: 710, hit: 713, death: 711 },
        knockbackResist: 0.3,
    },
    {
        id: "big_frog",
        name: "Big frog",
        npcId: 478,
        idleSeq: 1796,
        walkSeq: 1797,
        attackSeq: 1793,
        deathSeq: -1,
        hp: 28,
        damage: 3,
        speed: 1.8,
        radius: 0.4,
        xp: 18,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
        scale: 1.3,
    },
    {
        id: "zombie",
        name: "Zombie",
        tags: ["undead"],
        npcId: 26,
        idleSeq: 5565,
        walkSeq: 5566,
        attackSeq: 5568,
        deathSeq: 5569,
        hp: 38,
        damage: 4,
        speed: 1.5,
        radius: 0.4,
        xp: 22,
        sounds: { attack: 918, hit: 923, death: 922 },
        knockbackResist: 0.3,
    },
    {
        id: "skeleton",
        name: "Skeleton",
        tags: ["undead"],
        npcId: 70,
        idleSeq: 5483,
        walkSeq: 5481,
        attackSeq: 5487,
        deathSeq: 5491,
        hp: 46,
        damage: 5,
        speed: 2.0,
        radius: 0.4,
        xp: 26,
        sounds: { attack: 776, hit: 779, death: 777 },
    },
    {
        id: "dark_wizard",
        name: "Dark wizard",
        npcId: 510,
        ...HUMAN,
        attackSeq: 711,
        hp: 44,
        damage: 5,
        speed: 2.1,
        radius: 0.35,
        xp: 28,
        sounds: { attack: 2564, hit: 513, death: 512 },
    },
    {
        id: "giant_spider",
        name: "Giant spider",
        npcId: 2477,
        idleSeq: 5326,
        walkSeq: 5325,
        attackSeq: 5327,
        deathSeq: 5329,
        hp: 64,
        damage: 6,
        speed: 2.4,
        radius: 0.45,
        xp: 34,
        sounds: { attack: 3605, hit: 3607, death: 3606 },
        scale: 1.2,
        knockbackResist: 0.4,
    },
    {
        id: "ghost",
        name: "Ghost",
        tags: ["undead"],
        npcId: 85,
        idleSeq: 5538,
        walkSeq: 5539,
        attackSeq: 5540,
        deathSeq: 5542,
        hp: 54,
        damage: 6,
        speed: 1.9,
        radius: 0.35,
        xp: 32,
        sounds: { attack: 436, hit: 439, death: 438 },
        ghost: true,
    },
    {
        id: "giant_bat",
        name: "Giant bat",
        npcId: 2834,
        idleSeq: 4914,
        walkSeq: 4913,
        attackSeq: 4915,
        deathSeq: -1,
        hp: 46,
        damage: 5,
        speed: 3.2,
        radius: 0.4,
        xp: 30,
        sounds: { attack: 292, hit: 294, death: 293 },
        flies: true,
    },

    // Barrows: the crypts' tunnels (crypt rats, spiders and bloodworms; the
    // skeletons are the ones above), Mort'ton's afflicted and shades, and the
    // ghouls and feral vampyres of Morytania.
    //
    // These and the Wilderness's were found without the cache: npc ids and
    // idle/walk animations from the osrs-dumps npc config, and the attack and
    // death animations named after them in its seq config (rat_ready ->
    // rat_attack, rat_death). Humanoids that walk like players use player
    // animations. Combat sounds aren't in the cache, so most borrow a
    // relative's (a crypt rat a rat's); crypt spiders have their own.
    {
        id: "crypt_rat",
        name: "Crypt rat",
        npcId: 1679,
        idleSeq: 242,
        walkSeq: 239,
        attackSeq: 240,
        deathSeq: 243,
        hp: 5,
        damage: 1,
        speed: 2.0,
        radius: 0.3,
        xp: 5,
        sounds: { attack: 710, hit: 713, death: 711 },
        scale: 1.4,
    },
    {
        id: "bloodworm",
        name: "Bloodworm",
        npcId: 1678,
        idleSeq: 2071,
        walkSeq: 2069,
        attackSeq: 2070,
        deathSeq: 2073,
        hp: 8,
        damage: 2,
        speed: 1.6,
        radius: 0.3,
        xp: 7,
        // A big frog's
        sounds: { attack: 4787, hit: 4492, death: 4479 },
    },
    {
        id: "afflicted",
        name: "Afflicted",
        tags: ["undead"],
        npcId: 1293,
        ...HUMAN,
        // Shuffling, like a zombie
        walkSeq: 1306,
        hp: 15,
        damage: 3,
        speed: 1.5,
        radius: 0.35,
        xp: 10,
        sounds: HUMAN_SOUNDS,
    },
    {
        id: "giant_crypt_rat",
        name: "Giant crypt rat",
        npcId: 1680,
        idleSeq: 4932,
        walkSeq: 4931,
        attackSeq: 4933,
        deathSeq: 4935,
        hp: 28,
        damage: 3,
        speed: 2.2,
        radius: 0.45,
        xp: 17,
        sounds: { attack: 710, hit: 713, death: 711 },
        knockbackResist: 0.3,
    },
    {
        id: "crypt_spider",
        name: "Crypt spider",
        npcId: 1683,
        idleSeq: 6247,
        walkSeq: 6248,
        attackSeq: 6249,
        deathSeq: 6251,
        hp: 26,
        damage: 3,
        speed: 2.6,
        radius: 0.35,
        xp: 16,
        // Small spiders' attack and defend (on small_spider_update_attack_sound
        // and _defend_sound), and a giant spider's death
        sounds: { attack: 6963, hit: 6958, death: 3606 },
        scale: 1.3,
    },
    {
        id: "ghoul",
        name: "Ghoul",
        tags: ["undead"],
        npcId: 289,
        ...HUMAN,
        idleSeq: 1275,
        hp: 40,
        damage: 4,
        speed: 1.6,
        radius: 0.4,
        xp: 23,
        // A zombie's
        sounds: { attack: 918, hit: 923, death: 922 },
        knockbackResist: 0.3,
    },
    {
        id: "shade",
        name: "Loar Shade",
        tags: ["undead"],
        npcId: 1277,
        idleSeq: 1282,
        walkSeq: 1281,
        attackSeq: 1283,
        // Sinks back into the ground
        deathSeq: 1287,
        hp: 52,
        damage: 5,
        speed: 1.9,
        radius: 0.35,
        xp: 30,
        // A ghost's
        sounds: { attack: 436, hit: 439, death: 438 },
    },
    {
        id: "giant_crypt_spider",
        name: "Giant crypt spider",
        npcId: 1684,
        idleSeq: 5326,
        walkSeq: 5325,
        attackSeq: 5327,
        deathSeq: 5329,
        hp: 66,
        damage: 6,
        speed: 2.4,
        radius: 0.45,
        xp: 35,
        sounds: { attack: 3605, hit: 3607, death: 3606 },
        scale: 1.2,
        knockbackResist: 0.4,
    },
    {
        id: "feral_vampyre",
        name: "Feral Vampyre",
        tags: ["vampyre"],
        npcId: 3237,
        ...HUMAN,
        hp: 50,
        damage: 6,
        speed: 2.8,
        radius: 0.35,
        xp: 32,
        // Count Draynor's
        sounds: { attack: 879, hit: 884, death: 882 },
    },

    // The Wilderness: revenants from the caves beneath it, bandits, dark
    // warriors, chaos druids, hill giants, poison spiders and the Ankou (and
    // the Graveyard of Shadows's zombies and skeletons, the ones above)
    {
        id: "revenant_imp",
        name: "Revenant imp",
        tags: ["undead"],
        npcId: 7881,
        idleSeq: 171,
        walkSeq: 168,
        attackSeq: 169,
        deathSeq: 172,
        // The Wilderness's first enemy, so weaker and slower than an imp
        // (about a rat, Lumbridge's first)
        hp: 5,
        damage: 1,
        speed: 2.4,
        radius: 0.3,
        xp: 6,
        sounds: { attack: 534, hit: 536, death: 535 },
    },
    {
        id: "revenant_goblin",
        name: "Revenant goblin",
        tags: ["undead"],
        npcId: 7931,
        idleSeq: 6181,
        walkSeq: 6180,
        attackSeq: 6184,
        deathSeq: 6182,
        hp: 10,
        damage: 2,
        speed: 1.9,
        radius: 0.35,
        xp: 8,
        sounds: { attack: 469, hit: 472, death: 471 },
    },
    {
        id: "bandit",
        name: "Bandit",
        npcId: 6605,
        ...HUMAN,
        hp: 16,
        damage: 3,
        speed: 2.0,
        radius: 0.35,
        xp: 10,
        sounds: HUMAN_SOUNDS,
    },
    {
        id: "revenant_hobgoblin",
        name: "Revenant hobgoblin",
        tags: ["undead"],
        npcId: 7933,
        idleSeq: 166,
        walkSeq: 162,
        attackSeq: 163,
        deathSeq: 167,
        hp: 28,
        damage: 3,
        speed: 2.0,
        radius: 0.4,
        xp: 17,
        // A goblin's
        sounds: { attack: 469, hit: 472, death: 471 },
    },
    {
        id: "revenant_pyrefiend",
        name: "Revenant pyrefiend",
        tags: ["undead"],
        npcId: 7932,
        idleSeq: 1578,
        walkSeq: 1579,
        attackSeq: 1582,
        deathSeq: 1580,
        hp: 30,
        damage: 4,
        speed: 2.2,
        radius: 0.4,
        xp: 18,
        // An imp's
        sounds: { attack: 534, hit: 536, death: 535 },
    },
    {
        id: "dark_warrior",
        name: "Dark warrior",
        npcId: 531,
        ...HUMAN,
        hp: 44,
        damage: 5,
        speed: 1.9,
        radius: 0.35,
        xp: 27,
        sounds: HUMAN_SOUNDS,
    },
    {
        id: "chaos_druid",
        name: "Chaos druid",
        npcId: 520,
        ...HUMAN,
        attackSeq: 711,
        hp: 44,
        damage: 5,
        speed: 2.1,
        radius: 0.35,
        xp: 28,
        sounds: HUMAN_SOUNDS,
    },
    {
        id: "poison_spider",
        name: "Poison spider",
        npcId: 3023,
        idleSeq: 5326,
        walkSeq: 5325,
        attackSeq: 5327,
        deathSeq: 5329,
        hp: 62,
        damage: 6,
        speed: 2.5,
        radius: 0.45,
        xp: 34,
        sounds: { attack: 3605, hit: 3607, death: 3606 },
        scale: 1.2,
        knockbackResist: 0.4,
    },
    {
        id: "ankou",
        name: "Ankou",
        tags: ["undead"],
        npcId: 6608,
        ...HUMAN,
        hp: 54,
        damage: 6,
        speed: 1.9,
        radius: 0.35,
        xp: 32,
        // A ghost's; a spirit of death, it drifts through walls like one
        sounds: { attack: 436, hit: 439, death: 438 },
        ghost: true,
    },
    {
        id: "hill_giant",
        name: "Hill Giant",
        npcId: 13502,
        idleSeq: 4650,
        walkSeq: 4649,
        attackSeq: 4652,
        deathSeq: 4653,
        hp: 64,
        damage: 6,
        speed: 1.6,
        radius: 0.45,
        xp: 36,
        sounds: HUMAN_SOUNDS,
        scale: 0.85,
        knockbackResist: 0.6,
    },
    {
        id: "revenant_demon",
        name: "Revenant demon",
        tags: ["undead"],
        npcId: 7936,
        idleSeq: 66,
        walkSeq: 63,
        attackSeq: 64,
        deathSeq: 67,
        hp: 64,
        damage: 7,
        speed: 1.8,
        radius: 0.45,
        xp: 38,
        // An imp's
        sounds: { attack: 534, hit: 536, death: 535 },
        // Three tiles across in OSRS
        scale: 0.6,
        knockbackResist: 0.6,
    },

    // The final boss: the Wise Old Man of Draynor Village. He can't be fought
    // in OSRS, so he fights the way the stories go: a retired adventurer and
    // wizard (the standard and Ancient spellbooks), who once robbed the Draynor
    // bank. Teleport is the standard one (714 and 111), Telekinetic Grab
    // 142-144, Ice Barrage the Ancient cast (1979) with Ice Burst's splash
    // (363) where it lands. He calls in Draynor's dark wizards, and once he's
    // enraged he casts his Fire Waves three at a time and barrages all around
    // you.
    {
        // A random event's, on any map (RANDOM_EVENTS). Quick with his hands:
        // what he takes comes back when he's killed.
        id: "highwayman",
        name: "Highwayman",
        npcId: 518,
        ...HUMAN,
        hp: 450,
        damage: 0,
        speed: 2.8,
        radius: 0.45,
        xp: 150,
        sounds: HUMAN_SOUNDS,
        scale: 1.5,
        knockbackResist: 0.8,
        spawnMessage: "A highwayman jumps out at you! Stand and deliver!",
        caskets: ["hard"],
        boss: {
            attackInterval: 1.2,
            enrage: { message: "The highwayman gets desperate!", shout: "Hand it over!" },
            attacks: [
                {
                    name: "Pickpocket",
                    kind: "swing",
                    damage: 2,
                    windup: 0.4,
                    cooldown: 1,
                    range: 1.8,
                    radius: 1.8,
                    weight: 1,
                    shout: "Your money or your life!",
                    effect: { kind: "steal", fraction: 0.1 },
                },
            ],
        },
    },
    {
        id: "wise_old_man",
        name: "Wise Old Man",
        npcId: 2108,
        ...HUMAN,
        attackSeq: 711,
        hp: 7500,
        damage: 0,
        speed: 2.2,
        radius: 0.45,
        xp: 2000,
        sounds: HUMAN_SOUNDS,
        scale: 2,
        knockbackResist: 1,
        spawnMessage: "The Wise Old Man teleports in. He robbed Draynor's bank, and yours is next!",
        spawnSpotAnim: 111,
        // teleport_vwoop
        spawnSound: 200,
        boss: {
            attackInterval: 1.6,
            keepDistance: 5,
            enrage: {
                message: "The Wise Old Man stops holding back!",
                shout: "Enough games!",
            },
            attacks: [
                {
                    name: "Fire Wave",
                    kind: "projectile",
                    damage: 8,
                    windup: 0.5,
                    cooldown: 0,
                    range: 13,
                    weight: 3,
                    speed: 9,
                    homing: 0.8,
                    castSpotAnim: 155,
                    spotAnim: 156,
                    impactSpotAnim: 157,
                    sound: 162,
                    hitSound: 163,
                },
                {
                    name: "Ice Barrage",
                    kind: "blast",
                    damage: 9,
                    // Long enough to see the circle and get out of it
                    windup: 1.1,
                    cooldown: 5,
                    range: 13,
                    radius: 1.8,
                    weight: 3,
                    impactSpotAnim: 363,
                    seq: 1979,
                    sound: 168,
                    effect: { kind: "freeze", seconds: 1.5 },
                },
                {
                    name: "Summon dark wizards",
                    kind: "summon",
                    damage: 0,
                    windup: 1,
                    cooldown: 16,
                    range: 16,
                    weight: 2,
                    summon: { enemyId: "dark_wizard", count: 3 },
                    seq: 811,
                    sound: 200,
                    shout: "To me, my old friends!",
                },
                {
                    name: "Fire Wave volley",
                    kind: "projectile",
                    damage: 7,
                    windup: 0.6,
                    cooldown: 4,
                    range: 13,
                    weight: 3,
                    speed: 9,
                    count: 3,
                    spread: 0.3,
                    homing: 0.5,
                    castSpotAnim: 155,
                    spotAnim: 156,
                    impactSpotAnim: 157,
                    sound: 162,
                    hitSound: 163,
                    enraged: true,
                },
                {
                    name: "Ice Barrage storm",
                    kind: "blast",
                    damage: 8,
                    windup: 1.2,
                    cooldown: 8,
                    range: 13,
                    radius: 1.6,
                    count: 4,
                    spread: 3.5,
                    weight: 3,
                    impactSpotAnim: 363,
                    seq: 1979,
                    sound: 168,
                    effect: { kind: "freeze", seconds: 1.2 },
                    enraged: true,
                },
                {
                    name: "Telekinetic Grab",
                    kind: "projectile",
                    damage: 2,
                    windup: 0.5,
                    cooldown: 9,
                    range: 13,
                    weight: 2,
                    speed: 11,
                    homing: 1.2,
                    castSpotAnim: 142,
                    spotAnim: 143,
                    impactSpotAnim: 144,
                    sound: 160,
                    effect: { kind: "steal", fraction: 0.05 },
                },
                {
                    // Gets away from anyone who comes close
                    name: "Teleport",
                    kind: "teleport",
                    damage: 0,
                    windup: 0.6,
                    cooldown: 6,
                    range: 3,
                    radius: 7,
                    weight: 6,
                    castSpotAnim: 111,
                    impactSpotAnim: 111,
                    seq: 714,
                    sound: 200,
                },
            ],
        },
    },

    // Bosses that turn up during a run, each fighting like it does in OSRS.
    // They hit with their attacks rather than by touching the player, so their
    // damage is 0. The Barrows brothers are built on the player's skeleton and
    // use player animations (Dharok's greataxe 2067, Karil's crossbow 2075).
    {
        id: "dharok",
        name: "Dharok the Wretched",
        tags: ["undead"],
        npcId: 1673,
        ...HUMAN,
        attackSeq: 2067,
        hp: 600,
        damage: 0,
        speed: 2.4,
        radius: 0.4,
        xp: 300,
        sounds: HUMAN_SOUNDS,
        scale: 1.5,
        knockbackResist: 1,
        spawnMessage: "Dharok the Wretched climbs out of his crypt!",
        // A slow, heavy greataxe that hits harder the lower his hitpoints get,
        // and a leap that lands it on you from further off
        boss: {
            attackInterval: 2.2,
            wretched: 1.25,
            enrage: { message: "Dharok's wounds only make him angrier!" },
            attacks: [
                {
                    name: "Greataxe",
                    kind: "swing",
                    damage: 6,
                    windup: 0.7,
                    cooldown: 0,
                    range: 2.6,
                    radius: 2.4,
                    weight: 1,
                    // scimitar_slash
                    sound: 2500,
                },
                leapAttack(6, 2.2, 6),
            ],
        },
    },
    {
        id: "karil",
        name: "Karil the Tainted",
        tags: ["undead"],
        npcId: 1675,
        ...HUMAN,
        attackSeq: 2075,
        hp: 1100,
        damage: 0,
        speed: 2.8,
        radius: 0.4,
        xp: 500,
        sounds: HUMAN_SOUNDS,
        scale: 1.5,
        knockbackResist: 1,
        spawnMessage: "Karil the Tainted takes aim from the shadows!",
        // Keeps his distance and shoots crossbow bolts; his tainted shot
        // drains agility, slowing you down. Enraged, he fires them three at a
        // time.
        boss: {
            attackInterval: 1.8,
            keepDistance: 7,
            enrage: { message: "Karil the Tainted reloads, faster than ever!" },
            attacks: [
                {
                    name: "Bolt",
                    kind: "projectile",
                    damage: 3,
                    windup: 0.4,
                    cooldown: 0,
                    range: 13,
                    weight: 3,
                    speed: 13,
                    spotAnim: 27,
                    // Throwing knives' throw
                    sound: 2707,
                },
                {
                    name: "Tainted shot",
                    kind: "projectile",
                    damage: 4,
                    windup: 0.6,
                    cooldown: 8,
                    range: 13,
                    weight: 3,
                    speed: 18,
                    spotAnim: 27,
                    sound: 2707,
                    effect: TAINTED,
                },
                {
                    name: "Volley",
                    kind: "projectile",
                    damage: 3,
                    windup: 0.5,
                    cooldown: 3,
                    range: 13,
                    weight: 3,
                    speed: 14,
                    count: 3,
                    spread: 0.22,
                    spotAnim: 27,
                    sound: 2707,
                    enraged: true,
                },
            ],
        },
    },
    {
        id: "count_draynor",
        name: "Count Draynor",
        tags: ["vampyre"],
        npcId: 3481,
        ...HUMAN,
        hp: 1400,
        damage: 0,
        speed: 2.3,
        radius: 0.45,
        xp: 600,
        sounds: { attack: 879, hit: 884, death: 882 },
        scale: 1.8,
        knockbackResist: 1,
        spawnMessage: "Count Draynor has risen from his coffin!",
        // The Vampire Slayer fight: a bite that drinks your blood, a leap onto
        // anyone who runs, the bats of Draynor Manor, and getting back up once
        // (there's no stake to finish him with)
        boss: {
            attackInterval: 2,
            lifesteal: 1,
            rises: {
                times: 1,
                hp: 0.5,
                message: "Count Draynor regenerates! A vampyre won't stay down without a stake.",
            },
            enrage: {
                message: "Count Draynor's eyes glow red. He thirsts for blood!",
                shout: "Your blood is mine!",
            },
            attacks: [
                {
                    name: "Bite",
                    kind: "swing",
                    damage: 7,
                    windup: 0.6,
                    cooldown: 0,
                    range: 2.6,
                    radius: 2.2,
                    weight: 3,
                    sound: 879,
                },
                {
                    name: "Summon bats",
                    kind: "summon",
                    damage: 0,
                    windup: 1,
                    cooldown: 12,
                    range: 16,
                    weight: 2,
                    summon: { enemyId: "giant_bat", count: 4 },
                    // Arms up, the god spell cast
                    seq: 811,
                    // giant_bat_attack
                    sound: 292,
                },
                { ...leapAttack(6, 1.8, 7), name: "Vampyric leap" },
                {
                    name: "Bat swarm",
                    kind: "summon",
                    damage: 0,
                    windup: 1,
                    cooldown: 14,
                    range: 16,
                    weight: 2,
                    summon: { enemyId: "giant_bat", count: 7 },
                    seq: 811,
                    sound: 292,
                    enraged: true,
                },
            ],
        },
    },
    // The other three brothers, for the Barrows (weapon animations: Torag's
    // hammers 2068, Guthan's warspear 2080, Verac's flail 2062)
    {
        id: "torag",
        name: "Torag the Corrupted",
        tags: ["undead"],
        npcId: 1676,
        ...HUMAN,
        attackSeq: 2068,
        hp: 800,
        damage: 0,
        speed: 2.3,
        radius: 0.4,
        xp: 300,
        sounds: HUMAN_SOUNDS,
        scale: 1.5,
        knockbackResist: 1,
        spawnMessage: "Torag the Corrupted climbs out of his crypt!",
        // Two hammers, and his set effect drains your run energy, slowing you
        boss: {
            attackInterval: 2,
            enrage: { message: "Torag the Corrupted is enraged!" },
            attacks: [
                {
                    name: "Hammers",
                    kind: "swing",
                    damage: 5,
                    windup: 0.6,
                    cooldown: 0,
                    range: 2.6,
                    radius: 2.2,
                    weight: 1,
                    sound: 2500,
                    effect: CORRUPTED,
                },
                { ...leapAttack(5, 1.8, 7), effect: CORRUPTED },
            ],
        },
    },
    {
        id: "guthan",
        name: "Guthan the Infested",
        tags: ["undead"],
        npcId: 1674,
        ...HUMAN,
        attackSeq: 2080,
        hp: 2200,
        damage: 0,
        speed: 2.3,
        radius: 0.4,
        xp: 600,
        sounds: HUMAN_SOUNDS,
        scale: 1.5,
        knockbackResist: 1,
        spawnMessage: "Guthan the Infested climbs out of his crypt!",
        // A long warspear, and his set effect heals him by what it hits for
        boss: {
            attackInterval: 2,
            lifesteal: 1.5,
            enrage: { message: "Guthan the Infested is enraged!" },
            attacks: [
                {
                    name: "Warspear",
                    kind: "swing",
                    damage: 6,
                    windup: 0.6,
                    cooldown: 0,
                    range: 3,
                    radius: 2.6,
                    weight: 1,
                    sound: 2500,
                },
                leapAttack(6, 2, 7),
            ],
        },
    },
    {
        id: "verac",
        name: "Verac the Defiled",
        tags: ["undead"],
        npcId: 1677,
        ...HUMAN,
        attackSeq: 2062,
        hp: 4600,
        damage: 0,
        speed: 2.4,
        radius: 0.4,
        xp: 1200,
        sounds: HUMAN_SOUNDS,
        scale: 1.5,
        knockbackResist: 1,
        spawnMessage: "Verac the Defiled climbs out of his crypt!",
        // His flail hits through defences: armour doesn't soak any of it
        boss: {
            attackInterval: 1.8,
            enrage: { message: "Verac the Defiled is enraged!" },
            attacks: [
                {
                    name: "Flail",
                    kind: "swing",
                    damage: 7,
                    windup: 0.6,
                    cooldown: 0,
                    range: 2.6,
                    radius: 2.3,
                    weight: 1,
                    sound: 2500,
                    ignoreArmor: true,
                },
                { ...leapAttack(6, 2, 6), ignoreArmor: true },
            ],
        },
    },

    // Wilderness bosses that fight like players do, so they're on the
    // player's skeleton too
    {
        id: "crazy_archaeologist",
        name: "Crazy archaeologist",
        npcId: 6618,
        ...HUMAN,
        attackSeq: 2075,
        hp: 1800,
        damage: 0,
        speed: 2.4,
        radius: 0.4,
        xp: 500,
        sounds: HUMAN_SOUNDS,
        scale: 1.6,
        knockbackResist: 1,
        spawnMessage: 'The Crazy archaeologist storms out of his dig: "I\'m Bellock - respect me!"',
        // A crossbow, and Rain of knowledge: exploding books that land around
        // you. Enraged, he fires three bolts at once.
        boss: {
            attackInterval: 1.8,
            keepDistance: 6,
            enrage: {
                message: "The Crazy archaeologist is furious!",
                shout: "Get off my site!",
            },
            attacks: [
                {
                    name: "Crossbow",
                    kind: "projectile",
                    damage: 4,
                    windup: 0.4,
                    cooldown: 0,
                    range: 13,
                    weight: 3,
                    speed: 13,
                    spotAnim: 27,
                    sound: 2707,
                },
                {
                    name: "Rain of knowledge",
                    kind: "blast",
                    damage: 6,
                    windup: 1.3,
                    cooldown: 7,
                    range: 13,
                    radius: 1.4,
                    count: 3,
                    spread: 2.5,
                    weight: 3,
                    impactSpotAnim: 157,
                    seq: 811,
                    sound: 163,
                    shout: "Rain of knowledge!",
                },
                {
                    name: "Crazy shot",
                    kind: "projectile",
                    damage: 4,
                    windup: 0.5,
                    cooldown: 3,
                    range: 13,
                    weight: 3,
                    speed: 13,
                    count: 3,
                    spread: 0.25,
                    spotAnim: 27,
                    sound: 2707,
                    enraged: true,
                },
            ],
        },
    },
    {
        id: "chaos_fanatic",
        name: "Chaos Fanatic",
        npcId: 6619,
        ...HUMAN,
        attackSeq: 711,
        hp: 3600,
        damage: 0,
        speed: 2.3,
        radius: 0.4,
        xp: 900,
        sounds: HUMAN_SOUNDS,
        scale: 1.6,
        knockbackResist: 1,
        spawnMessage: 'The Chaos Fanatic comes screaming out of the ruins: "WEUGH!"',
        // Chaotic magic, explosions that land around you, and a spell that
        // knocks your weapons out of your hands. Enraged, the explosions come
        // five at a time.
        boss: {
            attackInterval: 1.8,
            keepDistance: 6,
            enrage: {
                message: "The Chaos Fanatic is beside himself!",
                shout: "AhehHeheuhHhahueHuUEehEahAH!",
            },
            attacks: [
                {
                    name: "Chaos blast",
                    kind: "projectile",
                    damage: 6,
                    windup: 0.5,
                    cooldown: 0,
                    range: 13,
                    weight: 4,
                    speed: 10,
                    homing: 0.8,
                    castSpotAnim: 129,
                    spotAnim: 130,
                    impactSpotAnim: 131,
                    sound: 155,
                    hitSound: 156,
                },
                {
                    name: "Explosion",
                    kind: "blast",
                    damage: 7,
                    windup: 1.2,
                    cooldown: 7,
                    range: 13,
                    radius: 1.5,
                    count: 3,
                    spread: 2.5,
                    weight: 3,
                    impactSpotAnim: 157,
                    seq: 811,
                    sound: 163,
                    shout: "Burn!",
                },
                {
                    name: "Disarm",
                    kind: "projectile",
                    damage: 2,
                    windup: 0.5,
                    cooldown: 10,
                    range: 13,
                    weight: 2,
                    speed: 11,
                    homing: 1.2,
                    castSpotAnim: 102,
                    spotAnim: 103,
                    impactSpotAnim: 104,
                    sound: 160,
                    effect: DISARMED,
                    shout: "Develish Oxen Roll!",
                },
                {
                    name: "Chaos storm",
                    kind: "blast",
                    damage: 7,
                    windup: 1.2,
                    cooldown: 8,
                    range: 13,
                    radius: 1.5,
                    count: 5,
                    spread: 3.5,
                    weight: 4,
                    impactSpotAnim: 157,
                    seq: 811,
                    sound: 163,
                    shout: "All your wilderness are belong to them!",
                    enraged: true,
                },
            ],
        },
    },
    {
        id: "ahrim",
        name: "Ahrim the Blighted",
        tags: ["undead"],
        npcId: 1672,
        ...HUMAN,
        attackSeq: 711,
        hp: 2300,
        damage: 0,
        speed: 2.3,
        radius: 0.4,
        xp: 800,
        sounds: HUMAN_SOUNDS,
        scale: 1.5,
        knockbackResist: 1,
        spawnMessage: "Ahrim the Blighted rises, crackling with power!",
        // Wave spells that curve after you, and curses: Weaken lowers your
        // strength, Confuse your attack. Enraged, he casts three waves at once.
        boss: {
            attackInterval: 2.2,
            keepDistance: 6,
            enrage: { message: "Ahrim the Blighted is enraged!" },
            attacks: [
                {
                    name: "Fire Wave",
                    kind: "projectile",
                    damage: 6,
                    windup: 0.5,
                    cooldown: 0,
                    range: 13,
                    weight: 4,
                    speed: 9,
                    homing: 0.9,
                    castSpotAnim: 155,
                    spotAnim: 156,
                    impactSpotAnim: 157,
                    sound: 162,
                    hitSound: 163,
                },
                {
                    name: "Weaken",
                    kind: "projectile",
                    damage: 3,
                    windup: 0.5,
                    cooldown: 7,
                    range: 13,
                    weight: 2,
                    speed: 10,
                    homing: 1.6,
                    castSpotAnim: 105,
                    spotAnim: 106,
                    impactSpotAnim: 107,
                    sound: 160,
                    hitSound: 161,
                    effect: WEAKENED,
                },
                {
                    name: "Confuse",
                    kind: "projectile",
                    damage: 3,
                    windup: 0.5,
                    cooldown: 7,
                    range: 13,
                    weight: 2,
                    speed: 10,
                    homing: 1.6,
                    castSpotAnim: 102,
                    spotAnim: 103,
                    impactSpotAnim: 104,
                    sound: 160,
                    hitSound: 161,
                    effect: CONFUSED,
                },
                {
                    name: "Fire Wave volley",
                    kind: "projectile",
                    damage: 6,
                    windup: 0.6,
                    cooldown: 4,
                    range: 13,
                    weight: 3,
                    speed: 9,
                    count: 3,
                    spread: 0.3,
                    homing: 0.5,
                    castSpotAnim: 155,
                    spotAnim: 156,
                    impactSpotAnim: 157,
                    sound: 162,
                    hitSound: 163,
                    enraged: true,
                },
            ],
        },
    },
    {
        id: "king_black_dragon",
        name: "King Black Dragon",
        npcId: 239,
        // Its own idle and walk from its NPC type; no attack or death
        // animation we know of, so it sinks when it dies
        idleSeq: NPC_IDLE_SEQ,
        walkSeq: NPC_WALK_SEQ,
        attackSeq: -1,
        deathSeq: -1,
        hp: 3700,
        damage: 0,
        speed: 1.5,
        radius: 0.45,
        xp: 1500,
        // Zombie sounds stand in for a dragon's
        sounds: { attack: 918, hit: 923, death: 922 },
        // Five tiles across, as in OSRS. Its collision stays small: any wider
        // and it often can't get round the Wilderness to the player.
        scale: 1,
        knockbackResist: 1,
        spawnMessage: "The King Black Dragon crawls out of his lair under the Lava Maze!",
        // Claws up close, and four kinds of breath from further off: fire,
        // poison, ice that freezes you and shock that drains your stats.
        // Enraged, he breathes fire three ways at once.
        boss: {
            attackInterval: 1.8,
            // Stops with his heads at you instead of standing over you, still in
            // reach of the shortest melee weapons
            keepDistance: 2.4,
            enrage: { message: "The King Black Dragon roars in fury!" },
            attacks: [
                {
                    name: "Claws",
                    kind: "swing",
                    damage: 10,
                    windup: 0.8,
                    cooldown: 0,
                    range: 3.4,
                    radius: 3.2,
                    weight: 4,
                    sound: 2500,
                },
                {
                    name: "Dragonfire",
                    kind: "projectile",
                    damage: 9,
                    windup: 0.5,
                    cooldown: 0,
                    range: 15,
                    minRange: 2.5,
                    weight: 3,
                    speed: 10,
                    spotAnim: 393,
                    impactSpotAnim: 157,
                    sound: 162,
                    hitSound: 163,
                },
                {
                    name: "Poison breath",
                    kind: "projectile",
                    damage: 3,
                    windup: 0.5,
                    cooldown: 4,
                    range: 15,
                    minRange: 2.5,
                    weight: 2,
                    speed: 10,
                    spotAnim: 394,
                    sound: 162,
                    effect: { kind: "poison", damage: 2, seconds: 5 },
                },
                {
                    name: "Ice breath",
                    kind: "projectile",
                    damage: 4,
                    windup: 0.5,
                    cooldown: 6,
                    range: 15,
                    minRange: 2.5,
                    weight: 2,
                    speed: 10,
                    spotAnim: 395,
                    sound: 162,
                    effect: { kind: "freeze", seconds: 1.4 },
                },
                {
                    name: "Shock breath",
                    kind: "projectile",
                    damage: 5,
                    windup: 0.5,
                    cooldown: 6,
                    range: 15,
                    minRange: 2.5,
                    weight: 2,
                    speed: 10,
                    spotAnim: 396,
                    sound: 162,
                    effect: SHOCKED,
                },
                {
                    name: "Dragonfire volley",
                    kind: "projectile",
                    damage: 8,
                    windup: 0.6,
                    cooldown: 3,
                    range: 15,
                    minRange: 2.5,
                    weight: 3,
                    speed: 10,
                    count: 3,
                    spread: 0.28,
                    spotAnim: 393,
                    impactSpotAnim: 157,
                    sound: 162,
                    hitSound: 163,
                    enraged: true,
                },
            ],
        },
    },
    // The Kharidian Desert: lizards, scorpions and jackals on the sand, the
    // Bandit Camp's bandits, kalphites out of their hive, crocodiles, mummies
    // and the locust riders of the pyramids. Kalphites and scarabs sound like
    // spiders, lizards and crocodiles like frogs.
    {
        id: "desert_lizard",
        name: "Desert lizard",
        npcId: 459,
        idleSeq: 2774,
        walkSeq: 2775,
        attackSeq: 2776,
        deathSeq: 2778,
        hp: 5,
        damage: 1,
        speed: 2.3,
        radius: 0.3,
        xp: 6,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
        scale: 0.75,
    },
    {
        id: "scorpion",
        name: "Scorpion",
        npcId: 3024,
        idleSeq: 6252,
        walkSeq: 6253,
        attackSeq: 6254,
        deathSeq: 6256,
        hp: 10,
        damage: 2,
        speed: 2.0,
        radius: 0.35,
        xp: 8,
        sounds: { attack: 3605, hit: 3607, death: 3606 },
        scale: 0.8,
    },
    {
        id: "jackal",
        name: "Jackal",
        npcId: 4185,
        idleSeq: 6561,
        walkSeq: 6560,
        attackSeq: 6559,
        deathSeq: 6558,
        hp: 9,
        damage: 2,
        speed: 2.6,
        radius: 0.3,
        xp: 8,
        // A rat's
        sounds: { attack: 710, hit: 713, death: 711 },
    },
    {
        id: "ugthanki",
        name: "Ugthanki",
        npcId: 4652,
        idleSeq: 51,
        walkSeq: 45,
        attackSeq: 49,
        deathSeq: 52,
        hp: 26,
        damage: 3,
        speed: 1.6,
        radius: 0.45,
        xp: 16,
        // A cow's
        sounds: { attack: 369, hit: 371, death: 370 },
        scale: 0.75,
        knockbackResist: 0.4,
    },
    {
        id: "scarab_swarm",
        name: "Scarab swarm",
        npcId: 1782,
        idleSeq: 1947,
        walkSeq: 1945,
        attackSeq: 1948,
        deathSeq: -1,
        hp: 6,
        damage: 2,
        speed: 2.8,
        radius: 0.3,
        xp: 5,
        sounds: { attack: 3605, hit: 3607, death: 3606 },
    },
    {
        id: "desert_bandit",
        name: "Desert bandit",
        npcId: 690,
        ...HUMAN,
        hp: 18,
        damage: 3,
        speed: 2.0,
        radius: 0.35,
        xp: 11,
        sounds: HUMAN_SOUNDS,
    },
    {
        id: "kalphite_worker",
        name: "Kalphite worker",
        npcId: 955,
        idleSeq: 6218,
        walkSeq: 6220,
        attackSeq: 6223,
        deathSeq: 6228,
        hp: 30,
        damage: 4,
        speed: 2.2,
        radius: 0.4,
        xp: 18,
        sounds: { attack: 3605, hit: 3607, death: 3606 },
        scale: 0.8,
    },
    {
        id: "crocodile",
        name: "Crocodile",
        npcId: 4184,
        idleSeq: 2037,
        walkSeq: 2036,
        attackSeq: 2039,
        deathSeq: 2038,
        hp: 44,
        damage: 5,
        speed: 1.7,
        radius: 0.45,
        xp: 26,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
        scale: 0.75,
        knockbackResist: 0.4,
    },
    {
        id: "kalphite_soldier",
        name: "Kalphite soldier",
        npcId: 138,
        idleSeq: 6218,
        walkSeq: 6220,
        attackSeq: 6223,
        deathSeq: 6228,
        hp: 56,
        damage: 6,
        speed: 2.0,
        radius: 0.45,
        xp: 32,
        sounds: { attack: 6963, hit: 6958, death: 3606 },
        // Three tiles across in OSRS
        scale: 0.6,
        knockbackResist: 0.5,
    },
    {
        id: "mummy",
        name: "Mummy",
        tags: ["undead"],
        npcId: 949,
        idleSeq: 5548,
        walkSeq: 5552,
        attackSeq: 5549,
        deathSeq: 5555,
        hp: 50,
        damage: 6,
        speed: 1.6,
        radius: 0.35,
        xp: 30,
        // A zombie's
        sounds: { attack: 918, hit: 923, death: 922 },
    },
    {
        id: "locust_rider",
        name: "Locust rider",
        npcId: 795,
        idleSeq: 5447,
        walkSeq: 5446,
        attackSeq: 5450,
        deathSeq: 5449,
        hp: 62,
        damage: 7,
        speed: 2.3,
        radius: 0.45,
        xp: 38,
        sounds: HUMAN_SOUNDS,
        scale: 0.8,
        knockbackResist: 0.5,
    },
    {
        id: "kalphite_guardian",
        name: "Kalphite guardian",
        npcId: 959,
        idleSeq: 6219,
        walkSeq: 6222,
        attackSeq: 6223,
        deathSeq: 6228,
        hp: 70,
        damage: 7,
        speed: 1.7,
        radius: 0.45,
        xp: 40,
        sounds: { attack: 6963, hit: 6958, death: 3606 },
        // Four tiles across in OSRS
        scale: 0.5,
        knockbackResist: 0.7,
    },

    // Desert Treasure's guardians of the diamonds, each after their fight in
    // the quest (player animations: scimitar slash 390, the Ancient cast
    // 1979)
    {
        id: "fareed",
        name: "Fareed",
        npcId: 3456,
        ...HUMAN,
        attackSeq: 390,
        hp: 2600,
        damage: 0,
        speed: 2.4,
        radius: 0.4,
        xp: 700,
        sounds: HUMAN_SOUNDS,
        scale: 1.6,
        knockbackResist: 1,
        spawnMessage: "Fareed bursts out of the flames, his scimitar blazing!",
        // A burning scimitar, a leap onto anyone who runs, and Fire Waves.
        // Enraged, fire rains down all around you.
        boss: {
            attackInterval: 1.9,
            enrage: { message: "Fareed blazes with fury!", shout: "Burn!" },
            attacks: [
                {
                    name: "Fire scimitar",
                    kind: "swing",
                    damage: 5,
                    windup: 0.6,
                    cooldown: 0,
                    range: 2.6,
                    radius: 2.4,
                    weight: 4,
                    impactSpotAnim: 157,
                    sound: 2500,
                },
                { ...leapAttack(4, 1.8, 7), name: "Fiery leap" },
                {
                    name: "Fire Wave",
                    kind: "projectile",
                    damage: 4,
                    windup: 0.5,
                    cooldown: 3,
                    range: 13,
                    minRange: 3,
                    weight: 3,
                    speed: 9,
                    homing: 0.8,
                    castSpotAnim: 155,
                    spotAnim: 156,
                    impactSpotAnim: 157,
                    sound: 162,
                    hitSound: 163,
                },
                {
                    name: "Inferno",
                    kind: "blast",
                    damage: 4,
                    windup: 1.2,
                    cooldown: 7,
                    range: 13,
                    radius: 1.5,
                    count: 4,
                    spread: 3,
                    weight: 3,
                    impactSpotAnim: 157,
                    seq: 811,
                    sound: 163,
                    enraged: true,
                },
            ],
        },
    },
    {
        id: "dessous",
        name: "Dessous",
        tags: ["vampyre"],
        npcId: 3459,
        ...HUMAN,
        hp: 3800,
        damage: 0,
        speed: 2.3,
        radius: 0.45,
        xp: 900,
        // A vampyre's
        sounds: { attack: 879, hit: 884, death: 882 },
        scale: 1.7,
        knockbackResist: 1,
        spawnMessage: "Dessous claws his way out of his grave, thirsting for blood!",
        // A vampyre: claws and blood magic that heal him by what they hit,
        // and a leap onto anyone who runs. Enraged, the blood comes three at
        // a time. The Sanguinesti staff's blood spells stand in for his.
        boss: {
            attackInterval: 2,
            lifesteal: 1,
            enrage: {
                message: "Dessous is in a blood frenzy!",
                shout: "Your blood will be mine!",
            },
            attacks: [
                {
                    name: "Claws",
                    kind: "swing",
                    damage: 5,
                    windup: 0.6,
                    cooldown: 0,
                    range: 2.6,
                    radius: 2.2,
                    weight: 4,
                    sound: 879,
                },
                { ...leapAttack(4, 1.8, 7), name: "Vampyric leap" },
                {
                    name: "Blood blast",
                    kind: "projectile",
                    damage: 3,
                    windup: 0.5,
                    cooldown: 2,
                    range: 13,
                    minRange: 3,
                    weight: 3,
                    speed: 10,
                    homing: 0.9,
                    castSpotAnim: 1540,
                    spotAnim: 1539,
                    impactSpotAnim: 1541,
                    sound: 162,
                    hitSound: 163,
                },
                {
                    name: "Blood volley",
                    kind: "projectile",
                    damage: 3,
                    windup: 0.6,
                    cooldown: 4,
                    range: 13,
                    weight: 3,
                    speed: 10,
                    count: 3,
                    spread: 0.3,
                    homing: 0.5,
                    castSpotAnim: 1540,
                    spotAnim: 1539,
                    impactSpotAnim: 1541,
                    sound: 162,
                    hitSound: 163,
                    enraged: true,
                },
            ],
        },
    },
    {
        id: "damis",
        name: "Damis",
        npcId: 682,
        ...HUMAN,
        hp: 2700,
        damage: 0,
        speed: 2.4,
        radius: 0.4,
        xp: 600,
        sounds: HUMAN_SOUNDS,
        scale: 1.6,
        knockbackResist: 1,
        spawnMessage: "Damis steps out of the shadows!",
        // Shadow-wrapped blows that weaken you, and a leap. Beaten, he gets
        // up again in his second form.
        boss: {
            attackInterval: 1.9,
            rises: {
                times: 1,
                hp: 1,
                message: "Damis rises again in his true form, wreathed in shadow!",
                into: "damis_risen",
            },
            enrage: { message: "Damis is enraged!" },
            attacks: [
                {
                    name: "Shadow blow",
                    kind: "swing",
                    damage: 4,
                    windup: 0.6,
                    cooldown: 0,
                    range: 2.6,
                    radius: 2.2,
                    weight: 4,
                    sound: 2500,
                },
                { ...leapAttack(4, 1.8, 7), name: "Shadow leap" },
                {
                    name: "Darkness",
                    kind: "blast",
                    damage: 2,
                    windup: 1,
                    cooldown: 8,
                    range: 12,
                    radius: 1.8,
                    weight: 2,
                    impactSpotAnim: 382,
                    seq: 1979,
                    sound: 177,
                    effect: SHADOWED,
                },
            ],
        },
    },
    // Damis's second form: only comes from the first getting back up
    {
        id: "damis_risen",
        name: "Damis",
        npcId: 683,
        ...HUMAN,
        hp: 2700,
        damage: 0,
        speed: 2.6,
        radius: 0.45,
        xp: 1000,
        sounds: HUMAN_SOUNDS,
        scale: 1.8,
        knockbackResist: 1,
        // Harder blows and more darkness; enraged, shadows burst all around
        boss: {
            attackInterval: 1.7,
            enrage: {
                message: "Damis is consumed by shadow!",
                shout: "You cannot escape the darkness!",
            },
            attacks: [
                {
                    name: "Shadow blow",
                    kind: "swing",
                    damage: 5,
                    windup: 0.6,
                    cooldown: 0,
                    range: 2.6,
                    radius: 2.4,
                    weight: 4,
                    sound: 2500,
                },
                { ...leapAttack(4, 2, 6), name: "Shadow leap" },
                {
                    name: "Darkness",
                    kind: "blast",
                    damage: 3,
                    windup: 1,
                    cooldown: 6,
                    range: 12,
                    radius: 1.8,
                    weight: 3,
                    impactSpotAnim: 382,
                    seq: 1979,
                    sound: 177,
                    effect: SHADOWED,
                },
                {
                    name: "Shadow storm",
                    kind: "blast",
                    damage: 4,
                    windup: 1.2,
                    cooldown: 7,
                    range: 12,
                    radius: 1.6,
                    count: 5,
                    spread: 3.5,
                    weight: 4,
                    impactSpotAnim: 382,
                    seq: 1979,
                    sound: 177,
                    enraged: true,
                },
            ],
        },
    },

    // The final boss: the Kalphite Queen, up out of the hive. Her spines
    // (288, 289) and magic (278-281) are her own; her acid is Smoke Burst's
    // cloud (389).
    {
        id: "kalphite_queen",
        name: "Kalphite Queen",
        npcId: 963,
        idleSeq: 6239,
        walkSeq: 6238,
        attackSeq: 6240,
        deathSeq: 6242,
        hp: 7000,
        damage: 0,
        speed: 1.6,
        radius: 0.45,
        xp: 1500,
        sounds: { attack: 6963, hit: 6958, death: 3606 },
        // Five tiles across in OSRS
        scale: 0.55,
        knockbackResist: 1,
        spawnMessage: "The Kalphite Queen bursts up out of the sand!",
        // Pincers up close, spines and acid from further off, and workers
        // from the hive. Beaten, she gets up again as her flying form.
        boss: {
            attackInterval: 2.5,
            rises: {
                times: 1,
                hp: 0.8,
                message: "The Kalphite Queen's shell cracks open... she rises again, airborne!",
                into: "kalphite_queen_risen",
            },
            enrage: { message: "The Kalphite Queen shrieks in fury!" },
            attacks: [
                {
                    name: "Pincers",
                    kind: "swing",
                    damage: 9,
                    windup: 0.8,
                    cooldown: 0,
                    range: 3.4,
                    radius: 3.2,
                    weight: 4,
                    sound: 6963,
                },
                {
                    name: "Spines",
                    kind: "projectile",
                    damage: 5,
                    windup: 0.5,
                    cooldown: 1,
                    range: 15,
                    minRange: 2.5,
                    weight: 3,
                    speed: 12,
                    count: 3,
                    spread: 0.3,
                    spotAnim: 288,
                    sound: 2707,
                },
                {
                    name: "Acid",
                    kind: "blast",
                    damage: 4,
                    windup: 1.2,
                    cooldown: 7,
                    range: 15,
                    radius: 1.6,
                    count: 3,
                    spread: 2.5,
                    weight: 2,
                    impactSpotAnim: 389,
                    sound: 182,
                    effect: { kind: "poison", damage: 2, seconds: 5 },
                },
                {
                    name: "Call the hive",
                    kind: "summon",
                    damage: 1,
                    windup: 1,
                    cooldown: 14,
                    range: 16,
                    weight: 2,
                    summon: { enemyId: "kalphite_worker", count: 4 },
                    sound: 6963,
                },
                {
                    name: "Spine volley",
                    kind: "projectile",
                    damage: 5,
                    windup: 0.6,
                    cooldown: 3,
                    range: 15,
                    minRange: 2.5,
                    weight: 3,
                    speed: 12,
                    count: 5,
                    spread: 0.25,
                    spotAnim: 288,
                    sound: 2707,
                    enraged: true,
                },
            ],
        },
    },
    // Her second form: only comes from the first getting back up
    {
        id: "kalphite_queen_risen",
        name: "Kalphite Queen",
        npcId: 965,
        idleSeq: 6236,
        walkSeq: 6236,
        attackSeq: 6237,
        deathSeq: 6233,
        hp: 5600,
        damage: 0,
        speed: 1.8,
        radius: 0.45,
        xp: 2000,
        sounds: { attack: 6963, hit: 6958, death: 3606 },
        scale: 0.55,
        knockbackResist: 1,
        // Flying now: magic that chases you, spines and acid. Enraged, her
        // magic comes three at a time.
        boss: {
            attackInterval: 2.4,
            enrage: { message: "The Kalphite Queen screeches, her wings a blur!" },
            attacks: [
                {
                    name: "Pincers",
                    kind: "swing",
                    damage: 10,
                    windup: 0.8,
                    cooldown: 0,
                    range: 3.4,
                    radius: 3.2,
                    weight: 4,
                    sound: 6963,
                },
                {
                    name: "Hive magic",
                    kind: "projectile",
                    damage: 7,
                    windup: 0.5,
                    cooldown: 0,
                    range: 15,
                    minRange: 2.5,
                    weight: 3,
                    speed: 10,
                    homing: 0.8,
                    castSpotAnim: 278,
                    spotAnim: 280,
                    impactSpotAnim: 281,
                    sound: 162,
                    hitSound: 163,
                },
                {
                    name: "Spines",
                    kind: "projectile",
                    damage: 5,
                    windup: 0.5,
                    cooldown: 2,
                    range: 15,
                    minRange: 2.5,
                    weight: 2,
                    speed: 12,
                    count: 3,
                    spread: 0.3,
                    spotAnim: 289,
                    sound: 2707,
                },
                {
                    name: "Acid",
                    kind: "blast",
                    damage: 4,
                    windup: 1.2,
                    cooldown: 6,
                    range: 15,
                    radius: 1.6,
                    count: 4,
                    spread: 3,
                    weight: 2,
                    impactSpotAnim: 389,
                    sound: 182,
                    effect: { kind: "poison", damage: 2, seconds: 5 },
                },
                {
                    name: "Hive magic volley",
                    kind: "projectile",
                    damage: 6,
                    windup: 0.6,
                    cooldown: 3,
                    range: 15,
                    minRange: 2.5,
                    weight: 3,
                    speed: 10,
                    count: 3,
                    spread: 0.3,
                    homing: 0.5,
                    castSpotAnim: 278,
                    spotAnim: 280,
                    impactSpotAnim: 281,
                    sound: 162,
                    hitSound: 163,
                    enraged: true,
                },
            ],
        },
    },
    // Fremennik Province: rock crabs on the beaches, wolves and yaks, the
    // Slayer Dungeon's cockatrices, basilisks, jellies and kurasks, mountain
    // and ice trolls, and the sea's snakes, dagannoths, wallasalki and rock
    // lobsters. Sea creatures sound like frogs, trolls like giants.
    {
        id: "rock_crab",
        name: "Rock crab",
        npcId: 100,
        idleSeq: 1310,
        walkSeq: 1311,
        attackSeq: 1312,
        deathSeq: 1314,
        hp: 5,
        damage: 1,
        speed: 2.1,
        radius: 0.3,
        xp: 6,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
    },
    {
        id: "wolf",
        name: "Wolf",
        npcId: 110,
        idleSeq: 6580,
        walkSeq: 6556,
        attackSeq: 6579,
        deathSeq: 6558,
        hp: 10,
        damage: 2,
        speed: 2.6,
        radius: 0.35,
        xp: 8,
        // A rat's
        sounds: { attack: 710, hit: 713, death: 711 },
        scale: 0.8,
    },
    {
        id: "cockatrice",
        name: "Cockatrice",
        npcId: 419,
        idleSeq: 1561,
        walkSeq: 1559,
        attackSeq: 1562,
        deathSeq: 1563,
        hp: 16,
        damage: 3,
        speed: 2.1,
        radius: 0.35,
        xp: 10,
        // A chicken's
        sounds: { attack: 355, hit: 357, death: 356 },
        scale: 0.8,
    },
    {
        id: "yak",
        name: "Yak",
        npcId: 5816,
        idleSeq: 5785,
        walkSeq: 5781,
        attackSeq: 5782,
        deathSeq: 5784,
        hp: 26,
        damage: 3,
        speed: 1.6,
        radius: 0.45,
        xp: 16,
        // A cow's
        sounds: { attack: 369, hit: 371, death: 370 },
        scale: 0.75,
        knockbackResist: 0.4,
    },
    {
        id: "sea_snake",
        name: "Sea snake young",
        npcId: 1097,
        idleSeq: 3535,
        walkSeq: 3537,
        attackSeq: 3538,
        deathSeq: 3540,
        hp: 8,
        damage: 2,
        speed: 2.7,
        radius: 0.3,
        xp: 5,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
        scale: 0.8,
    },
    {
        id: "mountain_troll",
        name: "Mountain troll",
        npcId: 936,
        idleSeq: 286,
        walkSeq: 283,
        attackSeq: 284,
        deathSeq: 287,
        hp: 30,
        damage: 4,
        speed: 1.8,
        radius: 0.4,
        xp: 18,
        sounds: HUMAN_SOUNDS,
        knockbackResist: 0.3,
    },
    {
        id: "basilisk",
        name: "Basilisk",
        npcId: 417,
        idleSeq: 1545,
        walkSeq: 1544,
        attackSeq: 1546,
        deathSeq: 1548,
        hp: 34,
        damage: 4,
        speed: 1.8,
        radius: 0.4,
        xp: 20,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
        scale: 0.8,
    },
    {
        id: "jelly",
        name: "Jelly",
        npcId: 437,
        idleSeq: 1583,
        walkSeq: 1584,
        attackSeq: 1586,
        deathSeq: 1587,
        hp: 44,
        damage: 5,
        speed: 1.7,
        radius: 0.4,
        xp: 26,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
        knockbackResist: 0.3,
    },
    {
        id: "dagannoth",
        name: "Dagannoth",
        npcId: 973,
        idleSeq: 1338,
        walkSeq: 1339,
        attackSeq: 1341,
        deathSeq: 1342,
        hp: 50,
        damage: 6,
        speed: 2.0,
        radius: 0.45,
        xp: 30,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
        scale: 0.8,
        knockbackResist: 0.4,
    },
    {
        id: "wallasalki",
        name: "Wallasalki",
        npcId: 5938,
        idleSeq: 2364,
        walkSeq: 2363,
        attackSeq: 2365,
        deathSeq: 2367,
        hp: 46,
        damage: 6,
        speed: 2.2,
        radius: 0.35,
        xp: 28,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
    },
    {
        id: "kurask",
        name: "Kurask",
        npcId: 410,
        idleSeq: 1511,
        walkSeq: 1510,
        attackSeq: 1512,
        deathSeq: 1513,
        hp: 66,
        damage: 7,
        speed: 1.7,
        radius: 0.45,
        xp: 38,
        sounds: HUMAN_SOUNDS,
        // Three tiles across in OSRS
        scale: 0.6,
        knockbackResist: 0.6,
    },
    {
        id: "ice_troll",
        name: "Ice troll",
        npcId: 648,
        idleSeq: 286,
        walkSeq: 283,
        attackSeq: 284,
        deathSeq: 287,
        hp: 62,
        damage: 7,
        speed: 1.9,
        radius: 0.4,
        xp: 36,
        sounds: HUMAN_SOUNDS,
        knockbackResist: 0.4,
    },
    {
        id: "rock_lobster",
        name: "Rock lobster",
        npcId: 5944,
        idleSeq: 2858,
        walkSeq: 2857,
        attackSeq: 2859,
        deathSeq: 2862,
        hp: 68,
        damage: 7,
        speed: 1.6,
        radius: 0.45,
        xp: 40,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
        scale: 0.75,
        knockbackResist: 0.6,
    },
    {
        id: "zombified_spawn",
        name: "Zombified spawn",
        tags: ["undead"],
        npcId: 8063,
        idleSeq: 7888,
        walkSeq: 7887,
        attackSeq: 7890,
        deathSeq: 7891,
        hp: 20,
        damage: 5,
        speed: 1.8,
        radius: 0.35,
        xp: 10,
        // A zombie's
        sounds: { attack: 918, hit: 923, death: 922 },
    },

    // The Dagannoth Kings of Waterbirth Island, one of each combat style.
    // They share a skeleton: idle 2850, walk 2849, death 2856, and an attack
    // each.
    {
        id: "dagannoth_rex",
        name: "Dagannoth Rex",
        npcId: 2267,
        idleSeq: 2850,
        walkSeq: 2849,
        attackSeq: 2853,
        deathSeq: 2856,
        hp: 3000,
        damage: 0,
        speed: 2.2,
        radius: 0.45,
        xp: 700,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
        // Three tiles across in OSRS
        scale: 0.85,
        knockbackResist: 1,
        spawnMessage: "Dagannoth Rex surfaces from the waters of Waterbirth!",
        // Melee: heavy bites and a leap onto anyone who runs. Enraged, he
        // bites twice as hard.
        boss: {
            attackInterval: 2.5,
            enrage: { message: "Dagannoth Rex roars!" },
            attacks: [
                {
                    name: "Bite",
                    kind: "swing",
                    damage: 5,
                    windup: 0.7,
                    cooldown: 0,
                    range: 3,
                    radius: 2.8,
                    weight: 4,
                    sound: 4787,
                },
                { ...leapAttack(4, 2, 6), name: "Leap", seq: 2853 },
                {
                    name: "Crushing bite",
                    kind: "swing",
                    damage: 7,
                    windup: 1,
                    cooldown: 4,
                    range: 3,
                    radius: 3.2,
                    weight: 3,
                    sound: 4787,
                    enraged: true,
                },
            ],
        },
    },
    {
        id: "dagannoth_supreme",
        name: "Dagannoth Supreme",
        npcId: 2265,
        idleSeq: 2850,
        walkSeq: 2849,
        attackSeq: 2854,
        deathSeq: 2856,
        hp: 4200,
        damage: 0,
        speed: 2.2,
        radius: 0.45,
        xp: 900,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
        scale: 0.85,
        knockbackResist: 1,
        spawnMessage: "Dagannoth Supreme rises out of the surf!",
        // Ranged: thrown axes from a distance, in volleys. Enraged, five at a
        // time. The rune thrownaxe's spin (41) stands in for his.
        boss: {
            attackInterval: 2.1,
            keepDistance: 6,
            enrage: { message: "Dagannoth Supreme bellows!" },
            attacks: [
                {
                    name: "Thrownaxe",
                    kind: "projectile",
                    damage: 3,
                    windup: 0.4,
                    cooldown: 0,
                    range: 14,
                    weight: 4,
                    speed: 13,
                    spotAnim: 41,
                    sound: 2707,
                },
                {
                    name: "Axe volley",
                    kind: "projectile",
                    damage: 3,
                    windup: 0.6,
                    cooldown: 4,
                    range: 14,
                    weight: 3,
                    speed: 13,
                    count: 3,
                    spread: 0.3,
                    spotAnim: 41,
                    sound: 2707,
                },
                {
                    name: "Axe storm",
                    kind: "projectile",
                    damage: 3,
                    windup: 0.6,
                    cooldown: 3,
                    range: 14,
                    weight: 4,
                    speed: 13,
                    count: 5,
                    spread: 0.25,
                    spotAnim: 41,
                    sound: 2707,
                    enraged: true,
                },
            ],
        },
    },
    {
        id: "dagannoth_prime",
        name: "Dagannoth Prime",
        npcId: 2266,
        idleSeq: 2850,
        walkSeq: 2849,
        attackSeq: 2855,
        deathSeq: 2856,
        hp: 5000,
        damage: 0,
        speed: 2.2,
        radius: 0.45,
        xp: 1100,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
        scale: 0.85,
        knockbackResist: 1,
        spawnMessage: "Dagannoth Prime emerges, crackling with water magic!",
        // Magic: Water Waves that curve after you, and water crashing down
        // around you. Enraged, three waves at once.
        boss: {
            attackInterval: 3.2,
            keepDistance: 6,
            enrage: { message: "Dagannoth Prime is enraged!" },
            attacks: [
                {
                    name: "Water Wave",
                    kind: "projectile",
                    damage: 4,
                    windup: 0.5,
                    cooldown: 0,
                    range: 14,
                    weight: 4,
                    speed: 9,
                    homing: 0.9,
                    castSpotAnim: 161,
                    spotAnim: 162,
                    impactSpotAnim: 163,
                    sound: 162,
                    hitSound: 163,
                },
                {
                    name: "Tidal crash",
                    kind: "blast",
                    damage: 4,
                    windup: 1.2,
                    cooldown: 7,
                    range: 14,
                    radius: 1.5,
                    count: 4,
                    spread: 3,
                    weight: 3,
                    impactSpotAnim: 163,
                    sound: 163,
                },
                {
                    name: "Water Wave volley",
                    kind: "projectile",
                    damage: 3,
                    windup: 0.6,
                    cooldown: 4,
                    range: 14,
                    weight: 3,
                    speed: 9,
                    count: 3,
                    spread: 0.3,
                    homing: 0.5,
                    castSpotAnim: 161,
                    spotAnim: 162,
                    impactSpotAnim: 163,
                    sound: 162,
                    hitSound: 163,
                    enraged: true,
                },
            ],
        },
    },

    // The final boss: Vorkath, the undead dragon of Ungael. Fights like the
    // King Black Dragon, with its own extras: acid that poisons, ice that
    // freezes and zombified spawn. The King Black Dragon's breaths (393, 395)
    // stand in for its own, and Smoke Burst's cloud (389) for its acid.
    {
        id: "vorkath",
        name: "Vorkath",
        tags: ["undead"],
        npcId: 8061,
        idleSeq: 7948,
        walkSeq: 7947,
        attackSeq: 7952,
        deathSeq: 7949,
        hp: 14000,
        damage: 0,
        speed: 1.4,
        radius: 0.45,
        xp: 2500,
        // A zombie's: it's undead
        sounds: { attack: 918, hit: 923, death: 922 },
        // Seven tiles across in OSRS
        scale: 0.42,
        knockbackResist: 1,
        spawnMessage: "Vorkath wakes, and rises from the ice of Ungael!",
        boss: {
            attackInterval: 2,
            enrage: { message: "Vorkath's undead fire burns brighter!" },
            attacks: [
                {
                    name: "Claws",
                    kind: "swing",
                    damage: 9,
                    windup: 0.8,
                    cooldown: 0,
                    range: 3.6,
                    radius: 3.4,
                    weight: 4,
                    sound: 2500,
                },
                {
                    name: "Dragonfire",
                    kind: "projectile",
                    damage: 9,
                    windup: 0.5,
                    cooldown: 0,
                    range: 15,
                    minRange: 2.5,
                    weight: 3,
                    speed: 10,
                    spotAnim: 393,
                    impactSpotAnim: 157,
                    sound: 162,
                    hitSound: 163,
                },
                {
                    name: "Ice dragonfire",
                    kind: "projectile",
                    damage: 3,
                    windup: 0.5,
                    cooldown: 7,
                    range: 15,
                    minRange: 2.5,
                    weight: 2,
                    speed: 10,
                    spotAnim: 395,
                    sound: 162,
                    effect: { kind: "freeze", seconds: 1.6 },
                },
                {
                    name: "Acid",
                    kind: "blast",
                    damage: 3,
                    windup: 1.2,
                    cooldown: 8,
                    range: 15,
                    radius: 1.6,
                    count: 5,
                    spread: 4,
                    weight: 2,
                    impactSpotAnim: 389,
                    sound: 182,
                    effect: { kind: "poison", damage: 2, seconds: 5 },
                },
                {
                    name: "Zombified spawn",
                    kind: "summon",
                    damage: 1,
                    windup: 1,
                    cooldown: 15,
                    range: 16,
                    weight: 2,
                    summon: { enemyId: "zombified_spawn", count: 3 },
                    sound: 918,
                },
                {
                    name: "Rapid fire",
                    kind: "projectile",
                    damage: 6,
                    windup: 0.6,
                    cooldown: 3,
                    range: 15,
                    minRange: 2.5,
                    weight: 3,
                    speed: 11,
                    count: 5,
                    spread: 0.25,
                    spotAnim: 393,
                    impactSpotAnim: 157,
                    sound: 162,
                    hitSound: 163,
                    enraged: true,
                },
            ],
        },
    },
    // Karamja: monkeys, snakes and jungle spiders, the jungle's tribesmen,
    // jogres and moss giants, lesser demons, and the TzHaar up out of the
    // volcano. The TzHaar sound like giants.
    {
        id: "monkey",
        name: "Monkey",
        npcId: 1038,
        idleSeq: 222,
        walkSeq: 219,
        attackSeq: 220,
        deathSeq: 223,
        hp: 4,
        damage: 1,
        speed: 2.5,
        radius: 0.3,
        xp: 5,
        // A rat's
        sounds: { attack: 710, hit: 713, death: 711 },
    },
    {
        id: "snake",
        name: "Snake",
        npcId: 1037,
        idleSeq: 277,
        walkSeq: 274,
        attackSeq: 275,
        deathSeq: 278,
        hp: 9,
        damage: 2,
        speed: 2.3,
        radius: 0.3,
        xp: 8,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
    },
    {
        id: "jungle_spider",
        name: "Jungle spider",
        npcId: 3020,
        idleSeq: 5326,
        walkSeq: 5325,
        attackSeq: 5327,
        deathSeq: 5329,
        hp: 14,
        damage: 3,
        speed: 2.5,
        radius: 0.35,
        xp: 9,
        sounds: { attack: 3605, hit: 3607, death: 3606 },
    },
    {
        id: "tribesman",
        name: "Tribesman",
        npcId: 530,
        ...HUMAN,
        idleSeq: 813,
        hp: 18,
        damage: 3,
        speed: 2.1,
        radius: 0.35,
        xp: 11,
        sounds: HUMAN_SOUNDS,
    },
    {
        id: "tz_kih",
        name: "Tz-Kih",
        npcId: 2189,
        idleSeq: 2618,
        walkSeq: 2619,
        attackSeq: 2621,
        deathSeq: 2620,
        hp: 8,
        damage: 2,
        speed: 2.8,
        radius: 0.3,
        xp: 5,
        // A bat's
        sounds: { attack: 292, hit: 294, death: 293 },
    },
    {
        id: "jogre",
        name: "Jogre",
        npcId: 2094,
        idleSeq: 130,
        walkSeq: 127,
        attackSeq: 128,
        deathSeq: 131,
        hp: 32,
        damage: 4,
        speed: 1.8,
        radius: 0.4,
        xp: 19,
        sounds: HUMAN_SOUNDS,
        scale: 0.85,
        knockbackResist: 0.4,
    },
    {
        id: "moss_giant",
        name: "Moss giant",
        npcId: 2090,
        idleSeq: 4656,
        walkSeq: 4654,
        attackSeq: 4652,
        deathSeq: 4653,
        hp: 40,
        damage: 5,
        speed: 1.6,
        radius: 0.45,
        xp: 24,
        sounds: HUMAN_SOUNDS,
        scale: 0.85,
        knockbackResist: 0.6,
    },
    {
        id: "tz_kek",
        name: "Tz-Kek",
        npcId: 2191,
        idleSeq: 2624,
        walkSeq: 2623,
        attackSeq: 2625,
        deathSeq: 2627,
        hp: 44,
        damage: 5,
        speed: 1.8,
        radius: 0.4,
        xp: 26,
        sounds: HUMAN_SOUNDS,
        scale: 0.8,
        knockbackResist: 0.4,
    },
    {
        id: "lesser_demon",
        name: "Lesser demon",
        npcId: 2005,
        idleSeq: 66,
        walkSeq: 63,
        attackSeq: 64,
        deathSeq: 67,
        hp: 52,
        damage: 6,
        speed: 1.9,
        radius: 0.45,
        xp: 30,
        // An imp's
        sounds: { attack: 534, hit: 536, death: 535 },
        scale: 0.8,
        knockbackResist: 0.5,
    },
    {
        id: "tok_xil",
        name: "Tok-Xil",
        npcId: 2193,
        idleSeq: 2631,
        walkSeq: 2632,
        attackSeq: 2628,
        deathSeq: 2630,
        hp: 62,
        damage: 7,
        speed: 1.8,
        radius: 0.45,
        xp: 36,
        sounds: HUMAN_SOUNDS,
        // Three tiles across in OSRS
        scale: 0.6,
        knockbackResist: 0.6,
    },
    {
        id: "yt_mejkot",
        name: "Yt-MejKot",
        npcId: 3123,
        idleSeq: 2636,
        walkSeq: 2634,
        attackSeq: 2637,
        deathSeq: 2638,
        hp: 72,
        damage: 7,
        speed: 1.6,
        radius: 0.45,
        xp: 42,
        sounds: HUMAN_SOUNDS,
        // Four tiles across in OSRS
        scale: 0.5,
        knockbackResist: 0.7,
    },
    // TzTok-Jad's healers, which come to his call
    {
        id: "yt_hurkot",
        name: "Yt-HurKot",
        npcId: 3128,
        idleSeq: 2636,
        walkSeq: 2634,
        attackSeq: 2637,
        deathSeq: 2638,
        hp: 30,
        damage: 4,
        speed: 2.0,
        radius: 0.35,
        xp: 15,
        sounds: HUMAN_SOUNDS,
    },

    // Karamja's bosses: Elvarg of Crandor, Legends' Quest's Nezikchened, and
    // Ket-Zek out of the Fight Caves (its magic 445-446)
    {
        id: "elvarg",
        name: "Elvarg",
        npcId: 6349,
        idleSeq: 90,
        walkSeq: 79,
        attackSeq: 91,
        deathSeq: 92,
        hp: 3200,
        damage: 0,
        speed: 1.8,
        radius: 0.45,
        xp: 800,
        // A zombie's stand in for a dragon's
        sounds: { attack: 918, hit: 923, death: 922 },
        // Four tiles across in OSRS
        scale: 0.65,
        knockbackResist: 1,
        spawnMessage: "Elvarg flies in from Crandor!",
        // Claws up close and fire from further off. Enraged, she breathes
        // fire three ways at once.
        boss: {
            attackInterval: 2.9,
            enrage: { message: "Elvarg roars in fury!" },
            attacks: [
                {
                    name: "Claws",
                    kind: "swing",
                    damage: 5,
                    windup: 0.7,
                    cooldown: 0,
                    range: 3.2,
                    radius: 3,
                    weight: 4,
                    sound: 2500,
                },
                {
                    name: "Dragonfire",
                    kind: "projectile",
                    damage: 5,
                    windup: 0.6,
                    cooldown: 0,
                    range: 15,
                    minRange: 2.5,
                    weight: 3,
                    speed: 10,
                    spotAnim: 393,
                    impactSpotAnim: 157,
                    seq: 81,
                    sound: 3752,
                    hitSound: 163,
                },
                {
                    name: "Firestorm",
                    kind: "blast",
                    damage: 4,
                    windup: 1.2,
                    cooldown: 7,
                    range: 15,
                    radius: 1.5,
                    count: 3,
                    spread: 2.5,
                    weight: 2,
                    impactSpotAnim: 157,
                    seq: 81,
                    sound: 3752,
                },
                {
                    name: "Dragonfire volley",
                    kind: "projectile",
                    damage: 4,
                    windup: 0.6,
                    cooldown: 3,
                    range: 15,
                    minRange: 2.5,
                    weight: 3,
                    speed: 10,
                    count: 3,
                    spread: 0.28,
                    spotAnim: 393,
                    impactSpotAnim: 157,
                    seq: 81,
                    sound: 3752,
                    hitSound: 163,
                    enraged: true,
                },
            ],
        },
    },
    {
        id: "nezikchened",
        name: "Nezikchened",
        npcId: 3962,
        idleSeq: 66,
        walkSeq: 63,
        attackSeq: 64,
        deathSeq: 67,
        hp: 4400,
        damage: 0,
        speed: 2.1,
        radius: 0.45,
        xp: 1000,
        // An imp's
        sounds: { attack: 534, hit: 536, death: 535 },
        // Three tiles across in OSRS
        scale: 0.85,
        knockbackResist: 1,
        spawnMessage: "Nezikchened claws his way up out of the Viyeldi caves!",
        // Claws, Fire Blasts, and lesser demons at his call. Enraged, fire
        // rains down all around you.
        boss: {
            attackInterval: 1.9,
            enrage: {
                message: "Nezikchened is enraged!",
                shout: "I will feast on your soul!",
            },
            attacks: [
                {
                    name: "Claws",
                    kind: "swing",
                    damage: 5,
                    windup: 0.7,
                    cooldown: 0,
                    range: 3,
                    radius: 2.8,
                    weight: 4,
                    sound: 534,
                },
                {
                    name: "Fire Blast",
                    kind: "projectile",
                    damage: 4,
                    windup: 0.5,
                    cooldown: 1,
                    range: 14,
                    minRange: 3,
                    weight: 3,
                    speed: 10,
                    homing: 0.8,
                    castSpotAnim: 129,
                    spotAnim: 130,
                    impactSpotAnim: 131,
                    sound: 155,
                    hitSound: 156,
                },
                {
                    name: "Summon demons",
                    kind: "summon",
                    damage: 1,
                    windup: 1,
                    cooldown: 14,
                    range: 16,
                    weight: 2,
                    summon: { enemyId: "lesser_demon", count: 2 },
                    sound: 534,
                },
                {
                    name: "Hellfire",
                    kind: "blast",
                    damage: 4,
                    windup: 1.2,
                    cooldown: 6,
                    range: 14,
                    radius: 1.5,
                    count: 5,
                    spread: 3.5,
                    weight: 4,
                    impactSpotAnim: 157,
                    sound: 163,
                    enraged: true,
                },
            ],
        },
    },
    {
        id: "ket_zek",
        name: "Ket-Zek",
        npcId: 3125,
        idleSeq: 2642,
        walkSeq: 2643,
        attackSeq: 2644,
        deathSeq: 2646,
        hp: 5600,
        damage: 0,
        speed: 1.8,
        radius: 0.45,
        xp: 1200,
        sounds: HUMAN_SOUNDS,
        // Five tiles across in OSRS
        scale: 0.55,
        knockbackResist: 1,
        spawnMessage: "Ket-Zek climbs out of the Fight Caves!",
        // A heavy fist up close and big magic bolts from afar, and it blinks
        // away from anyone who gets close. Enraged, three bolts at once.
        boss: {
            attackInterval: 2,
            keepDistance: 5,
            enrage: { message: "Ket-Zek glows white hot!" },
            attacks: [
                {
                    name: "Fist",
                    kind: "swing",
                    damage: 6,
                    windup: 0.8,
                    cooldown: 0,
                    range: 3.2,
                    radius: 3,
                    weight: 3,
                    sound: 2500,
                },
                {
                    name: "Magma bolt",
                    kind: "projectile",
                    damage: 5,
                    windup: 0.6,
                    cooldown: 0,
                    range: 15,
                    minRange: 3,
                    weight: 4,
                    speed: 9,
                    homing: 0.7,
                    spotAnim: 445,
                    impactSpotAnim: 446,
                    seq: 2647,
                    sound: 162,
                    hitSound: 163,
                },
                {
                    name: "Teleport",
                    kind: "teleport",
                    damage: 1,
                    windup: 0.6,
                    cooldown: 8,
                    range: 3,
                    radius: 7,
                    weight: 4,
                    castSpotAnim: 111,
                    impactSpotAnim: 111,
                    sound: 200,
                },
                {
                    name: "Magma volley",
                    kind: "projectile",
                    damage: 4,
                    windup: 0.7,
                    cooldown: 3,
                    range: 15,
                    minRange: 3,
                    weight: 3,
                    speed: 9,
                    count: 3,
                    spread: 0.3,
                    homing: 0.4,
                    spotAnim: 445,
                    impactSpotAnim: 446,
                    seq: 2647,
                    sound: 162,
                    hitSound: 163,
                    enraged: true,
                },
            ],
        },
    },

    // The final boss: TzTok-Jad, the Fight Caves' last. Slow, telegraphed
    // magic (447-448) and rocks that crash down where you stood (451); his
    // Yt-HurKot come to his call. Enraged, the rocks fall all around.
    {
        id: "tztok_jad",
        name: "TzTok-Jad",
        npcId: 3127,
        idleSeq: 2650,
        walkSeq: 2651,
        attackSeq: 2655,
        deathSeq: 2654,
        hp: 15500,
        damage: 0,
        speed: 1.5,
        radius: 0.45,
        xp: 3000,
        sounds: HUMAN_SOUNDS,
        // Five tiles across in OSRS
        scale: 0.55,
        knockbackResist: 1,
        spawnMessage: "TzTok-Jad rises out of the volcano!",
        boss: {
            attackInterval: 2,
            enrage: {
                message: "TzTok-Jad is enraged!",
                shout: "JalYt, you will burn!",
            },
            attacks: [
                {
                    name: "Stomp",
                    kind: "swing",
                    damage: 9,
                    windup: 0.9,
                    cooldown: 0,
                    range: 3.6,
                    radius: 3.4,
                    weight: 4,
                    sound: 2500,
                },
                {
                    name: "Magic",
                    kind: "projectile",
                    damage: 8,
                    windup: 1.2,
                    cooldown: 0,
                    range: 16,
                    minRange: 3,
                    weight: 3,
                    speed: 9,
                    homing: 0.9,
                    castSpotAnim: 447,
                    spotAnim: 448,
                    impactSpotAnim: 157,
                    seq: 2656,
                    sound: 162,
                    hitSound: 163,
                },
                {
                    name: "Rockfall",
                    kind: "blast",
                    damage: 8,
                    windup: 1.4,
                    cooldown: 0,
                    range: 16,
                    minRange: 3,
                    radius: 1.8,
                    weight: 3,
                    impactSpotAnim: 451,
                    seq: 2652,
                    sound: 2500,
                },
                {
                    name: "Call healers",
                    kind: "summon",
                    damage: 1,
                    windup: 1,
                    cooldown: 18,
                    range: 16,
                    weight: 2,
                    summon: { enemyId: "yt_hurkot", count: 4 },
                    sound: 2500,
                },
                {
                    name: "Rockstorm",
                    kind: "blast",
                    damage: 7,
                    windup: 1.3,
                    cooldown: 5,
                    range: 16,
                    radius: 1.6,
                    count: 5,
                    spread: 3.5,
                    weight: 4,
                    impactSpotAnim: 451,
                    seq: 2652,
                    sound: 2500,
                    enraged: true,
                },
            ],
        },
    },
    // Kourend: guanic bats and swamp crabs, lizards and sulphur lizards,
    // lizardmen and their brutes, bloodvelds and hydras, and Xeric's deathly
    // rangers and mages and skeletal mystics. Lizards and lizardmen sound like
    // frogs, bloodvelds like zombies.
    {
        id: "guanic_bat",
        name: "Guanic bat",
        npcId: 7587,
        idleSeq: 35,
        walkSeq: 29,
        attackSeq: 30,
        deathSeq: 36,
        hp: 4,
        damage: 1,
        speed: 2.6,
        radius: 0.3,
        xp: 5,
        sounds: { attack: 292, hit: 294, death: 293 },
    },
    {
        id: "swamp_crab",
        name: "Swamp crab",
        npcId: 8297,
        idleSeq: 1310,
        walkSeq: 1311,
        attackSeq: 1312,
        deathSeq: 1314,
        hp: 9,
        damage: 2,
        speed: 2.0,
        radius: 0.3,
        xp: 8,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
    },
    {
        id: "lizard",
        name: "Lizard",
        npcId: 458,
        idleSeq: 2774,
        walkSeq: 2775,
        attackSeq: 2776,
        deathSeq: 2778,
        hp: 14,
        damage: 3,
        speed: 2.2,
        radius: 0.35,
        xp: 9,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
        // Three tiles across in OSRS
        scale: 0.6,
    },
    {
        id: "sulphur_lizard",
        name: "Sulphur lizard",
        npcId: 8614,
        idleSeq: 8282,
        walkSeq: 8281,
        attackSeq: 8283,
        deathSeq: 8284,
        hp: 18,
        damage: 3,
        speed: 2.2,
        radius: 0.35,
        xp: 11,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
        scale: 0.8,
    },
    {
        id: "lizardman",
        name: "Lizardman",
        npcId: 6914,
        idleSeq: 7191,
        walkSeq: 7195,
        attackSeq: 7192,
        deathSeq: 7196,
        hp: 30,
        damage: 4,
        speed: 1.9,
        radius: 0.35,
        xp: 18,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
    },
    {
        id: "bloodveld",
        name: "Bloodveld",
        npcId: 484,
        idleSeq: 1551,
        walkSeq: 1549,
        attackSeq: 1552,
        deathSeq: 1553,
        hp: 40,
        damage: 5,
        speed: 1.9,
        radius: 0.4,
        xp: 24,
        sounds: { attack: 918, hit: 923, death: 922 },
        scale: 0.8,
        knockbackResist: 0.3,
    },
    {
        id: "deathly_ranger",
        name: "Deathly ranger",
        npcId: 7559,
        ...HUMAN,
        // A bow shot
        attackSeq: 426,
        hp: 36,
        damage: 5,
        speed: 2.0,
        radius: 0.35,
        xp: 22,
        sounds: HUMAN_SOUNDS,
    },
    {
        id: "deathly_mage",
        name: "Deathly mage",
        npcId: 7560,
        ...HUMAN,
        idleSeq: 813,
        walkSeq: 1205,
        attackSeq: 711,
        hp: 36,
        damage: 5,
        speed: 2.0,
        radius: 0.35,
        xp: 22,
        sounds: HUMAN_SOUNDS,
    },
    {
        id: "lizardman_brute",
        name: "Lizardman brute",
        npcId: 6918,
        idleSeq: 7191,
        walkSeq: 7195,
        attackSeq: 7192,
        deathSeq: 7196,
        hp: 54,
        damage: 6,
        speed: 2.0,
        radius: 0.4,
        xp: 32,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
        knockbackResist: 0.4,
    },
    {
        id: "skeletal_mystic",
        name: "Skeletal mystic",
        tags: ["undead"],
        npcId: 7604,
        idleSeq: 5483,
        walkSeq: 5479,
        attackSeq: 5485,
        deathSeq: -1,
        hp: 52,
        damage: 6,
        speed: 1.8,
        radius: 0.4,
        xp: 30,
        // A skeleton's
        sounds: { attack: 776, hit: 779, death: 777 },
        scale: 0.8,
    },
    {
        id: "hydra",
        name: "Hydra",
        npcId: 8609,
        idleSeq: 8260,
        walkSeq: 8259,
        attackSeq: 8261,
        deathSeq: 8264,
        hp: 72,
        damage: 7,
        speed: 1.6,
        radius: 0.45,
        xp: 42,
        sounds: { attack: 4089, hit: 4492, death: 4075 },
        // Three tiles across in OSRS
        scale: 0.6,
        knockbackResist: 0.7,
    },
    // What Kourend's bosses call in
    {
        id: "shaman_spawn",
        name: "Spawn",
        npcId: 6768,
        idleSeq: 7191,
        walkSeq: 7195,
        attackSeq: 7192,
        deathSeq: 7196,
        hp: 10,
        damage: 4,
        speed: 2.4,
        radius: 0.3,
        xp: 6,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
    },
    {
        id: "spawn_of_sarachnis",
        name: "Spawn of Sarachnis",
        npcId: 8714,
        idleSeq: 8320,
        walkSeq: 8319,
        attackSeq: 8147,
        deathSeq: 8318,
        hp: 24,
        damage: 4,
        speed: 2.3,
        radius: 0.35,
        xp: 12,
        sounds: { attack: 3605, hit: 3607, death: 3606 },
        scale: 0.7,
    },
    {
        id: "reanimated_demon",
        name: "Reanimated demon spawn",
        tags: ["undead"],
        npcId: 7287,
        idleSeq: 66,
        walkSeq: 63,
        attackSeq: 64,
        deathSeq: 67,
        hp: 26,
        damage: 4,
        speed: 2.0,
        radius: 0.35,
        xp: 12,
        sounds: { attack: 534, hit: 536, death: 535 },
    },

    // Kourend's bosses: the Lizardman shaman, Sarachnis under the Forthos
    // Dungeon, and Skotizo of the Catacombs. The King Black Dragon's poison
    // (394) and ice (395) breaths stand in for the shaman's acid and
    // Sarachnis's webs, and the Ancient barrages for Skotizo's dark magic.
    {
        id: "lizardman_shaman",
        name: "Lizardman shaman",
        npcId: 6766,
        idleSeq: 7191,
        walkSeq: 7195,
        attackSeq: 7192,
        deathSeq: 7196,
        hp: 3400,
        damage: 0,
        speed: 2.2,
        radius: 0.45,
        xp: 800,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
        // Three tiles across in OSRS
        scale: 0.85,
        knockbackResist: 1,
        spawnMessage: "A Lizardman shaman stalks out of the canyon!",
        // Claws, acid that poisons, a jump onto you, and spawn that run at
        // you. Enraged, the acid comes three at a time.
        boss: {
            attackInterval: 1.9,
            enrage: { message: "The Lizardman shaman hisses in fury!" },
            attacks: [
                {
                    name: "Claws",
                    kind: "swing",
                    damage: 4,
                    windup: 0.6,
                    cooldown: 0,
                    range: 2.8,
                    radius: 2.6,
                    weight: 4,
                    sound: 4787,
                },
                {
                    name: "Acid spit",
                    kind: "projectile",
                    damage: 2,
                    windup: 0.5,
                    cooldown: 2,
                    range: 14,
                    minRange: 3,
                    weight: 3,
                    speed: 10,
                    homing: 0.6,
                    spotAnim: 394,
                    seq: 7193,
                    sound: 4787,
                    effect: { kind: "poison", damage: 2, seconds: 4 },
                },
                { ...leapAttack(4, 2, 6), name: "Jump", seq: 7152 },
                {
                    name: "Spawn",
                    kind: "summon",
                    damage: 1,
                    windup: 1,
                    cooldown: 18,
                    range: 16,
                    weight: 2,
                    summon: { enemyId: "shaman_spawn", count: 3 },
                    seq: 7193,
                    sound: 4787,
                },
                {
                    name: "Acid volley",
                    kind: "projectile",
                    damage: 2,
                    windup: 0.6,
                    cooldown: 4,
                    range: 14,
                    minRange: 3,
                    weight: 3,
                    speed: 10,
                    count: 3,
                    spread: 0.3,
                    spotAnim: 394,
                    seq: 7193,
                    sound: 4787,
                    effect: { kind: "poison", damage: 2, seconds: 4 },
                    enraged: true,
                },
            ],
        },
    },
    {
        id: "sarachnis",
        name: "Sarachnis",
        npcId: 8713,
        idleSeq: 8320,
        walkSeq: 8319,
        attackSeq: 8147,
        deathSeq: 8318,
        hp: 4600,
        damage: 0,
        speed: 2,
        radius: 0.45,
        xp: 1000,
        sounds: { attack: 6963, hit: 6958, death: 3606 },
        // Five tiles across in OSRS
        scale: 0.55,
        knockbackResist: 1,
        spawnMessage: "Sarachnis crawls up out of the Forthos Dungeon!",
        // Bites, webs that hold you in place, and her spawn. Enraged, three
        // webs at once.
        boss: {
            attackInterval: 1.9,
            enrage: { message: "Sarachnis shrieks!" },
            attacks: [
                {
                    name: "Bite",
                    kind: "swing",
                    damage: 5,
                    windup: 0.7,
                    cooldown: 0,
                    range: 3.2,
                    radius: 3,
                    weight: 4,
                    sound: 6963,
                },
                {
                    name: "Web",
                    kind: "projectile",
                    damage: 2,
                    windup: 0.5,
                    cooldown: 4,
                    range: 14,
                    minRange: 3,
                    weight: 3,
                    speed: 10,
                    spotAnim: 395,
                    sound: 6963,
                    effect: { kind: "freeze", seconds: 1.4 },
                },
                {
                    name: "Spawn",
                    kind: "summon",
                    damage: 1,
                    windup: 1,
                    cooldown: 14,
                    range: 16,
                    weight: 2,
                    summon: { enemyId: "spawn_of_sarachnis", count: 3 },
                    sound: 6963,
                },
                {
                    name: "Web volley",
                    kind: "projectile",
                    damage: 2,
                    windup: 0.6,
                    cooldown: 5,
                    range: 14,
                    minRange: 3,
                    weight: 3,
                    speed: 10,
                    count: 3,
                    spread: 0.3,
                    spotAnim: 395,
                    sound: 6963,
                    effect: { kind: "freeze", seconds: 1.2 },
                    enraged: true,
                },
            ],
        },
    },
    {
        id: "skotizo",
        name: "Skotizo",
        npcId: 7286,
        idleSeq: 4675,
        walkSeq: 4674,
        attackSeq: 4680,
        deathSeq: 4624,
        hp: 6000,
        damage: 0,
        speed: 2,
        radius: 0.45,
        xp: 1300,
        sounds: { attack: 534, hit: 536, death: 535 },
        // Five tiles across in OSRS
        scale: 0.55,
        knockbackResist: 1,
        spawnMessage: "Skotizo bursts up out of the Catacombs of Kourend!",
        // Claws, dark magic, and reanimated demons. Enraged, darkness falls
        // all around you.
        boss: {
            attackInterval: 2.5,
            enrage: {
                message: "Skotizo is enraged!",
                shout: "Your soul is mine!",
            },
            attacks: [
                {
                    name: "Claws",
                    kind: "swing",
                    damage: 5,
                    windup: 0.7,
                    cooldown: 0,
                    range: 3.2,
                    radius: 3,
                    weight: 4,
                    sound: 534,
                },
                {
                    name: "Dark magic",
                    kind: "blast",
                    damage: 4,
                    windup: 1,
                    cooldown: 0,
                    range: 14,
                    minRange: 3,
                    radius: 1.6,
                    weight: 3,
                    impactSpotAnim: 383,
                    sound: 175,
                },
                {
                    name: "Reanimate",
                    kind: "summon",
                    damage: 1,
                    windup: 1,
                    cooldown: 12,
                    range: 16,
                    weight: 2,
                    summon: { enemyId: "reanimated_demon", count: 3 },
                    sound: 534,
                },
                {
                    name: "Darkness falls",
                    kind: "blast",
                    damage: 3,
                    windup: 1.2,
                    cooldown: 6,
                    range: 14,
                    radius: 1.5,
                    count: 5,
                    spread: 3.5,
                    weight: 4,
                    impactSpotAnim: 383,
                    sound: 175,
                    enraged: true,
                },
            ],
        },
    },

    // The final boss: the Alchemical Hydra of Mount Karuulm. Acid pools,
    // lightning (the King Black Dragon's shock breath, 396) and a ring of
    // fire. It has no death animation, so it sinks.
    {
        id: "alchemical_hydra",
        name: "Alchemical Hydra",
        npcId: 8615,
        idleSeq: 8233,
        walkSeq: 8232,
        attackSeq: 8234,
        deathSeq: -1,
        hp: 17000,
        damage: 0,
        speed: 1.5,
        radius: 0.45,
        xp: 3500,
        sounds: { attack: 4128, hit: 4492, death: 4075 },
        // Six tiles across in OSRS
        scale: 0.48,
        knockbackResist: 1,
        spawnMessage: "The Alchemical Hydra slithers out of its lair under Mount Karuulm!",
        boss: {
            attackInterval: 3.6,
            enrage: {
                message: "The Alchemical Hydra's last head turns black. It's enraged!",
            },
            attacks: [
                {
                    name: "Bite",
                    kind: "swing",
                    damage: 9,
                    windup: 0.8,
                    cooldown: 0,
                    range: 3.6,
                    radius: 3.4,
                    weight: 4,
                    sound: 4128,
                },
                {
                    name: "Lightning",
                    kind: "projectile",
                    damage: 6,
                    windup: 0.5,
                    cooldown: 0,
                    range: 15,
                    minRange: 2.5,
                    weight: 3,
                    speed: 11,
                    count: 2,
                    spread: 0.4,
                    homing: 0.3,
                    spotAnim: 396,
                    seq: 8235,
                    sound: 4128,
                },
                {
                    name: "Acid pools",
                    kind: "blast",
                    damage: 3,
                    windup: 1.2,
                    cooldown: 7,
                    range: 15,
                    radius: 1.6,
                    count: 5,
                    spread: 3.5,
                    weight: 2,
                    impactSpotAnim: 389,
                    seq: 8236,
                    sound: 182,
                    effect: { kind: "poison", damage: 3, seconds: 5 },
                },
                {
                    name: "Firestorm",
                    kind: "blast",
                    damage: 6,
                    windup: 1.3,
                    cooldown: 9,
                    range: 15,
                    radius: 1.5,
                    count: 6,
                    spread: 4,
                    weight: 2,
                    impactSpotAnim: 157,
                    seq: 8236,
                    sound: 163,
                },
                {
                    name: "Lightning volley",
                    kind: "projectile",
                    damage: 5,
                    windup: 0.6,
                    cooldown: 3,
                    range: 15,
                    minRange: 2.5,
                    weight: 3,
                    speed: 11,
                    count: 4,
                    spread: 0.3,
                    spotAnim: 396,
                    seq: 8235,
                    sound: 4128,
                    enraged: true,
                },
            ],
        },
    },
    // Varlamore: jerboas, pyre foxes and antelopes, blood jaguars, the
    // Colosseum's serpent shamans, jaguar warriors and Fremennik warband,
    // sulphur and frost naguas, gryphons and minotaurs. Beasts sound like
    // rats, cows and bats.
    {
        id: "embertailed_jerboa",
        name: "Embertailed jerboa",
        npcId: 13139,
        idleSeq: 6089,
        walkSeq: 6088,
        attackSeq: 6090,
        deathSeq: 6092,
        hp: 4,
        damage: 1,
        speed: 2.6,
        radius: 0.3,
        xp: 5,
        sounds: { attack: 710, hit: 713, death: 711 },
    },
    {
        id: "pyre_fox",
        name: "Pyre fox",
        npcId: 13138,
        idleSeq: 7269,
        walkSeq: 6577,
        attackSeq: 6578,
        deathSeq: 6576,
        hp: 9,
        damage: 2,
        speed: 2.6,
        radius: 0.3,
        xp: 8,
        sounds: { attack: 710, hit: 713, death: 711 },
    },
    {
        id: "sunlight_antelope",
        name: "Sunlight antelope",
        npcId: 13133,
        idleSeq: 6374,
        walkSeq: 6373,
        attackSeq: 6376,
        deathSeq: 6377,
        hp: 15,
        damage: 3,
        speed: 2.2,
        radius: 0.4,
        xp: 10,
        sounds: { attack: 369, hit: 371, death: 370 },
        scale: 0.8,
    },
    {
        id: "moonlight_antelope",
        name: "Moonlight antelope",
        npcId: 13132,
        idleSeq: 6374,
        walkSeq: 6373,
        attackSeq: 6376,
        deathSeq: 6377,
        hp: 17,
        damage: 3,
        speed: 2.2,
        radius: 0.4,
        xp: 11,
        sounds: { attack: 369, hit: 371, death: 370 },
        scale: 0.8,
    },
    {
        id: "blood_jaguar",
        name: "Blood jaguar",
        npcId: 13021,
        idleSeq: 9091,
        walkSeq: 9090,
        attackSeq: 9092,
        deathSeq: -1,
        hp: 20,
        damage: 3,
        speed: 2.4,
        radius: 0.4,
        xp: 12,
        sounds: { attack: 710, hit: 713, death: 711 },
        scale: 0.8,
    },
    {
        id: "serpent_shaman",
        name: "Serpent shaman",
        npcId: 12811,
        ...HUMAN,
        idleSeq: 813,
        walkSeq: 1205,
        attackSeq: 711,
        hp: 30,
        damage: 4,
        speed: 2.0,
        radius: 0.35,
        xp: 18,
        sounds: HUMAN_SOUNDS,
    },
    {
        id: "jaguar_warrior",
        name: "Jaguar warrior",
        npcId: 12810,
        ...HUMAN,
        attackSeq: 390,
        hp: 36,
        damage: 5,
        speed: 2.2,
        radius: 0.4,
        xp: 22,
        sounds: HUMAN_SOUNDS,
        knockbackResist: 0.3,
    },
    {
        id: "sulphur_nagua",
        name: "Sulphur nagua",
        npcId: 12879,
        idleSeq: 10999,
        walkSeq: 10991,
        attackSeq: 10988,
        deathSeq: -1,
        hp: 44,
        damage: 5,
        speed: 1.9,
        radius: 0.4,
        xp: 26,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
        scale: 0.8,
        knockbackResist: 0.4,
    },
    {
        id: "fremennik_archer",
        name: "Fremennik warband archer",
        npcId: 12814,
        ...HUMAN,
        // A bow shot
        attackSeq: 426,
        hp: 40,
        damage: 5,
        speed: 2.0,
        radius: 0.35,
        xp: 24,
        sounds: HUMAN_SOUNDS,
    },
    {
        id: "fremennik_seer",
        name: "Fremennik warband seer",
        npcId: 12815,
        ...HUMAN,
        attackSeq: 711,
        hp: 40,
        damage: 5,
        speed: 2.0,
        radius: 0.35,
        xp: 24,
        sounds: HUMAN_SOUNDS,
    },
    {
        id: "fremennik_berserker",
        name: "Fremennik warband berserker",
        npcId: 12816,
        ...HUMAN,
        attackSeq: 390,
        hp: 52,
        damage: 6,
        speed: 2.1,
        radius: 0.35,
        xp: 30,
        sounds: HUMAN_SOUNDS,
        knockbackResist: 0.3,
    },
    {
        id: "frost_nagua",
        name: "Frost nagua",
        npcId: 13787,
        idleSeq: 10999,
        walkSeq: 10991,
        attackSeq: 10988,
        deathSeq: -1,
        hp: 52,
        damage: 6,
        speed: 1.9,
        radius: 0.4,
        xp: 30,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
        knockbackResist: 0.4,
    },
    {
        id: "gryphon",
        name: "Gryphon",
        npcId: 14857,
        idleSeq: 12547,
        walkSeq: 12549,
        attackSeq: 12551,
        deathSeq: 12556,
        hp: 62,
        damage: 7,
        speed: 2.2,
        radius: 0.45,
        xp: 36,
        // A bat's
        sounds: { attack: 292, hit: 294, death: 293 },
        scale: 0.8,
        knockbackResist: 0.5,
    },
    {
        id: "minotaur",
        name: "Minotaur",
        npcId: 12812,
        idleSeq: 10840,
        walkSeq: 10842,
        attackSeq: 10843,
        deathSeq: 10846,
        hp: 72,
        damage: 7,
        speed: 1.8,
        radius: 0.45,
        xp: 42,
        // A cow's
        sounds: { attack: 369, hit: 371, death: 370 },
        // Three tiles across in OSRS
        scale: 0.6,
        knockbackResist: 0.7,
    },

    // The Fortis Colosseum's champions: the Manticore and the Shockwave and
    // Javelin Colossi. Fire Wave (156) and the crossbow bolt (27) stand in
    // for the Manticore's orbs and spikes, Saradomin Strike's bolt (76) for
    // the shockwaves.
    {
        id: "manticore",
        name: "Manticore",
        npcId: 12818,
        idleSeq: 10863,
        walkSeq: 10864,
        attackSeq: 10869,
        deathSeq: 10866,
        hp: 3600,
        damage: 0,
        speed: 2.2,
        radius: 0.45,
        xp: 900,
        sounds: { attack: 292, hit: 294, death: 293 },
        // Three tiles across in OSRS
        scale: 0.85,
        knockbackResist: 1,
        spawnMessage: "The Manticore is let loose into the arena!",
        // Claws, orbs of magic and spikes from its tail, three at a time.
        // Enraged, five.
        boss: {
            attackInterval: 4,
            keepDistance: 4,
            enrage: { message: "The Manticore roars!" },
            attacks: [
                {
                    name: "Claws",
                    kind: "swing",
                    damage: 4,
                    windup: 0.7,
                    cooldown: 0,
                    range: 3.2,
                    radius: 3,
                    weight: 3,
                    sound: 292,
                },
                {
                    name: "Orbs",
                    kind: "projectile",
                    damage: 3,
                    windup: 0.6,
                    cooldown: 2,
                    range: 14,
                    weight: 4,
                    speed: 9,
                    count: 3,
                    spread: 0.35,
                    homing: 0.4,
                    spotAnim: 156,
                    impactSpotAnim: 157,
                    sound: 162,
                    hitSound: 163,
                },
                {
                    name: "Tail spikes",
                    kind: "projectile",
                    damage: 3,
                    windup: 0.5,
                    cooldown: 3,
                    range: 14,
                    weight: 3,
                    speed: 13,
                    count: 3,
                    spread: 0.25,
                    spotAnim: 27,
                    sound: 2707,
                },
                {
                    name: "Orb storm",
                    kind: "projectile",
                    damage: 3,
                    windup: 0.6,
                    cooldown: 3,
                    range: 14,
                    weight: 4,
                    speed: 9,
                    count: 5,
                    spread: 0.3,
                    homing: 0.3,
                    spotAnim: 156,
                    impactSpotAnim: 157,
                    sound: 162,
                    hitSound: 163,
                    enraged: true,
                },
            ],
        },
    },
    {
        id: "shockwave_colossus",
        name: "Shockwave Colossus",
        npcId: 12819,
        idleSeq: 10902,
        walkSeq: 10881,
        attackSeq: 10903,
        deathSeq: 10895,
        hp: 4800,
        damage: 0,
        speed: 2,
        radius: 0.45,
        xp: 1100,
        sounds: HUMAN_SOUNDS,
        scale: 0.85,
        knockbackResist: 1,
        spawnMessage: "The Shockwave Colossus strides into the arena, crackling!",
        // Lightning that chases you and shockwaves that strike from above.
        // Enraged, a thunderstorm.
        boss: {
            attackInterval: 3.2,
            keepDistance: 6,
            enrage: { message: "The Shockwave Colossus surges with power!" },
            attacks: [
                {
                    name: "Lightning",
                    kind: "projectile",
                    damage: 3,
                    windup: 0.5,
                    cooldown: 0,
                    range: 14,
                    weight: 4,
                    speed: 10,
                    homing: 0.8,
                    spotAnim: 396,
                    sound: 1659,
                },
                {
                    name: "Shockwave",
                    kind: "blast",
                    damage: 3,
                    windup: 1.1,
                    cooldown: 6,
                    range: 14,
                    radius: 1.5,
                    count: 3,
                    spread: 2.5,
                    weight: 3,
                    impactSpotAnim: 76,
                    sound: 1659,
                },
                {
                    name: "Thunderstorm",
                    kind: "blast",
                    damage: 3,
                    windup: 1.2,
                    cooldown: 6,
                    range: 14,
                    radius: 1.5,
                    count: 6,
                    spread: 3.5,
                    weight: 4,
                    impactSpotAnim: 76,
                    sound: 1659,
                    enraged: true,
                },
            ],
        },
    },
    {
        id: "javelin_colossus",
        name: "Javelin Colossus",
        npcId: 12817,
        idleSeq: 10889,
        walkSeq: 10879,
        attackSeq: 10892,
        deathSeq: 10888,
        hp: 6200,
        damage: 0,
        speed: 2,
        radius: 0.45,
        xp: 1300,
        sounds: HUMAN_SOUNDS,
        scale: 0.85,
        knockbackResist: 1,
        spawnMessage: "The Javelin Colossus takes the field!",
        // Javelins from a distance, in volleys, and one thrown high that lands
        // where you stood. Enraged, five at a time.
        boss: {
            attackInterval: 3.4,
            keepDistance: 7,
            enrage: { message: "The Javelin Colossus bellows to the crowd!" },
            attacks: [
                {
                    name: "Javelin",
                    kind: "projectile",
                    damage: 4,
                    windup: 0.4,
                    cooldown: 0,
                    range: 15,
                    weight: 4,
                    speed: 14,
                    spotAnim: 27,
                    sound: 2707,
                },
                {
                    name: "Javelin volley",
                    kind: "projectile",
                    damage: 3,
                    windup: 0.6,
                    cooldown: 4,
                    range: 15,
                    weight: 3,
                    speed: 14,
                    count: 3,
                    spread: 0.3,
                    spotAnim: 27,
                    sound: 2707,
                },
                {
                    name: "Javelin toss",
                    kind: "blast",
                    damage: 4,
                    windup: 1.3,
                    cooldown: 6,
                    range: 15,
                    radius: 1.6,
                    weight: 2,
                    impactSpotAnim: 157,
                    sound: 2707,
                },
                {
                    name: "Javelin storm",
                    kind: "projectile",
                    damage: 3,
                    windup: 0.6,
                    cooldown: 3,
                    range: 15,
                    weight: 4,
                    speed: 14,
                    count: 5,
                    spread: 0.25,
                    spotAnim: 27,
                    sound: 2707,
                    enraged: true,
                },
            ],
        },
    },

    // The final boss: Sol Heredit, the Colosseum's champion. Spear and shield,
    // a leap, and Grapple: three blasts at once. Enraged, he parries and
    // strikes all around you.
    {
        id: "sol_heredit",
        name: "Sol Heredit",
        npcId: 12821,
        idleSeq: 10874,
        walkSeq: 10878,
        attackSeq: 10883,
        deathSeq: 10888,
        hp: 18500,
        damage: 0,
        speed: 2,
        radius: 0.45,
        xp: 4000,
        sounds: HUMAN_SOUNDS,
        // Five tiles across in OSRS
        scale: 0.6,
        knockbackResist: 1,
        spawnMessage: "Sol Heredit enters the arena to the roar of the crowd!",
        boss: {
            attackInterval: 2.9,
            enrage: {
                message: "Sol Heredit is enraged!",
                shout: "Is that all you have, little ant?",
            },
            attacks: [
                {
                    name: "Spear",
                    kind: "swing",
                    damage: 9,
                    windup: 0.8,
                    cooldown: 0,
                    range: 3.6,
                    radius: 3.4,
                    weight: 4,
                    seq: 10883,
                    sound: 2500,
                },
                {
                    name: "Shield bash",
                    kind: "swing",
                    damage: 7,
                    windup: 0.9,
                    cooldown: 3,
                    range: 3.6,
                    radius: 3.8,
                    weight: 3,
                    seq: 10884,
                    sound: 2500,
                },
                { ...leapAttack(7, 2.2, 6), name: "Leap", seq: 10886 },
                {
                    name: "Grapple",
                    kind: "blast",
                    damage: 7,
                    windup: 1.2,
                    cooldown: 5,
                    range: 14,
                    radius: 1.6,
                    count: 3,
                    spread: 2,
                    weight: 3,
                    impactSpotAnim: 157,
                    seq: 10885,
                    sound: 2500,
                },
                {
                    name: "Triple parry",
                    kind: "blast",
                    damage: 7,
                    windup: 1.2,
                    cooldown: 5,
                    range: 14,
                    radius: 1.5,
                    count: 6,
                    spread: 3.5,
                    weight: 4,
                    impactSpotAnim: 157,
                    seq: 10885,
                    sound: 2500,
                    enraged: true,
                },
            ],
        },
    },

    // Morytania: the ghasts, shades and zombies of Mort Myre, Canifis's
    // werewolves, Darkmeyer's vampyres (juveniles, juvinates and the flying
    // vyrewatch), mutated bloodvelds, nail beasts, and the Theatre of Blood's
    // Nylocas. Vampyres sound like Count Draynor, Nylocas like spiders.
    {
        id: "ghast",
        name: "Ghast",
        tags: ["undead"],
        npcId: 946,
        idleSeq: 1086,
        walkSeq: 1085,
        attackSeq: 1087,
        deathSeq: 1095,
        hp: 5,
        damage: 1,
        speed: 2.3,
        radius: 0.35,
        xp: 5,
        sounds: { attack: 436, hit: 439, death: 438 },
        scale: 0.8,
    },
    {
        id: "vampyre_juvenile",
        name: "Vampyre juvenile",
        tags: ["vampyre"],
        npcId: 3692,
        idleSeq: 4702,
        walkSeq: 4699,
        attackSeq: 4705,
        deathSeq: 4708,
        hp: 9,
        damage: 2,
        speed: 2.5,
        radius: 0.3,
        xp: 8,
        sounds: { attack: 879, hit: 884, death: 882 },
    },
    {
        id: "myre_zombie",
        name: "Zombie",
        tags: ["undead"],
        npcId: 2507,
        idleSeq: 5576,
        walkSeq: 5577,
        attackSeq: 5578,
        deathSeq: 5575,
        hp: 15,
        damage: 3,
        speed: 1.9,
        radius: 0.35,
        xp: 10,
        sounds: { attack: 918, hit: 923, death: 922 },
    },
    {
        id: "fiyr_shade",
        name: "Fiyr shade",
        tags: ["undead"],
        npcId: 1286,
        idleSeq: 1282,
        walkSeq: 1281,
        attackSeq: 1284,
        deathSeq: 1287,
        hp: 17,
        damage: 3,
        speed: 2.2,
        radius: 0.35,
        xp: 11,
        sounds: { attack: 436, hit: 439, death: 438 },
    },
    {
        id: "werewolf",
        name: "Werewolf",
        npcId: 2593,
        idleSeq: 6539,
        walkSeq: 6541,
        attackSeq: 6536,
        deathSeq: 6537,
        hp: 20,
        damage: 3,
        speed: 2.4,
        radius: 0.4,
        xp: 12,
        sounds: { attack: 710, hit: 713, death: 711 },
    },
    {
        id: "vampyre_juvinate",
        name: "Vampyre juvinate",
        tags: ["vampyre"],
        npcId: 3694,
        idleSeq: 4702,
        walkSeq: 4699,
        attackSeq: 4711,
        deathSeq: 4708,
        hp: 30,
        damage: 3,
        speed: 2.0,
        radius: 0.35,
        xp: 18,
        sounds: { attack: 879, hit: 884, death: 882 },
    },
    {
        id: "nail_beast",
        name: "Nail beast",
        npcId: 2946,
        idleSeq: 5986,
        walkSeq: 5987,
        attackSeq: 5988,
        deathSeq: 5990,
        hp: 36,
        damage: 3,
        speed: 1.9,
        radius: 0.4,
        xp: 22,
        sounds: { attack: 369, hit: 371, death: 370 },
        scale: 0.8,
        knockbackResist: 0.3,
    },
    {
        id: "mutated_bloodveld",
        name: "Mutated bloodveld",
        npcId: 7276,
        idleSeq: 1551,
        walkSeq: 1549,
        attackSeq: 1552,
        deathSeq: 1553,
        hp: 44,
        damage: 4,
        speed: 1.9,
        radius: 0.45,
        xp: 26,
        sounds: { attack: 918, hit: 923, death: 922 },
        scale: 0.8,
        knockbackResist: 0.4,
    },
    // The Nylocas, in their three colours (grey melee, green ranged, blue
    // magic). The big ones split into two small ones when they die, like they
    // do in the Theatre.
    {
        id: "nylocas_ischyros",
        name: "Nylocas Ischyros",
        npcId: 8342,
        idleSeq: 8002,
        walkSeq: 8003,
        attackSeq: 8004,
        deathSeq: 8005,
        hp: 18,
        damage: 4,
        speed: 2.5,
        radius: 0.35,
        xp: 14,
        onlyStyle: "melee",
        sounds: { attack: 3605, hit: 3607, death: 3606 },
    },
    {
        id: "nylocas_toxobolos",
        name: "Nylocas Toxobolos",
        npcId: 8343,
        idleSeq: 7993,
        walkSeq: 7997,
        attackSeq: 7999,
        deathSeq: 7998,
        hp: 18,
        damage: 3,
        speed: 2.3,
        radius: 0.35,
        xp: 14,
        onlyStyle: "ranged",
        sounds: { attack: 3605, hit: 3607, death: 3606 },
    },
    {
        id: "nylocas_hagios",
        name: "Nylocas Hagios",
        npcId: 8344,
        idleSeq: 7988,
        walkSeq: 7987,
        attackSeq: 7989,
        deathSeq: 7991,
        hp: 18,
        damage: 3,
        speed: 2.3,
        radius: 0.35,
        xp: 14,
        onlyStyle: "magic",
        sounds: { attack: 3605, hit: 3607, death: 3606 },
    },
    {
        id: "big_nylocas_ischyros",
        name: "Nylocas Ischyros",
        npcId: 8345,
        idleSeq: 8002,
        walkSeq: 8003,
        attackSeq: 8004,
        deathSeq: 8005,
        hp: 30,
        damage: 5,
        speed: 2.2,
        radius: 0.45,
        xp: 24,
        onlyStyle: "melee",
        sounds: { attack: 3605, hit: 3607, death: 3606 },
        scale: 0.8,
        knockbackResist: 0.4,
        splits: { into: "nylocas_ischyros", count: 2 },
    },
    {
        id: "big_nylocas_toxobolos",
        name: "Nylocas Toxobolos",
        npcId: 8346,
        idleSeq: 7993,
        walkSeq: 7997,
        attackSeq: 7999,
        deathSeq: 7998,
        hp: 28,
        damage: 4,
        speed: 2.0,
        radius: 0.45,
        xp: 24,
        onlyStyle: "ranged",
        sounds: { attack: 3605, hit: 3607, death: 3606 },
        scale: 0.8,
        knockbackResist: 0.4,
        splits: { into: "nylocas_toxobolos", count: 2 },
    },
    {
        id: "big_nylocas_hagios",
        name: "Nylocas Hagios",
        npcId: 8347,
        idleSeq: 7988,
        walkSeq: 7987,
        attackSeq: 7989,
        deathSeq: 7991,
        hp: 28,
        damage: 4,
        speed: 2.0,
        radius: 0.45,
        xp: 24,
        onlyStyle: "magic",
        sounds: { attack: 3605, hit: 3607, death: 3606 },
        scale: 0.8,
        knockbackResist: 0.4,
        splits: { into: "nylocas_hagios", count: 2 },
    },
    // Darkmeyer's guards: the vyrewatch, on their wings, and the sentinels
    {
        id: "vyrewatch",
        name: "Vyrewatch",
        tags: ["vampyre"],
        npcId: 3717,
        idleSeq: 4689,
        walkSeq: 4683,
        attackSeq: 4685,
        deathSeq: 4697,
        hp: 50,
        damage: 5,
        speed: 2.1,
        radius: 0.4,
        xp: 32,
        sounds: { attack: 879, hit: 884, death: 882 },
        // Three tiles across with its wings out
        scale: 0.7,
        knockbackResist: 0.4,
    },
    {
        id: "vyrewatch_sentinel",
        name: "Vyrewatch Sentinel",
        tags: ["vampyre"],
        npcId: 9756,
        idleSeq: 8703,
        walkSeq: 8702,
        attackSeq: 8707,
        deathSeq: 8713,
        hp: 68,
        damage: 6,
        speed: 2.0,
        radius: 0.4,
        xp: 40,
        sounds: { attack: 879, hit: 884, death: 882 },
        knockbackResist: 0.6,
    },

    // Morytania's bosses: Vanstrom Klause, and the Theatre of Blood's
    // Pestilent Bloat and Sotetseg. The Blood Barrage (376, 377) stands in
    // for Vanstrom's magic; the Theatre's own spot animations are used for
    // the rest (the Bloat's flies 1568 and falling flesh 1570, Sotetseg's
    // orbs 1606 and death ball 1604).
    {
        id: "vanstrom_klause",
        name: "Vanstrom Klause",
        tags: ["vampyre"],
        npcId: 3735,
        idleSeq: 4702,
        walkSeq: 4699,
        attackSeq: 4705,
        deathSeq: 4708,
        hp: 3600,
        damage: 0,
        speed: 2.0,
        radius: 0.4,
        xp: 900,
        sounds: { attack: 879, hit: 884, death: 882 },
        scale: 1.6,
        knockbackResist: 1,
        spawnMessage: "Vanstrom Klause steps out of the shadows!",
        // Claws and blood magic that heal him, and he vanishes and comes back
        // somewhere else. Enraged, juveniles answer his call.
        boss: {
            attackInterval: 2.6,
            lifesteal: 0.25,
            enrage: {
                message: "Vanstrom Klause hisses with fury!",
                shout: "You will not leave Morytania alive!",
            },
            attacks: [
                {
                    name: "Claws",
                    kind: "swing",
                    damage: 4,
                    windup: 0.6,
                    cooldown: 0,
                    range: 2.8,
                    radius: 2.4,
                    weight: 4,
                    seq: 4701,
                    sound: 879,
                },
                {
                    name: "Blood barrage",
                    kind: "blast",
                    damage: 3,
                    windup: 1,
                    cooldown: 4,
                    range: 12,
                    radius: 1.6,
                    weight: 3,
                    impactSpotAnim: 377,
                    sound: 106,
                },
                {
                    name: "Vanish",
                    kind: "teleport",
                    damage: 0,
                    windup: 0.6,
                    cooldown: 12,
                    range: 14,
                    minRange: 6,
                    radius: 4,
                    weight: 2,
                    castSpotAnim: 376,
                    impactSpotAnim: 376,
                    sound: 200,
                },
                {
                    name: "Call the juveniles",
                    kind: "summon",
                    damage: 0,
                    windup: 1,
                    cooldown: 16,
                    range: 16,
                    weight: 2,
                    summon: { enemyId: "vampyre_juvenile", count: 4 },
                    sound: 879,
                    enraged: true,
                },
            ],
        },
    },
    {
        id: "pestilent_bloat",
        name: "Pestilent Bloat",
        tags: ["undead"],
        npcId: 8359,
        idleSeq: 8080,
        walkSeq: 8081,
        attackSeq: 8085,
        deathSeq: -1,
        hp: 5200,
        damage: 0,
        speed: 1.5,
        radius: 0.45,
        xp: 1100,
        sounds: { attack: 918, hit: 923, death: 922 },
        // Five tiles across in OSRS
        scale: 0.6,
        knockbackResist: 1,
        spawnMessage: "The Pestilent Bloat lumbers out of the Theatre!",
        // Stomps, and flesh that falls from the ceiling all around; its flies
        // follow you. Enraged, the flesh rains down.
        boss: {
            attackInterval: 3.4,
            enrage: { message: "The Pestilent Bloat's flies swarm!" },
            attacks: [
                {
                    name: "Stomp",
                    kind: "swing",
                    damage: 4,
                    windup: 0.9,
                    cooldown: 0,
                    range: 3.4,
                    radius: 3.2,
                    weight: 4,
                    sound: 918,
                },
                {
                    name: "Falling flesh",
                    kind: "blast",
                    damage: 3,
                    windup: 1.2,
                    cooldown: 4,
                    range: 16,
                    radius: 1.4,
                    count: 4,
                    spread: 3,
                    weight: 4,
                    impactSpotAnim: 1570,
                    sound: 922,
                },
                {
                    name: "Flies",
                    kind: "projectile",
                    damage: 2,
                    windup: 0.6,
                    cooldown: 3,
                    range: 14,
                    weight: 3,
                    speed: 5,
                    count: 3,
                    spread: 0.6,
                    homing: 1,
                    spotAnim: 1568,
                    sound: 292,
                },
                {
                    name: "Rain of flesh",
                    kind: "blast",
                    damage: 3,
                    windup: 1.2,
                    cooldown: 5,
                    range: 16,
                    radius: 1.4,
                    count: 9,
                    spread: 4.5,
                    weight: 4,
                    impactSpotAnim: 1571,
                    sound: 922,
                    enraged: true,
                },
            ],
        },
    },
    {
        id: "sotetseg",
        name: "Sotetseg",
        npcId: 8388,
        idleSeq: 8137,
        walkSeq: 8136,
        attackSeq: 8138,
        deathSeq: 8140,
        hp: 6700,
        damage: 0,
        speed: 1.9,
        radius: 0.45,
        xp: 1300,
        sounds: { attack: 3605, hit: 3607, death: 3606 },
        scale: 0.6,
        knockbackResist: 1,
        spawnMessage: "Sotetseg rises from the Theatre's maze!",
        // Red orbs from a distance, a stomp up close, and the ball of death:
        // slow, but it hits hard if it catches you. Enraged, two at once.
        boss: {
            attackInterval: 3,
            keepDistance: 5,
            enrage: { message: "Sotetseg's eyes burn red!" },
            attacks: [
                {
                    name: "Stomp",
                    kind: "swing",
                    damage: 5,
                    windup: 0.8,
                    cooldown: 0,
                    range: 3.4,
                    radius: 3.2,
                    weight: 3,
                    sound: 3605,
                },
                {
                    name: "Orbs",
                    kind: "projectile",
                    damage: 3,
                    windup: 0.6,
                    cooldown: 1,
                    range: 15,
                    weight: 4,
                    speed: 9,
                    count: 3,
                    spread: 0.3,
                    spotAnim: 1606,
                    seq: 8139,
                    sound: 162,
                },
                {
                    name: "Ball of death",
                    kind: "projectile",
                    damage: 10,
                    windup: 1.2,
                    cooldown: 7,
                    range: 16,
                    weight: 3,
                    speed: 4.5,
                    homing: 0.5,
                    spotAnim: 1604,
                    impactSpotAnim: 377,
                    seq: 8139,
                    sound: 106,
                    message: "Sotetseg launches a ball of death! Keep moving!",
                },
                {
                    name: "Twin balls of death",
                    kind: "projectile",
                    damage: 9,
                    windup: 1.2,
                    cooldown: 7,
                    range: 16,
                    weight: 4,
                    speed: 4.5,
                    count: 2,
                    spread: 0.5,
                    homing: 0.5,
                    spotAnim: 1604,
                    impactSpotAnim: 377,
                    seq: 8139,
                    sound: 106,
                    message: "Two balls of death! Keep moving!",
                    enraged: true,
                },
            ],
        },
    },

    // The Theatre of Blood raid's adds: the blood spawn and Nylocas Matomenos that crawl out to
    // the Maiden. Its bosses are in theatre.ts.
    {
        id: "blood_spawn",
        name: "Blood spawn",
        tags: ["vampyre"],
        npcId: 8367,
        idleSeq: 8102,
        walkSeq: 8101,
        attackSeq: 8104,
        deathSeq: 8103,
        hp: 6,
        damage: 1,
        speed: 2.3,
        radius: 0.35,
        xp: 6,
        sounds: { attack: 3605, hit: 3607, death: 3606 },
    },
    {
        id: "nylocas_matomenos",
        name: "Nylocas Matomenos",
        npcId: 8366,
        idleSeq: 8096,
        walkSeq: 8095,
        attackSeq: 8098,
        deathSeq: 8097,
        hp: 14,
        damage: 2,
        speed: 1.8,
        radius: 0.5,
        xp: 10,
        sounds: { attack: 3605, hit: 3607, death: 3606 },
        scale: 0.8,
        knockbackResist: 0.5,
        expertAs: "nylocas_matomenos_healer",
    },
    {
        id: "nylocas_matomenos_healer",
        name: "Nylocas Matomenos",
        npcId: 8366,
        idleSeq: 8096,
        walkSeq: 8095,
        attackSeq: 8098,
        deathSeq: 8097,
        hp: 14,
        damage: 2,
        speed: 1.8,
        radius: 0.5,
        xp: 10,
        sounds: { attack: 3605, hit: 3607, death: 3606 },
        scale: 0.8,
        knockbackResist: 0.5,
        // Crawls to the Maiden and feeds her, as in OSRS: kill it on the way
        heals: {
            fraction: 0.02,
            interval: 1.5,
            range: 1.5,
            targets: ["maiden_of_sugadinti"],
            seek: 30,
            spotAnim: 1579,
        },
    },

    // The final boss: Verzik Vitur, in her three forms. She starts on her
    // throne behind a shield, gets up as the spider-legged second form that
    // webs you and calls in Nylocas, and then as the great spider. Her own
    // spot animations: the power blast (1580), her shield (1581), lightning
    // (1585), webs (1601) and the green ball (1598).
    {
        id: "verzik_vitur",
        name: "Verzik Vitur",
        tags: ["vampyre"],
        npcId: 8370,
        idleSeq: 8107,
        walkSeq: 8107,
        attackSeq: 8109,
        deathSeq: -1,
        hp: 7000,
        damage: 0,
        // On her throne: she doesn't move until she gets up
        speed: 1,
        radius: 0.45,
        xp: 5000,
        sounds: { attack: 879, hit: 884, death: 882 },
        scale: 0.55,
        knockbackResist: 1,
        stationary: true,
        spawnMessage: "Verzik Vitur rises from her throne!",
        shield: {
            seconds: 5,
            spotAnim: 1581,
            message: "Verzik's shield is down. Attack!",
        },
        boss: {
            attackInterval: 2.6,
            rises: {
                times: 1,
                hp: 1,
                message: "Verzik Vitur rises from her throne in her true form!",
                into: "verzik_vitur_risen",
            },
            enrage: {
                message: "Verzik's power blasts quicken!",
                shout: "Oh I'm going to enjoy this...",
            },
            attacks: [
                {
                    name: "Power blast",
                    kind: "projectile",
                    damage: 5,
                    windup: 0.8,
                    cooldown: 0,
                    range: 30,
                    weight: 4,
                    speed: 8,
                    homing: 0.6,
                    spotAnim: 1580,
                    sound: 1659,
                },
                {
                    name: "Collapsing pillars",
                    kind: "blast",
                    damage: 6,
                    windup: 1.3,
                    cooldown: 5,
                    range: 30,
                    radius: 1.8,
                    count: 4,
                    spread: 3,
                    weight: 3,
                    impactSpotAnim: 451,
                    sound: 1659,
                },
                {
                    name: "Power volley",
                    kind: "projectile",
                    damage: 5,
                    windup: 0.8,
                    cooldown: 3,
                    range: 30,
                    weight: 4,
                    speed: 8,
                    count: 3,
                    spread: 0.35,
                    homing: 0.4,
                    spotAnim: 1580,
                    sound: 1659,
                    enraged: true,
                },
            ],
        },
    },
    // Her second form: only comes from the first getting back up
    {
        id: "verzik_vitur_risen",
        name: "Verzik Vitur",
        tags: ["vampyre"],
        npcId: 8372,
        idleSeq: 8113,
        walkSeq: 8113,
        attackSeq: 8114,
        deathSeq: -1,
        hp: 7000,
        damage: 0,
        speed: 2,
        radius: 0.45,
        xp: 5000,
        sounds: { attack: 879, hit: 884, death: 882 },
        // Three tiles across in OSRS
        scale: 0.85,
        knockbackResist: 1,
        boss: {
            attackInterval: 2.6,
            keepDistance: 4,
            rises: {
                times: 2,
                hp: 1,
                message: "Verzik Vitur transforms into a monstrous spider!",
                into: "verzik_vitur_spider",
            },
            enrage: {
                message: "Verzik Vitur is enraged!",
                shout: "Behold my true nature!",
            },
            // The Nylocas come to her as she weakens
            summonsAt: [
                {
                    hp: 0.75,
                    enemyId: "nylocas_ischyros",
                    count: 4,
                    message: "Nylocas crawl in to defend Verzik!",
                },
                {
                    hp: 0.5,
                    enemyId: "nylocas_toxobolos",
                    count: 4,
                    message: "More Nylocas crawl in!",
                },
                {
                    hp: 0.25,
                    enemyId: "nylocas_hagios",
                    count: 4,
                    message: "More Nylocas crawl in!",
                },
            ],
            attacks: [
                {
                    name: "Bounce",
                    kind: "swing",
                    damage: 8,
                    windup: 0.8,
                    cooldown: 0,
                    range: 3.4,
                    radius: 3.2,
                    weight: 4,
                    sound: 2500,
                },
                {
                    name: "Lightning",
                    kind: "projectile",
                    damage: 5,
                    windup: 0.6,
                    cooldown: 2,
                    range: 16,
                    weight: 4,
                    speed: 10,
                    homing: 0.8,
                    spotAnim: 1585,
                    seq: 8116,
                    sound: 1659,
                },
                {
                    name: "Webs",
                    kind: "blast",
                    damage: 4,
                    windup: 1.1,
                    cooldown: 6,
                    range: 16,
                    radius: 1.5,
                    count: 5,
                    spread: 3.5,
                    weight: 3,
                    impactSpotAnim: 1601,
                    seq: 8118,
                    sound: 3605,
                    effect: { kind: "freeze", seconds: 1 },
                    message: "Verzik spits webs! Don't get stuck!",
                },
                {
                    name: "Call the Nylocas",
                    kind: "summon",
                    damage: 0,
                    windup: 1,
                    cooldown: 14,
                    range: 16,
                    weight: 2,
                    summon: { enemyId: "big_nylocas_ischyros", count: 3 },
                    seq: 8118,
                    sound: 3605,
                    enraged: true,
                },
            ],
        },
    },
    // Her last form: only comes from the second getting back up
    {
        id: "verzik_vitur_spider",
        name: "Verzik Vitur",
        tags: ["vampyre"],
        npcId: 8374,
        idleSeq: 8120,
        walkSeq: 8121,
        attackSeq: 8123,
        deathSeq: 8128,
        hp: 7000,
        damage: 0,
        speed: 2.2,
        radius: 0.45,
        xp: 5000,
        sounds: { attack: 3605, hit: 3607, death: 3606 },
        // Seven tiles across in OSRS
        scale: 0.5,
        knockbackResist: 1,
        boss: {
            attackInterval: 2.4,
            lifesteal: 0.5,
            enrage: {
                message: "Verzik Vitur is enraged!",
                shout: "You're tougher than I thought...",
            },
            attacks: [
                {
                    name: "Claws",
                    kind: "swing",
                    damage: 9,
                    windup: 0.7,
                    cooldown: 0,
                    range: 3.6,
                    radius: 3.4,
                    weight: 4,
                    sound: 3605,
                },
                {
                    name: "Green ball",
                    kind: "projectile",
                    damage: 7,
                    windup: 0.8,
                    cooldown: 4,
                    range: 16,
                    weight: 3,
                    speed: 6,
                    homing: 0.7,
                    spotAnim: 1598,
                    seq: 8125,
                    sound: 162,
                    message: "Verzik hurls a green ball! Outrun it!",
                },
                {
                    name: "Webs",
                    kind: "blast",
                    damage: 5,
                    windup: 1.1,
                    cooldown: 6,
                    range: 16,
                    radius: 1.5,
                    count: 7,
                    spread: 4,
                    weight: 3,
                    impactSpotAnim: 1601,
                    seq: 8125,
                    sound: 3605,
                    effect: { kind: "freeze", seconds: 1 },
                },
                {
                    name: "Nylocas hatch",
                    kind: "summon",
                    damage: 0,
                    windup: 1,
                    cooldown: 12,
                    range: 16,
                    weight: 2,
                    summon: { enemyId: "nylocas_hagios", count: 4 },
                    sound: 3605,
                },
                {
                    name: "Web storm",
                    kind: "blast",
                    damage: 5,
                    windup: 1.1,
                    cooldown: 5,
                    range: 16,
                    radius: 1.5,
                    count: 11,
                    spread: 5,
                    weight: 4,
                    impactSpotAnim: 1601,
                    seq: 8125,
                    sound: 3605,
                    effect: { kind: "freeze", seconds: 1 },
                    enraged: true,
                },
            ],
        },
    },

    // Prifddinas: the Gauntlet's crystalline creatures (rats, bats, spiders,
    // scorpions, wolves, bears and dark beasts) loose in the city, its elf
    // guards and the Iorwerth's warriors and archers, Zulrah's snakelings,
    // and late on the corrupted creatures of the Corrupted Gauntlet.
    {
        id: "crystalline_rat",
        name: "Crystalline rat",
        npcId: 9026,
        idleSeq: 4932,
        walkSeq: 4931,
        attackSeq: 4933,
        deathSeq: 4935,
        hp: 4,
        damage: 1,
        speed: 2.4,
        radius: 0.3,
        xp: 5,
        sounds: { attack: 710, hit: 713, death: 711 },
        scale: 0.7,
    },
    {
        id: "crystalline_bat",
        name: "Crystalline bat",
        npcId: 9028,
        idleSeq: 4914,
        walkSeq: 4913,
        attackSeq: 4915,
        deathSeq: -1,
        hp: 9,
        damage: 2,
        speed: 2.4,
        radius: 0.35,
        xp: 8,
        sounds: { attack: 292, hit: 294, death: 293 },
        scale: 0.7,
        flies: true,
    },
    {
        id: "crystalline_spider",
        name: "Crystalline spider",
        npcId: 9027,
        idleSeq: 8340,
        walkSeq: 8341,
        attackSeq: 8004,
        deathSeq: 8005,
        hp: 15,
        damage: 3,
        speed: 2.2,
        radius: 0.35,
        xp: 10,
        sounds: { attack: 3605, hit: 3607, death: 3606 },
    },
    {
        id: "crystalline_scorpion",
        name: "Crystalline scorpion",
        npcId: 9030,
        idleSeq: 6252,
        walkSeq: 6253,
        attackSeq: 6254,
        deathSeq: 6256,
        hp: 17,
        damage: 3,
        speed: 2.0,
        radius: 0.4,
        xp: 11,
        sounds: { attack: 3605, hit: 3607, death: 3606 },
        scale: 0.8,
    },
    {
        id: "crystalline_wolf",
        name: "Crystalline wolf",
        npcId: 9031,
        idleSeq: 6580,
        walkSeq: 6556,
        attackSeq: 6579,
        deathSeq: 6558,
        hp: 20,
        damage: 3,
        speed: 2.4,
        radius: 0.4,
        xp: 12,
        sounds: { attack: 710, hit: 713, death: 711 },
        scale: 0.8,
    },
    {
        id: "elf_guard",
        name: "Guard",
        npcId: 9182,
        ...HUMAN,
        idleSeq: 813,
        walkSeq: 1205,
        // A halberd's jab
        attackSeq: 440,
        hp: 30,
        damage: 4,
        speed: 2.1,
        radius: 0.35,
        xp: 18,
        sounds: HUMAN_SOUNDS,
        knockbackResist: 0.2,
    },
    {
        id: "iorwerth_warrior",
        name: "Iorwerth Warrior",
        npcId: 3429,
        ...HUMAN,
        idleSeq: 813,
        walkSeq: 1205,
        attackSeq: 390,
        hp: 36,
        damage: 5,
        speed: 2.2,
        radius: 0.35,
        xp: 22,
        sounds: HUMAN_SOUNDS,
        knockbackResist: 0.3,
    },
    {
        id: "iorwerth_archer",
        name: "Iorwerth Archer",
        npcId: 3428,
        ...HUMAN,
        // A bow shot
        attackSeq: 426,
        hp: 40,
        damage: 5,
        speed: 2.0,
        radius: 0.35,
        xp: 24,
        sounds: HUMAN_SOUNDS,
    },
    {
        id: "crystalline_bear",
        name: "Crystalline bear",
        npcId: 9032,
        idleSeq: 4919,
        walkSeq: 4923,
        attackSeq: 4925,
        deathSeq: -1,
        hp: 46,
        damage: 6,
        speed: 1.9,
        radius: 0.45,
        xp: 26,
        sounds: { attack: 369, hit: 371, death: 370 },
        // Three tiles across in OSRS
        scale: 0.6,
        knockbackResist: 0.5,
    },
    {
        id: "snakeling",
        name: "Snakeling",
        npcId: 2045,
        idleSeq: 1721,
        walkSeq: 2405,
        attackSeq: 2406,
        deathSeq: 2408,
        hp: 30,
        damage: 4,
        speed: 2.4,
        radius: 0.3,
        xp: 18,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
    },
    {
        id: "crystalline_dark_beast",
        name: "Crystalline dark beast",
        npcId: 9034,
        idleSeq: 2730,
        walkSeq: 2729,
        attackSeq: 2731,
        deathSeq: 2733,
        hp: 54,
        damage: 6,
        speed: 2.0,
        radius: 0.45,
        xp: 32,
        sounds: { attack: 534, hit: 536, death: 535 },
        scale: 0.6,
        knockbackResist: 0.6,
    },
    // The Corrupted Gauntlet's: tougher, in red
    {
        id: "corrupted_wolf",
        name: "Corrupted wolf",
        npcId: 9045,
        idleSeq: 6580,
        walkSeq: 6556,
        attackSeq: 6579,
        deathSeq: 6558,
        hp: 50,
        damage: 6,
        speed: 2.3,
        radius: 0.4,
        xp: 30,
        sounds: { attack: 710, hit: 713, death: 711 },
        scale: 0.8,
        knockbackResist: 0.3,
    },
    {
        id: "corrupted_scorpion",
        name: "Corrupted scorpion",
        npcId: 9044,
        idleSeq: 6252,
        walkSeq: 6253,
        attackSeq: 6254,
        deathSeq: 6256,
        hp: 56,
        damage: 6,
        speed: 2.3,
        radius: 0.4,
        xp: 34,
        sounds: { attack: 3605, hit: 3607, death: 3606 },
        scale: 0.8,
        knockbackResist: 0.4,
    },
    {
        id: "corrupted_bear",
        name: "Corrupted bear",
        npcId: 9046,
        idleSeq: 4919,
        walkSeq: 4923,
        attackSeq: 4925,
        deathSeq: -1,
        hp: 66,
        damage: 7,
        speed: 2.0,
        radius: 0.45,
        xp: 38,
        sounds: { attack: 369, hit: 371, death: 370 },
        scale: 0.6,
        knockbackResist: 0.6,
    },
    {
        id: "corrupted_dark_beast",
        name: "Corrupted dark beast",
        npcId: 9048,
        idleSeq: 2730,
        walkSeq: 2729,
        attackSeq: 2731,
        deathSeq: 2733,
        hp: 76,
        damage: 7,
        speed: 2.0,
        radius: 0.45,
        xp: 44,
        sounds: { attack: 534, hit: 536, death: 535 },
        scale: 0.6,
        knockbackResist: 0.7,
    },

    // Prifddinas's bosses: Zulrah, out of its swamp, and Zalcano and the
    // Crystalline Hunllef from under the city. Zulrah's own spot animations
    // (its spikes 1044 and magic 1046, snakelings 1047) and Smoke Burst's
    // cloud (389) for its venom; TzTok-Jad's rocks (451) for Zalcano's; the
    // Hunllef's own crystals (1707, 1711) and spikes (1719).
    {
        id: "zulrah",
        name: "Zulrah",
        npcId: 2042,
        idleSeq: 5070,
        walkSeq: 5070,
        attackSeq: 5069,
        deathSeq: -1,
        hp: 1400,
        damage: 0,
        speed: 1,
        radius: 0.45,
        xp: 900,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
        // Five tiles across in OSRS
        scale: 0.6,
        knockbackResist: 1,
        // It stays where it surfaces, and its snakelings come to it
        stationary: true,
        spawns: {
            enemies: [{ id: "snakeling", weight: 1 }],
            interval: 12,
            count: 2,
            max: 6,
            radius: 3,
            spotAnim: 1047,
        },
        spawnMessage: "Zulrah surfaces!",
        // The green form: spikes from a distance and clouds of venom. Beaten,
        // it dives and comes back up red, then blue.
        boss: {
            attackInterval: 3.4,
            rises: {
                times: 1,
                hp: 1,
                message: "Zulrah dives... and surfaces again, red with rage!",
                into: "zulrah_magma",
            },
            enrage: { message: "Zulrah hisses!" },
            attacks: [
                {
                    name: "Spikes",
                    kind: "projectile",
                    damage: 3,
                    windup: 0.6,
                    cooldown: 0,
                    range: 11,
                    weight: 4,
                    speed: 10,
                    spotAnim: 1044,
                    sound: 2707,
                },
                {
                    name: "Venom clouds",
                    kind: "blast",
                    damage: 3,
                    windup: 1.2,
                    cooldown: 5,
                    range: 11,
                    radius: 1.6,
                    count: 3,
                    spread: 3,
                    weight: 3,
                    impactSpotAnim: 389,
                    sound: 4787,
                    effect: { kind: "poison", damage: 2, seconds: 5 },
                },
                {
                    name: "Spike volley",
                    kind: "projectile",
                    damage: 3,
                    windup: 0.6,
                    cooldown: 3,
                    range: 11,
                    weight: 4,
                    speed: 10,
                    count: 3,
                    spread: 0.3,
                    spotAnim: 1044,
                    sound: 2707,
                    enraged: true,
                },
            ],
        },
    },
    // Its red form: only comes from the green one diving
    {
        id: "zulrah_magma",
        name: "Zulrah",
        npcId: 2043,
        idleSeq: 5070,
        walkSeq: 5070,
        attackSeq: 5806,
        deathSeq: -1,
        hp: 1400,
        damage: 0,
        speed: 1,
        radius: 0.45,
        xp: 900,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
        scale: 0.6,
        knockbackResist: 1,
        stationary: true,
        spawns: {
            enemies: [{ id: "snakeling", weight: 1 }],
            interval: 12,
            count: 2,
            max: 6,
            radius: 3,
            spotAnim: 1047,
        },
        // Slams its tail down on whoever comes close, and spits venom
        boss: {
            attackInterval: 3.4,
            rises: {
                times: 2,
                hp: 1,
                message: "Zulrah dives... and surfaces again, blue and crackling!",
                into: "zulrah_tanzanite",
            },
            enrage: { message: "Zulrah hisses!" },
            attacks: [
                {
                    name: "Tail slam",
                    kind: "swing",
                    damage: 7,
                    windup: 1.2,
                    cooldown: 0,
                    range: 5,
                    radius: 4.5,
                    weight: 4,
                    sound: 4787,
                    message: "Zulrah raises its tail! Get away!",
                },
                {
                    name: "Venom clouds",
                    kind: "blast",
                    damage: 3,
                    windup: 1.2,
                    cooldown: 4,
                    range: 11,
                    radius: 1.6,
                    count: 4,
                    spread: 3.5,
                    weight: 3,
                    impactSpotAnim: 389,
                    seq: 5069,
                    sound: 4787,
                    effect: { kind: "poison", damage: 2, seconds: 5 },
                },
            ],
        },
    },
    // Its blue form: only comes from the red one diving
    {
        id: "zulrah_tanzanite",
        name: "Zulrah",
        npcId: 2044,
        idleSeq: 5070,
        walkSeq: 5070,
        attackSeq: 5069,
        deathSeq: 5804,
        hp: 1400,
        damage: 0,
        speed: 1,
        radius: 0.45,
        xp: 900,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
        scale: 0.6,
        knockbackResist: 1,
        stationary: true,
        spawns: {
            enemies: [{ id: "snakeling", weight: 1 }],
            interval: 12,
            count: 2,
            max: 6,
            radius: 3,
            spotAnim: 1047,
        },
        // Magic that follows you, spikes, and venom
        boss: {
            attackInterval: 3.2,
            enrage: { message: "Zulrah hisses!" },
            attacks: [
                {
                    name: "Magic",
                    kind: "projectile",
                    damage: 3,
                    windup: 0.6,
                    cooldown: 0,
                    range: 11,
                    weight: 4,
                    speed: 8,
                    homing: 0.6,
                    spotAnim: 1046,
                    sound: 162,
                },
                {
                    name: "Spikes",
                    kind: "projectile",
                    damage: 3,
                    windup: 0.6,
                    cooldown: 2,
                    range: 11,
                    weight: 3,
                    speed: 10,
                    count: 3,
                    spread: 0.3,
                    spotAnim: 1044,
                    sound: 2707,
                },
                {
                    name: "Venom clouds",
                    kind: "blast",
                    damage: 3,
                    windup: 1.2,
                    cooldown: 5,
                    range: 11,
                    radius: 1.6,
                    count: 5,
                    spread: 4,
                    weight: 3,
                    impactSpotAnim: 389,
                    sound: 4787,
                    effect: { kind: "poison", damage: 2, seconds: 5 },
                },
            ],
        },
    },
    {
        id: "zalcano",
        name: "Zalcano",
        npcId: 9049,
        idleSeq: 8429,
        walkSeq: 8430,
        attackSeq: 8431,
        deathSeq: 8437,
        hp: 5600,
        damage: 0,
        speed: 1.6,
        radius: 0.45,
        xp: 1100,
        sounds: { attack: 534, hit: 536, death: 535 },
        scale: 0.6,
        knockbackResist: 1,
        spawnMessage: "Zalcano tears herself out of the rock!",
        // Rocks that fall from the cave's roof where you stand, a stomp, and
        // a glowing patch that burns. Enraged, the roof comes down.
        boss: {
            attackInterval: 3.2,
            keepDistance: 4,
            enrage: { message: "Zalcano's core glows white-hot!" },
            attacks: [
                {
                    name: "Stomp",
                    kind: "swing",
                    damage: 5,
                    windup: 0.9,
                    cooldown: 0,
                    range: 3.4,
                    radius: 3.2,
                    weight: 3,
                    seq: 8433,
                    sound: 2500,
                },
                {
                    name: "Falling rocks",
                    kind: "blast",
                    damage: 4,
                    windup: 1.3,
                    cooldown: 4,
                    range: 16,
                    radius: 1.6,
                    count: 3,
                    spread: 3,
                    weight: 4,
                    impactSpotAnim: 451,
                    sound: 1659,
                },
                {
                    name: "Rock",
                    kind: "projectile",
                    damage: 3,
                    windup: 0.7,
                    cooldown: 2,
                    range: 16,
                    weight: 3,
                    speed: 9,
                    spotAnim: 1727,
                    sound: 2707,
                },
                {
                    name: "Rockfall",
                    kind: "blast",
                    damage: 4,
                    windup: 1.3,
                    cooldown: 5,
                    range: 16,
                    radius: 1.6,
                    count: 7,
                    spread: 4.5,
                    weight: 4,
                    impactSpotAnim: 451,
                    sound: 1659,
                    message: "The roof is coming down!",
                    enraged: true,
                },
            ],
        },
    },
    {
        id: "crystalline_hunllef",
        name: "Crystalline Hunllef",
        npcId: 9021,
        idleSeq: 8417,
        walkSeq: 8416,
        attackSeq: 8418,
        deathSeq: 8421,
        hp: 7200,
        damage: 0,
        speed: 2,
        radius: 0.45,
        xp: 1300,
        sounds: { attack: 4089, hit: 4492, death: 4075 },
        scale: 0.6,
        knockbackResist: 1,
        spawnMessage: "The Crystalline Hunllef breaks out of the Gauntlet!",
        // Crystal magic and arrows from a distance and a stomp up close.
        // Enraged, crystal spikes burst from the floor.
        boss: {
            attackInterval: 2.8,
            keepDistance: 6,
            enrage: { message: "The Crystalline Hunllef shrieks!" },
            attacks: [
                {
                    name: "Stomp",
                    kind: "swing",
                    damage: 6,
                    windup: 0.8,
                    cooldown: 0,
                    range: 3.4,
                    radius: 3.2,
                    weight: 3,
                    seq: 8423,
                    sound: 4089,
                },
                {
                    name: "Crystal magic",
                    kind: "projectile",
                    damage: 4,
                    windup: 0.6,
                    cooldown: 0,
                    range: 15,
                    weight: 4,
                    speed: 9,
                    homing: 0.5,
                    spotAnim: 1707,
                    sound: 162,
                },
                {
                    name: "Crystal arrows",
                    kind: "projectile",
                    damage: 4,
                    windup: 0.5,
                    cooldown: 2,
                    range: 15,
                    weight: 4,
                    speed: 13,
                    count: 3,
                    spread: 0.25,
                    spotAnim: 1711,
                    seq: 8420,
                    sound: 2707,
                },
                {
                    name: "Crystal spikes",
                    kind: "blast",
                    damage: 5,
                    windup: 1.1,
                    cooldown: 5,
                    range: 15,
                    radius: 1.5,
                    count: 7,
                    spread: 4,
                    weight: 4,
                    impactSpotAnim: 1719,
                    sound: 1659,
                    enraged: true,
                },
            ],
        },
    },

    // The final boss: the Corrupted Hunllef, the Corrupted Gauntlet's last.
    // Its corrupted crystals (1708, 1712), tornadoes that chase you down
    // (1722) and floor tiles that burn (1723).
    {
        id: "corrupted_hunllef",
        name: "Corrupted Hunllef",
        npcId: 9035,
        idleSeq: 8417,
        walkSeq: 8416,
        attackSeq: 8418,
        deathSeq: 8421,
        hp: 23000,
        damage: 0,
        speed: 2.1,
        radius: 0.45,
        xp: 5000,
        sounds: { attack: 4128, hit: 4492, death: 4075 },
        scale: 0.6,
        knockbackResist: 1,
        spawnMessage: "The Corrupted Hunllef emerges from the Corrupted Gauntlet!",
        boss: {
            attackInterval: 2.6,
            keepDistance: 5,
            enrage: {
                message: "The Corrupted Hunllef is enraged!",
                shout: "Skreeeee!",
            },
            attacks: [
                {
                    name: "Stomp",
                    kind: "swing",
                    damage: 9,
                    windup: 0.8,
                    cooldown: 0,
                    range: 3.6,
                    radius: 3.4,
                    weight: 3,
                    seq: 8423,
                    sound: 4128,
                },
                {
                    name: "Corrupted magic",
                    kind: "projectile",
                    damage: 6,
                    windup: 0.6,
                    cooldown: 0,
                    range: 16,
                    weight: 4,
                    speed: 9,
                    homing: 0.5,
                    spotAnim: 1708,
                    sound: 162,
                },
                {
                    name: "Corrupted arrows",
                    kind: "projectile",
                    damage: 5,
                    windup: 0.5,
                    cooldown: 2,
                    range: 16,
                    weight: 3,
                    speed: 13,
                    count: 3,
                    spread: 0.25,
                    spotAnim: 1712,
                    seq: 8420,
                    sound: 2707,
                },
                {
                    name: "Tornadoes",
                    kind: "projectile",
                    damage: 6,
                    windup: 1,
                    cooldown: 6,
                    range: 16,
                    weight: 3,
                    speed: 3.6,
                    count: 3,
                    spread: 1.2,
                    homing: 2.5,
                    spotAnim: 1722,
                    sound: 1659,
                    message: "Tornadoes! Outrun them!",
                },
                {
                    name: "Corrupted floor",
                    kind: "blast",
                    damage: 6,
                    windup: 1.2,
                    cooldown: 5,
                    range: 16,
                    radius: 1.4,
                    count: 7,
                    spread: 4,
                    weight: 3,
                    impactSpotAnim: 1723,
                    sound: 1659,
                },
                {
                    name: "Tornado storm",
                    kind: "projectile",
                    damage: 6,
                    windup: 1,
                    cooldown: 6,
                    range: 16,
                    weight: 4,
                    speed: 3.8,
                    count: 5,
                    spread: 1.2,
                    homing: 2.5,
                    spotAnim: 1722,
                    sound: 1659,
                    message: "More tornadoes! Keep running!",
                    enraged: true,
                },
            ],
        },
    },

    // The God Wars Dungeon: the gods' armies at war in the cave under
    // Trollheim. Bandos's goblins, hobgoblins, orks, ogres, jogres and
    // cyclopes; Armadyl's aviansies, who fly over the cave's rocks; Saradomin's
    // knights and priests; Zamorak's imps, fiends, werewolves, hellhounds and
    // bloodvelds; each god's spiritual warriors, rangers and mages; and the
    // goraks, who fight for no one. Each army is a faction: they come from
    // their own side and hurt each other when they meet. Their npc ids are the
    // dungeon's own, which sometimes look different from their kin elsewhere
    // (the Bandos jogres, the Zamorakian werewolves).
    {
        id: "gwd_goblin",
        name: "Goblin",
        faction: "bandos",
        npcId: 2245,
        idleSeq: 6181,
        walkSeq: 6180,
        attackSeq: 6184,
        deathSeq: 6182,
        hp: 5,
        damage: 1,
        speed: 2.4,
        radius: 0.3,
        xp: 5,
        sounds: { attack: 469, hit: 472, death: 471 },
    },
    {
        id: "gwd_imp",
        name: "Imp",
        faction: "zamorak",
        npcId: 3134,
        idleSeq: 171,
        walkSeq: 168,
        attackSeq: 169,
        deathSeq: 172,
        hp: 4,
        damage: 1,
        speed: 2.7,
        radius: 0.3,
        xp: 5,
        sounds: { attack: 534, hit: 536, death: 535 },
    },
    {
        id: "icefiend",
        name: "Icefiend",
        faction: "zamorak",
        npcId: 3140,
        idleSeq: 1578,
        walkSeq: 1579,
        attackSeq: 1582,
        deathSeq: 1580,
        hp: 9,
        damage: 2,
        speed: 2.3,
        radius: 0.3,
        xp: 8,
        sounds: { attack: 534, hit: 536, death: 535 },
    },
    {
        id: "pyrefiend",
        name: "Pyrefiend",
        faction: "zamorak",
        npcId: 3139,
        idleSeq: 1578,
        walkSeq: 1579,
        attackSeq: 1582,
        deathSeq: 1580,
        hp: 13,
        damage: 2,
        speed: 2.3,
        radius: 0.3,
        xp: 9,
        sounds: { attack: 534, hit: 536, death: 535 },
    },
    {
        id: "gwd_hobgoblin",
        name: "Hobgoblin",
        faction: "bandos",
        npcId: 2241,
        idleSeq: 166,
        walkSeq: 162,
        attackSeq: 163,
        deathSeq: 167,
        hp: 15,
        damage: 3,
        speed: 2.1,
        radius: 0.35,
        xp: 10,
        sounds: { attack: 469, hit: 472, death: 471 },
    },
    {
        id: "aviansie",
        name: "Aviansie",
        faction: "armadyl",
        npcId: 3169,
        idleSeq: 6952,
        walkSeq: 6953,
        // A thrown spear
        attackSeq: 6956,
        deathSeq: 6959,
        hp: 15,
        damage: 3,
        speed: 2.5,
        radius: 0.35,
        xp: 10,
        sounds: { attack: 292, hit: 294, death: 293 },
        // Two tiles across in OSRS
        scale: 0.8,
        flies: true,
    },
    {
        id: "knight_of_saradomin",
        name: "Knight of Saradomin",
        faction: "saradomin",
        npcId: 2214,
        ...HUMAN,
        // A sword's slash
        attackSeq: 390,
        hp: 18,
        damage: 3,
        speed: 2.2,
        radius: 0.35,
        xp: 11,
        sounds: HUMAN_SOUNDS,
    },
    {
        id: "gwd_werewolf",
        name: "Werewolf",
        faction: "zamorak",
        npcId: 3135,
        idleSeq: 6539,
        walkSeq: 6541,
        attackSeq: 6536,
        deathSeq: 6537,
        hp: 22,
        damage: 3,
        speed: 2.4,
        radius: 0.4,
        xp: 13,
        sounds: { attack: 710, hit: 713, death: 711 },
    },
    {
        id: "saradomin_priest",
        name: "Saradomin priest",
        faction: "saradomin",
        npcId: 2209,
        ...HUMAN,
        // The god spell cast
        attackSeq: 811,
        hp: 28,
        damage: 4,
        speed: 2.0,
        radius: 0.35,
        xp: 17,
        sounds: HUMAN_SOUNDS,
    },
    {
        id: "gwd_ork",
        name: "Ork",
        faction: "bandos",
        npcId: 2237,
        idleSeq: 4318,
        walkSeq: 4319,
        attackSeq: 4320,
        deathSeq: 4321,
        hp: 30,
        damage: 4,
        speed: 2.1,
        radius: 0.35,
        xp: 18,
        sounds: { attack: 469, hit: 472, death: 471 },
        knockbackResist: 0.2,
    },
    {
        id: "armadyl_spiritual_ranger",
        name: "Spiritual ranger",
        faction: "armadyl",
        npcId: 3167,
        idleSeq: 6952,
        walkSeq: 6953,
        attackSeq: 6956,
        deathSeq: 6959,
        hp: 34,
        damage: 4,
        speed: 2.3,
        radius: 0.4,
        xp: 20,
        sounds: { attack: 292, hit: 294, death: 293 },
        scale: 0.8,
        flies: true,
        knockbackResist: 0.2,
    },
    {
        id: "hellhound",
        name: "Hellhound",
        faction: "zamorak",
        npcId: 3133,
        idleSeq: 6580,
        walkSeq: 6582,
        attackSeq: 6581,
        deathSeq: 6576,
        hp: 36,
        damage: 5,
        speed: 2.3,
        radius: 0.4,
        xp: 22,
        sounds: { attack: 710, hit: 713, death: 711 },
        scale: 0.8,
        knockbackResist: 0.3,
    },
    {
        id: "gwd_ogre",
        name: "Ogre",
        faction: "bandos",
        npcId: 2233,
        idleSeq: 357,
        walkSeq: 358,
        attackSeq: 359,
        deathSeq: 361,
        hp: 42,
        damage: 5,
        speed: 1.8,
        radius: 0.45,
        xp: 25,
        sounds: HUMAN_SOUNDS,
        scale: 0.8,
        knockbackResist: 0.4,
    },
    {
        id: "gwd_bloodveld",
        name: "Bloodveld",
        faction: "zamorak",
        npcId: 3138,
        idleSeq: 1551,
        walkSeq: 1549,
        attackSeq: 1552,
        deathSeq: 1553,
        hp: 44,
        damage: 5,
        speed: 1.9,
        radius: 0.45,
        xp: 26,
        sounds: { attack: 918, hit: 923, death: 922 },
        scale: 0.8,
        knockbackResist: 0.4,
    },
    {
        id: "gwd_jogre",
        name: "Jogre",
        faction: "bandos",
        npcId: 2234,
        idleSeq: 2931,
        walkSeq: 2932,
        attackSeq: 2935,
        deathSeq: 2938,
        hp: 48,
        damage: 6,
        speed: 1.8,
        radius: 0.45,
        xp: 28,
        sounds: HUMAN_SOUNDS,
        scale: 0.8,
        knockbackResist: 0.4,
    },
    {
        id: "saradomin_spiritual_warrior",
        name: "Spiritual warrior",
        faction: "saradomin",
        npcId: 2210,
        ...HUMAN,
        attackSeq: 390,
        hp: 52,
        damage: 6,
        speed: 2.1,
        radius: 0.35,
        xp: 30,
        sounds: HUMAN_SOUNDS,
        knockbackResist: 0.3,
    },
    {
        id: "gorak",
        name: "Gorak",
        // Goraks fight for no god, so everyone's army is theirs to fight
        faction: "gorak",
        npcId: 3141,
        idleSeq: 4299,
        walkSeq: 4298,
        attackSeq: 4301,
        deathSeq: 4302,
        hp: 54,
        damage: 6,
        speed: 2.0,
        radius: 0.45,
        xp: 32,
        sounds: { attack: 369, hit: 371, death: 370 },
        scale: 0.8,
        knockbackResist: 0.5,
    },
    // The eyrie's veterans
    {
        id: "aviansie_veteran",
        name: "Aviansie",
        faction: "armadyl",
        npcId: 3176,
        idleSeq: 6952,
        walkSeq: 6953,
        attackSeq: 6955,
        deathSeq: 6959,
        hp: 56,
        damage: 6,
        speed: 2.3,
        radius: 0.4,
        xp: 32,
        sounds: { attack: 292, hit: 294, death: 293 },
        scale: 0.8,
        flies: true,
        knockbackResist: 0.3,
    },
    {
        id: "gwd_cyclops",
        name: "Cyclops",
        faction: "bandos",
        npcId: 2235,
        idleSeq: 4650,
        walkSeq: 4649,
        attackSeq: 4652,
        deathSeq: 4653,
        hp: 62,
        damage: 7,
        speed: 1.8,
        radius: 0.45,
        xp: 36,
        sounds: HUMAN_SOUNDS,
        scale: 0.8,
        knockbackResist: 0.6,
    },
    {
        id: "saradomin_spiritual_mage",
        name: "Spiritual mage",
        faction: "saradomin",
        npcId: 2212,
        ...HUMAN,
        attackSeq: 811,
        hp: 64,
        damage: 7,
        speed: 2.0,
        radius: 0.35,
        xp: 37,
        sounds: HUMAN_SOUNDS,
        knockbackResist: 0.4,
    },
    {
        id: "zamorak_spiritual_warrior",
        name: "Spiritual warrior",
        faction: "zamorak",
        npcId: 3159,
        ...HUMAN,
        attackSeq: 390,
        hp: 70,
        damage: 7,
        speed: 2.1,
        radius: 0.35,
        xp: 40,
        sounds: HUMAN_SOUNDS,
        knockbackResist: 0.5,
    },
    {
        id: "bandos_spiritual_warrior",
        name: "Spiritual warrior",
        faction: "bandos",
        npcId: 2243,
        idleSeq: 4318,
        walkSeq: 4319,
        attackSeq: 4322,
        deathSeq: 4321,
        hp: 70,
        damage: 7,
        speed: 2.1,
        radius: 0.4,
        xp: 40,
        sounds: { attack: 469, hit: 472, death: 471 },
        knockbackResist: 0.5,
    },
    {
        id: "armadyl_spiritual_warrior",
        name: "Spiritual warrior",
        faction: "armadyl",
        npcId: 3166,
        idleSeq: 6952,
        walkSeq: 6953,
        attackSeq: 6955,
        deathSeq: 6959,
        hp: 70,
        damage: 7,
        speed: 2.2,
        radius: 0.4,
        xp: 40,
        sounds: { attack: 292, hit: 294, death: 293 },
        scale: 0.8,
        flies: true,
        knockbackResist: 0.5,
    },

    // The generals' bodyguards, who come to them as they're hurt (and fight
    // the other armies too)
    {
        id: "starlight",
        name: "Starlight",
        faction: "saradomin",
        npcId: 2206,
        idleSeq: 6374,
        walkSeq: 6373,
        attackSeq: 6376,
        deathSeq: 6377,
        hp: 80,
        damage: 5,
        speed: 2.4,
        radius: 0.45,
        xp: 50,
        sounds: { attack: 369, hit: 371, death: 370 },
        scale: 0.8,
        knockbackResist: 0.7,
    },
    {
        id: "growler",
        name: "Growler",
        faction: "saradomin",
        npcId: 2207,
        idleSeq: 7032,
        walkSeq: 7033,
        attackSeq: 7036,
        deathSeq: 7034,
        hp: 80,
        damage: 5,
        speed: 2.4,
        radius: 0.45,
        xp: 50,
        sounds: { attack: 710, hit: 713, death: 711 },
        scale: 0.6,
        knockbackResist: 0.7,
    },
    {
        id: "bree",
        name: "Bree",
        faction: "saradomin",
        npcId: 2208,
        idleSeq: 4311,
        walkSeq: 4310,
        attackSeq: 7027,
        deathSeq: 7028,
        hp: 80,
        damage: 5,
        speed: 2.2,
        radius: 0.45,
        xp: 50,
        sounds: HUMAN_SOUNDS,
        scale: 0.8,
        knockbackResist: 0.7,
    },
    {
        id: "wingman_skree",
        name: "Wingman Skree",
        faction: "armadyl",
        npcId: 3163,
        idleSeq: 6952,
        walkSeq: 6953,
        attackSeq: 6956,
        deathSeq: 6959,
        hp: 80,
        damage: 5,
        speed: 2.4,
        radius: 0.45,
        xp: 50,
        sounds: { attack: 292, hit: 294, death: 293 },
        scale: 0.8,
        flies: true,
        knockbackResist: 0.7,
    },
    {
        id: "flockleader_geerin",
        name: "Flockleader Geerin",
        faction: "armadyl",
        npcId: 3164,
        idleSeq: 6952,
        walkSeq: 6953,
        attackSeq: 6956,
        deathSeq: 6959,
        hp: 80,
        damage: 5,
        speed: 2.4,
        radius: 0.45,
        xp: 50,
        sounds: { attack: 292, hit: 294, death: 293 },
        scale: 0.8,
        flies: true,
        knockbackResist: 0.7,
    },
    {
        id: "flight_kilisa",
        name: "Flight Kilisa",
        faction: "armadyl",
        npcId: 3165,
        idleSeq: 6952,
        walkSeq: 6953,
        attackSeq: 6955,
        deathSeq: 6959,
        hp: 80,
        damage: 5,
        speed: 2.4,
        radius: 0.45,
        xp: 50,
        sounds: { attack: 292, hit: 294, death: 293 },
        scale: 0.8,
        flies: true,
        knockbackResist: 0.7,
    },
    {
        id: "tstanon_karlak",
        name: "Tstanon Karlak",
        faction: "zamorak",
        npcId: 3130,
        idleSeq: 66,
        walkSeq: 63,
        attackSeq: 64,
        deathSeq: 67,
        hp: 80,
        damage: 5,
        speed: 2.1,
        radius: 0.45,
        xp: 50,
        sounds: { attack: 534, hit: 536, death: 535 },
        // Three tiles across in OSRS
        scale: 0.65,
        knockbackResist: 0.7,
    },
    {
        id: "zakln_gritch",
        name: "Zakl'n Gritch",
        faction: "zamorak",
        npcId: 3131,
        idleSeq: 66,
        walkSeq: 63,
        attackSeq: 64,
        deathSeq: 67,
        hp: 80,
        damage: 5,
        speed: 2.1,
        radius: 0.45,
        xp: 50,
        sounds: { attack: 534, hit: 536, death: 535 },
        scale: 0.8,
        knockbackResist: 0.7,
    },
    {
        id: "balfrug_kreeyath",
        name: "Balfrug Kreeyath",
        faction: "zamorak",
        npcId: 3132,
        idleSeq: 66,
        walkSeq: 63,
        attackSeq: 64,
        deathSeq: 67,
        hp: 80,
        damage: 5,
        speed: 2.1,
        radius: 0.45,
        xp: 50,
        sounds: { attack: 534, hit: 536, death: 535 },
        scale: 0.65,
        knockbackResist: 0.7,
    },
    {
        id: "sergeant_strongstack",
        name: "Sergeant Strongstack",
        faction: "bandos",
        npcId: 2216,
        idleSeq: 6153,
        walkSeq: 6152,
        attackSeq: 6154,
        deathSeq: 6156,
        hp: 80,
        damage: 5,
        speed: 2.2,
        radius: 0.35,
        xp: 50,
        sounds: { attack: 469, hit: 472, death: 471 },
        knockbackResist: 0.7,
    },
    {
        id: "sergeant_steelwill",
        name: "Sergeant Steelwill",
        faction: "bandos",
        npcId: 2217,
        idleSeq: 6153,
        walkSeq: 6152,
        attackSeq: 6154,
        deathSeq: 6156,
        hp: 80,
        damage: 5,
        speed: 2.2,
        radius: 0.35,
        xp: 50,
        sounds: { attack: 469, hit: 472, death: 471 },
        knockbackResist: 0.7,
    },
    {
        id: "sergeant_grimspike",
        name: "Sergeant Grimspike",
        faction: "bandos",
        npcId: 2218,
        idleSeq: 6153,
        walkSeq: 6152,
        attackSeq: 6154,
        deathSeq: 6156,
        hp: 80,
        damage: 5,
        speed: 2.2,
        radius: 0.35,
        xp: 50,
        sounds: { attack: 469, hit: 472, death: 471 },
        knockbackResist: 0.7,
    },
    // General Graardor comes out of the Bandos stronghold late in the run as
    // an elite (not a boss: a big one, with the elite's casket), at the head
    // of an ork charge, calling his sergeants to him as he goes
    {
        id: "general_graardor",
        name: "General Graardor",
        faction: "bandos",
        npcId: 2215,
        idleSeq: 7017,
        walkSeq: 7016,
        attackSeq: 7018,
        deathSeq: 7020,
        hp: 140,
        damage: 8,
        speed: 1.9,
        radius: 0.9,
        xp: 80,
        sounds: { attack: 469, hit: 472, death: 471 },
        // Four tiles across in OSRS; as an elite he's drawn at 1.6x this, a
        // little bigger than that. Wider than most to touch, so his body is
        // where he hits from.
        scale: 0.75,
        knockbackResist: 1,
        spawns: {
            enemies: [
                { id: "sergeant_strongstack", weight: 1 },
                { id: "sergeant_steelwill", weight: 1 },
                { id: "sergeant_grimspike", weight: 1 },
            ],
            interval: 10,
            count: 3,
            max: 3,
        },
    },

    // God Wars's bosses: the generals of three of the armies, each in the
    // middle of their own, calling in their bodyguards as they're hurt.
    // Commander Zilyana is quick, with Saradomin's lightning (Saradomin
    // Strike's, 76); Kree'arra shoots feathers (1199) and whirlwinds (the Wind
    // Wave's, 158-160) from a distance; K'ril Tsutsaroth's claws poison, his
    // magic is Flames of Zamorak (78), and his prayer smash hits through armour.
    {
        id: "commander_zilyana",
        name: "Commander Zilyana",
        faction: "saradomin",
        npcId: 2205,
        idleSeq: 6966,
        walkSeq: 6965,
        attackSeq: 6967,
        deathSeq: 6968,
        hp: 4000,
        damage: 0,
        speed: 2.2,
        radius: 0.45,
        xp: 1000,
        sounds: HUMAN_SOUNDS,
        // Two tiles across in OSRS
        scale: 1.4,
        knockbackResist: 1,
        spawnMessage: "Commander Zilyana charges out of Saradomin's encampment!",
        boss: {
            attackInterval: 2.6,
            // Stops with its body at you rather than standing over you
            keepDistance: 1.2,
            enrage: {
                message: "Commander Zilyana is enraged!",
                shout: "Saradomin lend me strength!",
            },
            summonsAt: [
                {
                    hp: 0.75,
                    enemyId: "starlight",
                    count: 1,
                    shout: "Death to the enemies of the light!",
                },
                { hp: 0.5, enemyId: "growler", count: 1, shout: "Slay the evil ones!" },
                {
                    hp: 0.25,
                    enemyId: "bree",
                    count: 1,
                    shout: "Forward! Our allies are with us!",
                },
            ],
            attacks: [
                {
                    name: "Slash",
                    kind: "swing",
                    damage: 4,
                    windup: 0.5,
                    cooldown: 0,
                    range: 2.8,
                    radius: 2.4,
                    weight: 4,
                    sound: 2564,
                },
                {
                    name: "Saradomin's light",
                    kind: "blast",
                    damage: 3,
                    windup: 1,
                    cooldown: 3,
                    range: 14,
                    radius: 1.6,
                    count: 2,
                    spread: 2.5,
                    weight: 3,
                    impactSpotAnim: 76,
                    seq: 6970,
                    sound: 1659,
                },
                { ...leapAttack(4, 2, 7), name: "Charge" },
                {
                    name: "Wrath of Saradomin",
                    kind: "blast",
                    damage: 4,
                    windup: 1.1,
                    cooldown: 5,
                    range: 16,
                    radius: 1.5,
                    count: 7,
                    spread: 4,
                    weight: 4,
                    impactSpotAnim: 76,
                    seq: 6970,
                    sound: 1659,
                    shout: "By the power of Saradomin!",
                    enraged: true,
                },
            ],
        },
    },
    {
        id: "kreearra",
        name: "Kree'arra",
        faction: "armadyl",
        npcId: 3162,
        idleSeq: 6976,
        walkSeq: 6977,
        attackSeq: 6980,
        deathSeq: 6979,
        hp: 5500,
        damage: 0,
        speed: 1.7,
        radius: 0.45,
        xp: 1300,
        sounds: { attack: 292, hit: 294, death: 293 },
        // Five tiles across in OSRS
        scale: 1,
        knockbackResist: 1,
        spawnMessage: "Kree'arra swoops down from the Armadyl eyrie!",
        boss: {
            attackInterval: 3,
            keepDistance: 6,
            enrage: {
                message: "Kree'arra screeches with fury!",
                shout: "Kraaaw!",
            },
            summonsAt: [
                {
                    hp: 0.75,
                    enemyId: "wingman_skree",
                    count: 1,
                    message: "Wingman Skree flies to Kree'arra's side!",
                },
                {
                    hp: 0.5,
                    enemyId: "flockleader_geerin",
                    count: 1,
                    message: "Flockleader Geerin flies to Kree'arra's side!",
                },
                {
                    hp: 0.25,
                    enemyId: "flight_kilisa",
                    count: 1,
                    message: "Flight Kilisa flies to Kree'arra's side!",
                },
            ],
            attacks: [
                {
                    name: "Talons",
                    kind: "swing",
                    damage: 5,
                    windup: 0.6,
                    cooldown: 0,
                    range: 3.4,
                    radius: 3,
                    weight: 3,
                    seq: 6981,
                    sound: 292,
                },
                {
                    name: "Feathers",
                    kind: "projectile",
                    damage: 3,
                    windup: 0.7,
                    cooldown: 0,
                    range: 16,
                    weight: 4,
                    speed: 10,
                    count: 3,
                    spread: 0.4,
                    // His feathers (1199 is the whirlwind)
                    spotAnim: 1197,
                    sound: 2707,
                },
                {
                    name: "Whirlwind",
                    kind: "blast",
                    damage: 3,
                    windup: 1.1,
                    cooldown: 4,
                    range: 16,
                    radius: 1.6,
                    count: 3,
                    spread: 3,
                    weight: 3,
                    castSpotAnim: 158,
                    impactSpotAnim: 160,
                    sound: 1659,
                },
                {
                    name: "Gale",
                    kind: "blast",
                    damage: 4,
                    windup: 1.1,
                    cooldown: 5,
                    range: 16,
                    radius: 1.5,
                    count: 8,
                    spread: 5,
                    weight: 4,
                    castSpotAnim: 158,
                    impactSpotAnim: 160,
                    sound: 1659,
                    message: "Kree'arra whips up a gale! Keep moving!",
                    enraged: true,
                },
            ],
        },
    },
    {
        id: "kril_tsutsaroth",
        name: "K'ril Tsutsaroth",
        faction: "zamorak",
        npcId: 3129,
        idleSeq: 6935,
        walkSeq: 4070,
        attackSeq: 6948,
        deathSeq: 6949,
        hp: 7000,
        damage: 0,
        speed: 1.8,
        radius: 0.45,
        xp: 1500,
        sounds: { attack: 534, hit: 536, death: 535 },
        // Five tiles across in OSRS
        scale: 1,
        knockbackResist: 1,
        spawnMessage: "K'ril Tsutsaroth storms out of Zamorak's fortress!",
        boss: {
            attackInterval: 2.4,
            // Stops with its body at you rather than standing over you
            keepDistance: 2.2,
            enrage: {
                message: "K'ril Tsutsaroth is enraged!",
                shout: "Flay them all!",
            },
            summonsAt: [
                {
                    hp: 0.75,
                    enemyId: "tstanon_karlak",
                    count: 1,
                    shout: "Attack them, you dogs!",
                },
                {
                    hp: 0.5,
                    enemyId: "zakln_gritch",
                    count: 1,
                    shout: "Rend them limb from limb!",
                },
                { hp: 0.25, enemyId: "balfrug_kreeyath", count: 1, shout: "No retreat!" },
            ],
            attacks: [
                {
                    name: "Poison claws",
                    kind: "swing",
                    damage: 5,
                    windup: 0.7,
                    cooldown: 0,
                    range: 3.4,
                    radius: 3.2,
                    weight: 4,
                    sound: 534,
                    effect: { kind: "poison", damage: 2, seconds: 5 },
                },
                {
                    name: "Flames of Zamorak",
                    kind: "blast",
                    damage: 4,
                    windup: 1,
                    cooldown: 3,
                    range: 16,
                    radius: 1.6,
                    count: 3,
                    spread: 2.5,
                    weight: 3,
                    impactSpotAnim: 78,
                    seq: 6950,
                    sound: 1659,
                },
                {
                    name: "Prayer smash",
                    kind: "swing",
                    damage: 10,
                    windup: 1.2,
                    cooldown: 8,
                    range: 3.6,
                    radius: 3.4,
                    weight: 2,
                    sound: 2500,
                    ignoreArmor: true,
                    shout: "YARRRRRRR!",
                    message: "K'ril Tsutsaroth winds up a smash! Step away!",
                },
                {
                    name: "Firestorm",
                    kind: "blast",
                    damage: 4,
                    windup: 1.1,
                    cooldown: 5,
                    range: 16,
                    radius: 1.5,
                    count: 7,
                    spread: 4,
                    weight: 4,
                    impactSpotAnim: 78,
                    seq: 6950,
                    sound: 1659,
                    enraged: true,
                },
            ],
        },
    },

    // The final boss: Nex, Zaros's general, out of her ancient prison. She
    // fights in four forms, getting up as the next each time she's beaten,
    // each borrowing an Ancient spell (the barrages: smoke 391, shadow 383,
    // blood 377, ice 369) and calling in the bodyguard of its element.
    {
        id: "nex",
        name: "Nex",
        faction: "zaros",
        npcId: 11278,
        idleSeq: 9177,
        walkSeq: 9175,
        attackSeq: 9180,
        deathSeq: 9184,
        hp: 5500,
        damage: 0,
        speed: 2.0,
        radius: 0.45,
        xp: 5000,
        sounds: HUMAN_SOUNDS,
        // Three tiles across in OSRS
        scale: 1.2,
        knockbackResist: 1,
        spawnMessage: "Nex is free of her prison!",
        boss: {
            attackInterval: 2.4,
            // Stops with its body at you rather than standing over you
            keepDistance: 1.6,
            rises: {
                times: 1,
                hp: 1,
                message: "Nex: Darken my shadow!",
                into: "nex_shadow",
            },
            enrage: {
                message: "The smoke thickens around Nex!",
                shout: "Fill my soul with smoke!",
            },
            summonsAt: [{ hp: 0.6, enemyId: "fumus", count: 1, shout: "Fumus, don't fail me!" }],
            attacks: [
                NEX_CLAWS,
                {
                    name: "Smoke barrage",
                    kind: "blast",
                    damage: 4,
                    windup: 1,
                    cooldown: 2,
                    range: 16,
                    radius: 1.8,
                    count: 3,
                    spread: 2.5,
                    weight: 4,
                    impactSpotAnim: 391,
                    seq: 9182,
                    sound: 168,
                    effect: { kind: "poison", damage: 2, seconds: 5 },
                },
                { ...leapAttack(6, 2.4, 8), name: "No escape", shout: "There is... NO ESCAPE!" },
                {
                    name: "Smoke storm",
                    kind: "blast",
                    damage: 4,
                    windup: 1.1,
                    cooldown: 5,
                    range: 16,
                    radius: 1.6,
                    count: 7,
                    spread: 4.5,
                    weight: 4,
                    impactSpotAnim: 391,
                    seq: 9182,
                    sound: 168,
                    effect: { kind: "poison", damage: 2, seconds: 5 },
                    shout: "Let the virus flow through you!",
                    enraged: true,
                },
            ],
        },
    },
    // Her shadow form: only comes from the smoke form getting back up
    {
        id: "nex_shadow",
        name: "Nex",
        faction: "zaros",
        npcId: 11278,
        idleSeq: 9177,
        walkSeq: 9175,
        attackSeq: 9180,
        deathSeq: 9184,
        hp: 5500,
        damage: 0,
        speed: 2.0,
        radius: 0.45,
        xp: 5000,
        sounds: HUMAN_SOUNDS,
        scale: 1.2,
        knockbackResist: 1,
        boss: {
            attackInterval: 2.4,
            // Stops with its body at you rather than standing over you
            keepDistance: 1.6,
            rises: {
                times: 2,
                hp: 1,
                message: "Nex: Flood my lungs with blood!",
                into: "nex_blood",
            },
            enrage: { message: "The shadows deepen around Nex!", shout: "Embrace darkness!" },
            summonsAt: [{ hp: 0.6, enemyId: "umbra", count: 1, shout: "Umbra, don't fail me!" }],
            attacks: [
                NEX_CLAWS,
                {
                    name: "Shadow barrage",
                    kind: "blast",
                    damage: 4,
                    windup: 1,
                    cooldown: 2,
                    range: 16,
                    radius: 1.8,
                    count: 3,
                    spread: 2.5,
                    weight: 4,
                    impactSpotAnim: 383,
                    seq: 9182,
                    sound: 168,
                    effect: SHADOWED,
                },
                {
                    name: "Shadows",
                    kind: "blast",
                    damage: 5,
                    windup: 1.2,
                    cooldown: 6,
                    range: 16,
                    radius: 1.4,
                    count: 6,
                    spread: 4,
                    weight: 3,
                    impactSpotAnim: 383,
                    seq: 9179,
                    sound: 168,
                    message: "Shadows gather around you! Keep moving!",
                },
                {
                    name: "Darkness",
                    kind: "blast",
                    damage: 5,
                    windup: 1.2,
                    cooldown: 5,
                    range: 16,
                    radius: 1.4,
                    count: 10,
                    spread: 5,
                    weight: 4,
                    impactSpotAnim: 383,
                    seq: 9179,
                    sound: 168,
                    effect: SHADOWED,
                    enraged: true,
                },
            ],
        },
    },
    // Her blood form, which heals off what it hits
    {
        id: "nex_blood",
        name: "Nex",
        faction: "zaros",
        npcId: 11278,
        idleSeq: 9177,
        walkSeq: 9175,
        attackSeq: 9180,
        deathSeq: 9184,
        hp: 5500,
        damage: 0,
        speed: 2.0,
        radius: 0.45,
        xp: 5000,
        sounds: HUMAN_SOUNDS,
        scale: 1.2,
        knockbackResist: 1,
        boss: {
            attackInterval: 2.4,
            // Stops with its body at you rather than standing over you
            keepDistance: 1.6,
            lifesteal: 0.5,
            rises: {
                times: 3,
                hp: 1,
                message: "Nex: Infuse me with the power of ice!",
                into: "nex_ice",
            },
            enrage: { message: "Nex thirsts for blood!", shout: "A siphon will solve this!" },
            summonsAt: [{ hp: 0.6, enemyId: "cruor", count: 1, shout: "Cruor, don't fail me!" }],
            attacks: [
                NEX_CLAWS,
                {
                    name: "Blood barrage",
                    kind: "blast",
                    damage: 4,
                    windup: 1,
                    cooldown: 2,
                    range: 16,
                    radius: 1.8,
                    count: 3,
                    spread: 2.5,
                    weight: 4,
                    impactSpotAnim: 377,
                    seq: 9182,
                    sound: 106,
                },
                {
                    name: "Blood sacrifice",
                    kind: "swing",
                    damage: 7,
                    windup: 1.6,
                    cooldown: 8,
                    range: 6,
                    radius: 5,
                    weight: 3,
                    impactSpotAnim: 377,
                    seq: 9179,
                    sound: 106,
                    shout: "I demand a blood sacrifice!",
                    message: "Nex demands a blood sacrifice! Get away from her!",
                },
                {
                    name: "Blood siphon",
                    kind: "blast",
                    damage: 5,
                    windup: 1.1,
                    cooldown: 5,
                    range: 16,
                    radius: 1.5,
                    count: 7,
                    spread: 4,
                    weight: 4,
                    impactSpotAnim: 377,
                    seq: 9182,
                    sound: 106,
                    enraged: true,
                },
            ],
        },
    },
    // Her last form, ice; enraged, the power of Zaros itself
    {
        id: "nex_ice",
        name: "Nex",
        faction: "zaros",
        npcId: 11278,
        idleSeq: 9177,
        walkSeq: 9175,
        attackSeq: 9180,
        deathSeq: 9184,
        hp: 5500,
        damage: 0,
        speed: 2.0,
        radius: 0.45,
        xp: 5000,
        sounds: HUMAN_SOUNDS,
        scale: 1.2,
        knockbackResist: 1,
        boss: {
            attackInterval: 2.4,
            // Stops with its body at you rather than standing over you
            keepDistance: 1.6,
            enrage: {
                message: "Nex calls on the power of Zaros!",
                shout: "NOW, THE POWER OF ZAROS!",
            },
            summonsAt: [
                { hp: 0.6, enemyId: "glacies", count: 1, shout: "Glacies, don't fail me!" },
            ],
            attacks: [
                NEX_CLAWS,
                {
                    name: "Ice barrage",
                    kind: "blast",
                    damage: 4,
                    windup: 1,
                    cooldown: 2,
                    range: 16,
                    radius: 1.8,
                    count: 3,
                    spread: 2.5,
                    weight: 4,
                    impactSpotAnim: 369,
                    seq: 9182,
                    sound: 168,
                    effect: { kind: "freeze", seconds: 1 },
                },
                {
                    name: "Containment",
                    kind: "swing",
                    damage: 7,
                    windup: 1.2,
                    cooldown: 7,
                    range: 5,
                    radius: 4.5,
                    weight: 3,
                    impactSpotAnim: 369,
                    seq: 9179,
                    sound: 168,
                    shout: "Contain this!",
                    message: "Ice spikes burst up around Nex! Get away from her!",
                },
                {
                    name: "Ice prison",
                    kind: "blast",
                    damage: 8,
                    windup: 1.4,
                    cooldown: 9,
                    range: 16,
                    radius: 2,
                    weight: 3,
                    impactSpotAnim: 369,
                    seq: 9182,
                    sound: 168,
                    effect: { kind: "freeze", seconds: 2 },
                    shout: "Die now, in a prison of ice!",
                    message: "An ice prison closes in on you! Move!",
                },
                {
                    name: "Wrath of Zaros",
                    kind: "blast",
                    damage: 5,
                    windup: 1.1,
                    cooldown: 4,
                    range: 16,
                    radius: 1.5,
                    count: 9,
                    spread: 5,
                    weight: 4,
                    impactSpotAnim: 369,
                    seq: 9182,
                    sound: 168,
                    effect: { kind: "freeze", seconds: 1 },
                    enraged: true,
                },
            ],
        },
    },
    // Nex's bodyguards, one for each of her forms: Umbra steps out of the
    // shadows next to you, and Cruor heals Nex's blood form
    {
        id: "fumus",
        name: "Fumus",
        faction: "zaros",
        npcId: 11283,
        ...HUMAN,
        // The Ancient cast
        attackSeq: 1979,
        hp: 140,
        damage: 6,
        speed: 2.1,
        radius: 0.4,
        xp: 60,
        sounds: HUMAN_SOUNDS,
        knockbackResist: 0.7,
    },
    {
        id: "umbra",
        name: "Umbra",
        faction: "zaros",
        npcId: 11284,
        ...HUMAN,
        attackSeq: 1979,
        hp: 140,
        damage: 6,
        speed: 2.1,
        radius: 0.4,
        xp: 60,
        sounds: HUMAN_SOUNDS,
        knockbackResist: 0.7,
        blinks: {
            interval: 6,
            minRange: 5,
            distance: 1.5,
            windup: 0.6,
            castSpotAnim: 383,
            impactSpotAnim: 383,
            sound: 200,
        },
    },
    {
        id: "cruor",
        name: "Cruor",
        faction: "zaros",
        npcId: 11285,
        ...HUMAN,
        attackSeq: 1979,
        hp: 140,
        damage: 6,
        speed: 2.1,
        radius: 0.4,
        xp: 60,
        sounds: HUMAN_SOUNDS,
        knockbackResist: 0.7,
        heals: {
            fraction: 0.01,
            interval: 2,
            range: 2.5,
            targets: ["nex_blood"],
            seek: 30,
            spotAnim: 377,
        },
    },
    {
        id: "glacies",
        name: "Glacies",
        faction: "zaros",
        npcId: 11286,
        ...HUMAN,
        attackSeq: 1979,
        hp: 140,
        damage: 6,
        speed: 2.1,
        radius: 0.4,
        xp: 60,
        sounds: HUMAN_SOUNDS,
        knockbackResist: 0.7,
    },

    // The Inferno, under Mor Ul Rek: the city's TzHaar (Hur, Xil, Mej and Ket
    // castes) and the Inferno's creatures. Jal-Nib nibblers in swarms,
    // Jal-MejRah bats, Jal-Ak blobs that split into three small ones when they
    // die, Jal-ImKot that dig in and come up near you, Jal-Xil rangers and
    // Jal-Zek mages that bring the dead back up. The TzHaar sound like
    // people, the Inferno's creatures like the Fight Caves'.
    {
        id: "jal_nib",
        name: "Jal-Nib",
        npcId: 7691,
        idleSeq: 7573,
        walkSeq: 7572,
        attackSeq: 7574,
        deathSeq: 7576,
        hp: 4,
        damage: 1,
        speed: 2.8,
        radius: 0.3,
        xp: 5,
        sounds: { attack: 710, hit: 713, death: 711 },
    },
    {
        id: "tzhaar_hur",
        name: "TzHaar-Hur",
        npcId: 2161,
        idleSeq: 2603,
        walkSeq: 2601,
        attackSeq: 2609,
        deathSeq: 2607,
        hp: 8,
        damage: 2,
        speed: 2.3,
        radius: 0.35,
        xp: 7,
        sounds: HUMAN_SOUNDS,
    },
    {
        id: "jal_mejrah",
        name: "Jal-MejRah",
        npcId: 7692,
        idleSeq: 7577,
        walkSeq: 7577,
        attackSeq: 7578,
        deathSeq: 7580,
        hp: 9,
        damage: 2,
        speed: 2.6,
        radius: 0.35,
        xp: 8,
        sounds: { attack: 292, hit: 294, death: 293 },
        // Two tiles across in OSRS
        scale: 0.7,
        flies: true,
    },
    {
        id: "tzhaar_xil",
        name: "TzHaar-Xil",
        npcId: 2168,
        idleSeq: 2603,
        walkSeq: 2601,
        attackSeq: 2613,
        deathSeq: 2607,
        hp: 15,
        damage: 2,
        speed: 2.2,
        radius: 0.35,
        xp: 10,
        sounds: HUMAN_SOUNDS,
    },
    {
        id: "tzhaar_mej",
        name: "TzHaar-Mej",
        npcId: 2154,
        idleSeq: 2604,
        walkSeq: 2602,
        attackSeq: 2611,
        deathSeq: 2607,
        hp: 17,
        damage: 2,
        speed: 2.1,
        radius: 0.35,
        xp: 11,
        sounds: HUMAN_SOUNDS,
    },
    {
        id: "tzhaar_ket",
        name: "TzHaar-Ket",
        npcId: 7679,
        idleSeq: 2603,
        walkSeq: 2601,
        attackSeq: 2610,
        deathSeq: 2607,
        hp: 20,
        damage: 2,
        speed: 2.2,
        radius: 0.35,
        xp: 13,
        sounds: HUMAN_SOUNDS,
        knockbackResist: 0.2,
    },
    {
        id: "jal_ak",
        name: "Jal-Ak",
        npcId: 7693,
        idleSeq: 7586,
        walkSeq: 7587,
        attackSeq: 7582,
        deathSeq: 7584,
        hp: 28,
        damage: 3,
        speed: 1.9,
        radius: 0.45,
        xp: 20,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
        // Three tiles across in OSRS
        scale: 0.6,
        knockbackResist: 0.4,
        splits: { into: "jal_akrek_ket", count: 3 },
    },
    // What a Jal-Ak splits into
    {
        id: "jal_akrek_ket",
        name: "Jal-AkRek-Ket",
        npcId: 7696,
        idleSeq: 7586,
        walkSeq: 7587,
        attackSeq: 7582,
        deathSeq: 7584,
        hp: 4,
        damage: 1,
        speed: 2.0,
        radius: 0.3,
        xp: 5,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
    },
    {
        id: "jal_imkot",
        name: "Jal-ImKot",
        npcId: 7697,
        idleSeq: 7595,
        walkSeq: 7596,
        attackSeq: 7597,
        deathSeq: 7599,
        hp: 32,
        damage: 3,
        speed: 1.6,
        radius: 0.45,
        xp: 26,
        sounds: { attack: 4787, hit: 4492, death: 4479 },
        // Four tiles across in OSRS
        scale: 0.5,
        knockbackResist: 0.6,
        // Digs in when it's left behind, and comes up near you, slowly enough
        // to get away from it, and a crowd of them one after another
        blinks: {
            interval: 10,
            minRange: 8,
            distance: 5,
            windup: 2.2,
            seq: 7600,
            sound: 2500,
            surface: { seq: 7601, seconds: 2.1 },
            gap: 1.5,
        },
    },
    {
        id: "jal_xil",
        name: "Jal-Xil",
        npcId: 7698,
        idleSeq: 7602,
        walkSeq: 7603,
        attackSeq: 7604,
        deathSeq: 7606,
        hp: 56,
        damage: 6,
        speed: 1.8,
        radius: 0.45,
        xp: 32,
        sounds: { attack: 2707, hit: 4492, death: 4479 },
        scale: 0.6,
        knockbackResist: 0.5,
    },
    {
        id: "jal_zek",
        name: "Jal-Zek",
        npcId: 7699,
        idleSeq: 7609,
        walkSeq: 7608,
        attackSeq: 7612,
        deathSeq: 7613,
        hp: 72,
        damage: 7,
        speed: 1.7,
        radius: 0.45,
        xp: 40,
        sounds: { attack: 162, hit: 4492, death: 4479 },
        scale: 0.5,
        knockbackResist: 0.7,
        // Raises the last thing to die near it, now and then
        revives: { interval: 8, range: 9, hp: 0.6, seq: 7611, spotAnim: 1382 },
    },
    // JalTok-Jad's healers, which go to him and heal him
    {
        id: "inferno_yt_hurkot",
        name: "Yt-HurKot",
        npcId: 7701,
        idleSeq: 2636,
        walkSeq: 2634,
        attackSeq: 2637,
        deathSeq: 2638,
        hp: 30,
        damage: 4,
        speed: 2.2,
        radius: 0.35,
        xp: 15,
        sounds: HUMAN_SOUNDS,
        heals: {
            fraction: 0.005,
            interval: 2,
            range: 2.5,
            targets: ["jaltok_jad", "jaltok_jad_finale"],
            seek: 30,
            spotAnim: 444,
        },
    },

    // The Inferno's bosses: JalTok-Jad with his healers, a Jal-Zek champion
    // that keeps raising the dead and a TzHaar-Ket champion, and at the end
    // three JalTok-Jads at once. Jad's magic and the rocks are TzTok-Jad's
    // (447-448, 451); the Jal-Zek's magic is its own (1376).
    {
        id: "jaltok_jad",
        name: "JalTok-Jad",
        npcId: 7700,
        idleSeq: 7589,
        walkSeq: 7588,
        attackSeq: 7590,
        deathSeq: 7594,
        hp: 4400,
        damage: 0,
        speed: 1.5,
        radius: 0.45,
        xp: 1200,
        sounds: HUMAN_SOUNDS,
        // Five tiles across in OSRS
        scale: 0.55,
        knockbackResist: 1,
        spawnMessage: "JalTok-Jad rises out of the lava!",
        boss: {
            attackInterval: 3,
            enrage: { message: "JalTok-Jad is enraged!", shout: "JalYt, you will burn!" },
            attacks: [
                ...jadAttacks(4, 3),
                {
                    name: "Call healers",
                    kind: "summon",
                    damage: 0,
                    windup: 1,
                    cooldown: 20,
                    range: 16,
                    weight: 3,
                    summon: { enemyId: "inferno_yt_hurkot", count: 2 },
                    sound: 2500,
                },
                {
                    name: "Rockstorm",
                    kind: "blast",
                    damage: 3,
                    windup: 1.3,
                    cooldown: 5,
                    range: 16,
                    radius: 1.6,
                    count: 5,
                    spread: 3.5,
                    weight: 4,
                    impactSpotAnim: 451,
                    seq: 7593,
                    sound: 2500,
                    enraged: true,
                },
            ],
        },
    },
    // The Inferno's final boss is three of these at once, each with healers
    // of its own to call in: the run's won when the last of them falls
    {
        id: "jaltok_jad_finale",
        name: "JalTok-Jad",
        npcId: 10623,
        idleSeq: 7589,
        walkSeq: 7588,
        attackSeq: 7590,
        deathSeq: 7594,
        hp: 8800,
        damage: 0,
        speed: 1.5,
        radius: 0.45,
        xp: 2000,
        sounds: HUMAN_SOUNDS,
        scale: 0.55,
        knockbackResist: 1,
        spawnMessage: "Three JalTok-Jads rise out of the lava!",
        boss: {
            // Slower than the one Jad: there are three of them
            attackInterval: 3.6,
            enrage: { message: "A JalTok-Jad is enraged!", shout: "JalYt, you will burn!" },
            attacks: [
                ...jadAttacks(4, 3),
                {
                    name: "Call healers",
                    kind: "summon",
                    damage: 0,
                    windup: 1,
                    cooldown: 30,
                    range: 16,
                    weight: 2,
                    summon: { enemyId: "inferno_yt_hurkot", count: 2 },
                    sound: 2500,
                },
                {
                    name: "Rockstorm",
                    kind: "blast",
                    damage: 3,
                    windup: 1.3,
                    cooldown: 6,
                    range: 16,
                    radius: 1.6,
                    count: 4,
                    spread: 3.5,
                    weight: 3,
                    impactSpotAnim: 451,
                    seq: 7593,
                    sound: 2500,
                    enraged: true,
                },
            ],
        },
    },
    {
        id: "jal_zek_champion",
        name: "Jal-Zek champion",
        npcId: 7703,
        idleSeq: 7609,
        walkSeq: 7608,
        attackSeq: 7610,
        deathSeq: 7613,
        hp: 7600,
        damage: 0,
        speed: 1.7,
        radius: 0.45,
        xp: 1500,
        sounds: { attack: 162, hit: 4492, death: 4479 },
        // Four tiles across in OSRS, and bigger than the rest
        scale: 0.65,
        knockbackResist: 1,
        spawnMessage: "A Jal-Zek champion rises to raise the dead!",
        revives: { interval: 4, range: 14, hp: 0.6, seq: 7611, spotAnim: 1382 },
        boss: {
            attackInterval: 2.8,
            keepDistance: 5,
            enrage: { message: "The Jal-Zek champion is enraged!" },
            attacks: [
                {
                    name: "Slam",
                    kind: "swing",
                    damage: 7,
                    windup: 0.8,
                    cooldown: 0,
                    range: 3.4,
                    radius: 3.2,
                    weight: 3,
                    seq: 7612,
                    sound: 2500,
                },
                {
                    name: "Magic",
                    kind: "projectile",
                    damage: 5,
                    windup: 0.8,
                    cooldown: 0,
                    range: 16,
                    weight: 4,
                    speed: 9,
                    count: 2,
                    spread: 0.35,
                    homing: 0.5,
                    spotAnim: 1376,
                    impactSpotAnim: 157,
                    sound: 162,
                    hitSound: 163,
                },
                {
                    name: "Raise the blobs",
                    kind: "summon",
                    damage: 0,
                    windup: 1.2,
                    cooldown: 14,
                    range: 16,
                    weight: 2,
                    summon: { enemyId: "jal_ak", count: 3 },
                    seq: 7611,
                    sound: 162,
                },
                {
                    name: "Firestorm",
                    kind: "blast",
                    damage: 5,
                    windup: 1.1,
                    cooldown: 5,
                    range: 16,
                    radius: 1.5,
                    count: 7,
                    spread: 4,
                    weight: 4,
                    impactSpotAnim: 157,
                    sound: 162,
                    enraged: true,
                },
            ],
        },
    },

    // A TzHaar-Ket twice the size of the rest, with an obsidian maul: he comes
    // straight at you, leaps onto where you stood and calls in his own
    {
        id: "tzhaar_ket_champion",
        name: "TzHaar-Ket champion",
        npcId: 7679,
        idleSeq: 2603,
        walkSeq: 2601,
        attackSeq: 2610,
        deathSeq: 2607,
        hp: 6800,
        damage: 0,
        speed: 2,
        radius: 0.45,
        xp: 1500,
        sounds: HUMAN_SOUNDS,
        scale: 1.8,
        knockbackResist: 1,
        spawnMessage: "A TzHaar-Ket champion comes to test you, JalYt!",
        boss: {
            attackInterval: 2.4,
            enrage: { message: "The TzHaar-Ket champion is enraged!", shout: "TzHaar-Ket-Rok!" },
            attacks: [
                {
                    name: "Maul",
                    kind: "swing",
                    damage: 7,
                    windup: 0.8,
                    cooldown: 0,
                    range: 3,
                    radius: 2.8,
                    weight: 5,
                    sound: 2500,
                },
                {
                    name: "Leap",
                    kind: "blast",
                    damage: 6,
                    windup: 1.1,
                    cooldown: 6,
                    range: 12,
                    minRange: 4,
                    radius: 2.4,
                    weight: 3,
                    impactSpotAnim: 451,
                    sound: 2500,
                    leap: true,
                },
                {
                    name: "Call the Ket",
                    kind: "summon",
                    damage: 0,
                    windup: 1,
                    cooldown: 16,
                    range: 16,
                    weight: 2,
                    summon: { enemyId: "tzhaar_ket", count: 4 },
                    shout: "TzHaar-Ket, to me!",
                },
                {
                    name: "Quake",
                    kind: "blast",
                    damage: 4,
                    windup: 1.2,
                    cooldown: 5,
                    range: 16,
                    radius: 1.6,
                    count: 6,
                    spread: 4,
                    weight: 3,
                    impactSpotAnim: 451,
                    sound: 2500,
                    enraged: true,
                },
            ],
        },
    },

    // Halloween's mid-run bosses. Slash Bash, the zombie ogre of Zogre Flesh
    // Eaters, doesn't die to being hit in OSRS so much as out-last you: here his
    // disease stacks up and slows you while his bile leaves less and less
    // ground to stand on. Olm's acid spit (1354) and its splash (1294) stand in
    // for his, as he has none of his own.
    {
        id: "slash_bash",
        name: "Slash Bash",
        tags: ["undead"],
        npcId: 882,
        idleSeq: 357,
        walkSeq: 358,
        attackSeq: 359,
        deathSeq: 361,
        hp: 1800,
        damage: 0,
        speed: 1.5,
        radius: 0.45,
        xp: 700,
        // A zombie's: he's undead
        sounds: { attack: 918, hit: 923, death: 922 },
        knockbackResist: 1,
        spawnMessage: "Slash Bash lumbers out of the dark, reeking of disease!",
        boss: {
            attackInterval: 2.4,
            keepDistance: 2,
            enrage: {
                message: "Slash Bash retches. Bile everywhere!",
                shout: "Grrraaagh!",
            },
            attacks: [
                {
                    name: "Bash",
                    kind: "swing",
                    damage: 6,
                    windup: 0.9,
                    cooldown: 0,
                    range: 3,
                    radius: 2.8,
                    weight: 3,
                    sound: 918,
                    effect: DISEASED,
                },
                {
                    name: "Bile spit",
                    kind: "projectile",
                    damage: 4,
                    windup: 0.7,
                    cooldown: 3,
                    range: 14,
                    minRange: 3,
                    weight: 2,
                    speed: 9,
                    count: 3,
                    spread: 0.3,
                    spotAnim: 1354,
                    impactSpotAnim: 1294,
                    sound: 918,
                    effect: DISEASED,
                },
                {
                    name: "Bile throw",
                    kind: "blast",
                    damage: 5,
                    windup: 1.3,
                    cooldown: 5,
                    range: 14,
                    radius: 1.6,
                    count: 2,
                    spread: 4,
                    weight: 3,
                    impactSpotAnim: 1294,
                    sound: 918,
                    pool: BILE_POOL,
                },
                {
                    name: "Bile deluge",
                    kind: "blast",
                    damage: 5,
                    windup: 1.5,
                    cooldown: 9,
                    range: 16,
                    radius: 1.6,
                    count: 5,
                    spread: 6,
                    weight: 3,
                    impactSpotAnim: 1294,
                    sound: 918,
                    pool: BILE_POOL,
                    enraged: true,
                    message: "Slash Bash heaves up a flood of bile!",
                },
            ],
        },
    },
    // The Mimic, from elite and master clues: it lies on the map as a master
    // casket until it's walked onto. Its real three attacks: the melee bite,
    // six thrown candies (three do nothing more, red, green and blue bring a
    // Third Age minion each, who drop nothing) and the stomp, which hurts
    // more every moment you stay under it.
    {
        id: "the_mimic",
        name: "The Mimic",
        npcId: 7979,
        idleSeq: 8307,
        walkSeq: 8306,
        attackSeq: 8308,
        deathSeq: 8310,
        hp: 3200,
        damage: 0,
        speed: 1.7,
        radius: 0.45,
        xp: 1200,
        sounds: MIMIC_SOUNDS,
        // Five tiles across, as in OSRS
        scale: 1.2,
        knockbackResist: 1,
        spawnMessage: "Trick! The casket was a Mimic!",
        disguise: {
            casket: "master",
            seconds: 25,
            shake: 1.5,
            seq: 8309,
            // Its four sweets
            warning: { spotAnims: [1670, 1671, 1672, 1673], count: 12, ringRadius: 3.6 },
        },
        // Its bonus loot
        caskets: ["master"],
        boss: {
            attackInterval: 2.2,
            keepDistance: 4.5,
            stomp: { radius: 3, interval: 0.6, damage: 1, max: 3, sound: MIMIC_SOUNDS.attack },
            enrage: { message: "The Mimic gnashes its lid!" },
            attacks: [
                {
                    name: "Bite",
                    kind: "swing",
                    damage: 7,
                    windup: 0.8,
                    cooldown: 0,
                    range: 4.7,
                    radius: 4.5,
                    weight: 3,
                    sound: MIMIC_SOUNDS.attack,
                },
                {
                    name: "Candy attack",
                    kind: "blast",
                    damage: 5,
                    windup: 1.6,
                    cooldown: 7,
                    range: 16,
                    radius: 1.5,
                    count: 6,
                    spread: 5,
                    weight: 3,
                    seq: 8309,
                    // Purple do nothing more; red, green and blue bring a minion
                    impactSpotAnims: [1674, 1674, 1674, 1676, 1675, 1677],
                    candies: ["third_age_warrior", "third_age_ranger", "third_age_mage"],
                    sound: MIMIC_SOUNDS.candy,
                    message: "The Mimic flings a handful of candy!",
                },
                {
                    name: "Stomp",
                    kind: "blast",
                    damage: 6,
                    windup: 1.1,
                    cooldown: 6,
                    range: 12,
                    minRange: 5,
                    radius: 3.4,
                    weight: 2,
                    leap: true,
                    seq: 8309,
                    sound: MIMIC_SOUNDS.attack,
                },
                {
                    name: "Candy storm",
                    kind: "blast",
                    damage: 5,
                    windup: 1.3,
                    cooldown: 8,
                    range: 16,
                    radius: 1.5,
                    count: 9,
                    spread: 7,
                    weight: 2,
                    seq: 8309,
                    impactSpotAnims: [1674, 1675, 1676, 1677, 1674, 1675, 1676, 1677, 1674],
                    sound: MIMIC_SOUNDS.candy,
                    enraged: true,
                },
            ],
        },
    },
    // The Mimic's minions, one to a candy
    thirdAgeMinion("third_age_warrior", "Third Age Warrior", 8635),
    thirdAgeMinion("third_age_ranger", "Third Age Ranger", 8636),
    { ...thirdAgeMinion("third_age_mage", "Third Age Mage", 8637), idleSeq: 813, walkSeq: 1205 },

    // The fun maps' players (fun/enemies.ts)
    ...FUN_ENEMIES,

    // The Nightmare and all that comes with her (nightmare.ts)
    ...NIGHTMARE_ENEMIES,
    ...PHOSANI_ENEMIES,
];

// The Theatre of Blood raid's bosses, some of them copies of Morytania's above
ENEMIES.push(
    ...getTheatreBosses((id) => {
        const def = ENEMIES.find((e) => e.id === id);
        if (!def) {
            throw new Error("Unknown enemy " + id);
        }
        return def;
    }),
);

export const ENEMY_BY_ID: Map<string, EnemyDef> = new Map(ENEMIES.map((e) => [e.id, e]));

export function getEnemy(id: string): EnemyDef {
    const enemy = ENEMY_BY_ID.get(id);
    if (!enemy) {
        throw new Error("Unknown enemy " + id);
    }
    return enemy;
}
